import { Fragment, useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { type NavigateFunction } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronDown, ArrowLeft, CheckCircle2, Users, Sparkles, FileText, BarChart3, Instagram, Youtube, Plus, Calendar, DollarSign, Activity, ExternalLink, AlertTriangle, Camera, Search, ArrowUpDown, X, Check, MapPin, Play, MoreVertical, Eye, EyeOff, Image, Hash, Type, Link, Clock, AlertCircle, Loader2, Film, Heart, MessageCircle, MousePointerClick, TrendingUp, Share2, Download, ZoomIn } from 'lucide-react';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import type { OverlayRenderProps } from 'react-photo-view/dist/types';
import 'react-photo-view/dist/react-photo-view.css';
import { motion, AnimatePresence } from 'motion/react';
import type {
  ClientCampaign,
  ApiBeat,
  ClientCuration,
  ClientCampaignCollaboration,
  ClientDraftVersion,
  ClientDraftContentPlatform,
  ClientDraftReviewBody,
} from '@/api/beatlyApi';
import { listClientBeatProductEntries } from '@/api/beatlyApi';
import {
  acceptCuratedInfluencer,
  rejectCuratedInfluencer,
  selectClientTeam,
  postClientDraftApprove,
  postClientDraftReject,
} from '@/api/beatlyApi';
import { getTokenFromStorage, updateAuthInStorage } from '@/utils/auth';
import { buildDiscoveryEmbedSrc } from '@/utils/discoveryEmbed';
import { showError } from '@/utils/alert';
import { useAuth } from '@/contexts/AuthContext';
import { SecondarySidebar, SecondarySidebarHeader, SecondarySidebarNavItem } from './SecondarySidebar';
import { ContentReviewSection, type Collaborator, type OpenPublishedPostContext, type Post } from './ContentReviewSection';
import { ProfileDeliveryAddressRow } from './ProfileDeliveryAddressRow';
import { ImageWithFallback } from './figma/ImageWithFallback';
import brandLogo from 'figma:asset/56e55ee4ebfe4ae4be2bd7abe2c8e33a2d15aa87.png';
import { useCampaigns } from '@/hooks/useCampaigns';
import { useTeamsSearch } from '@/hooks/useTeamsSearch';
import { useCampaignCollaborations } from '@/hooks/useCampaignCollaborations';
import { mapCampaignCollaborationsToCollaborators } from '@/utils/mapCampaignCollaborationsToCollaborators';
import { mergeDraftsIntoCollaboratorsForReview } from '@/utils/mergeDraftsIntoCollaboratorsForReview';
import { sortClientTeamsByName } from '@/utils/sortClientTeamsByName';
import { getVideoPosterUrl, isVideoFileUrl } from '@/utils/mediaUtils';
import { isProbablyNonMediaPageUrl } from '@/utils/mapCampaignCollaborationsToCollaborators';
import {
  embedUrlForSocialPermalink,
  inferPlatformFromContentHints,
  inferPlatformFromSocialUrl,
  looseHttpUrlForView,
  viewUrlForPublishedPost,
  viewUrlFromEmbedPlayerSrc,
} from '@/utils/publishedSocialEmbedUrl';

/** Tab key for Profiles / Content Approval status filters (pending, approved, declined). */
type ContentReviewStatusTab = 'pending' | 'approved' | 'declined';

function filenameForContentApprovalDownload(url: string): string {
  try {
    const u = new URL(url, typeof window !== 'undefined' ? window.location.href : undefined);
    const seg = u.pathname.split('/').filter(Boolean).pop() || 'media';
    return decodeURIComponent(seg.split('?')[0] || 'media');
  } catch {
    return 'media';
  }
}

/**
 * Blob download when CORS allows; otherwise open URL in a new tab.
 * The raw `download` attribute is ignored for cross-origin hrefs, which broke this control for typical CDN URLs.
 */
function triggerContentApprovalMediaDownload(url: string): void {
  void (async () => {
    try {
      const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
      if (!res.ok) throw new Error('fetch failed');
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filenameForContentApprovalDownload(url);
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  })();
}

/** Fullscreen overlay for Content Approval draft media (same pattern as beatly-web-app Carousel + react-photo-view). */
function contentApprovalFullscreenOverlay(overlay: OverlayRenderProps, translate: TFunction) {
  const src = overlay.images[overlay.index]?.src as string | undefined;
  if (!src) return null;
  const showVideo = isVideoFileUrl(src);
  return (
    <>
      {showVideo && (
        <video
          key={`ca-fs-${overlay.index}-${src}`}
          className="pointer-events-auto absolute inset-0 z-[10] h-full w-full bg-black object-contain"
          controls
          controlsList="nofullscreen"
          playsInline
          autoPlay
          muted
          poster={getVideoPosterUrl(src)}
        >
          <source src={src} type="video/mp4" />
        </video>
      )}
      <div className="pointer-events-auto fixed bottom-[4.5rem] right-0 z-[2001] p-2">
        <button
          type="button"
          onClick={() => triggerContentApprovalMediaDownload(src)}
          className="inline-flex items-center gap-2 rounded-lg bg-white/15 px-3 py-2 text-sm font-medium text-white hover:bg-white/25"
        >
          {translate('contentReview.drafts.fullscreenDownload')}
          <Download className="h-4 w-4 shrink-0" aria-hidden />
        </button>
      </div>
    </>
  );
}

/** Profiles + Content Approval: default tab is pending if any pending; else approved if any; else declined if any; else pending (all empty). */
function defaultContentReviewStatusFilter(counts: {
  pending: number;
  approved: number;
  declined: number;
}): ContentReviewStatusTab {
  if (counts.pending > 0) return 'pending';
  if (counts.approved > 0) return 'approved';
  if (counts.declined > 0) return 'declined';
  return 'pending';
}

function usablePublishedMediaUrl(u: string): boolean {
  return u.length > 0 && !u.startsWith('data:image/svg+xml') && !isProbablyNonMediaPageUrl(u);
}

/** Resolves playable/displayable media for the published-post modal from a post + collaborator. */
function resolvePublishedMediaForPlayer(
  post: Post,
  _collaborator: Collaborator,
): {
  contentImage: string;
  mediaType?: 'image' | 'video';
  videoUrl?: string;
  contentKind?: 'video' | 'single_image' | 'carousel' | 'embed' | 'placeholder';
  embedPlayerUrl?: string;
} {
  const thumb = (post.thumbnail ?? '').trim();
  const perm = post.permalink?.trim();
  if (usablePublishedMediaUrl(thumb)) {
    const lowerBase = (thumb.split('?')[0] ?? '').toLowerCase();
    const isVideo =
      post.isVideo === true ||
      /\.(mp4|mov|webm|m4v|m3u8)(\?|#|$)/i.test(lowerBase) ||
      lowerBase.includes('/video/');
    return {
      contentImage: thumb,
      mediaType: isVideo ? 'video' : 'image',
      videoUrl: isVideo ? thumb : undefined,
      contentKind: isVideo ? 'video' : undefined,
    };
  }
  const embedSrc = perm ? embedUrlForSocialPermalink(perm) : null;
  if (embedSrc) {
    return {
      contentImage: usablePublishedMediaUrl(thumb) ? thumb : '',
      contentKind: 'embed',
      embedPlayerUrl: embedSrc,
    };
  }
  return { contentImage: '', contentKind: 'placeholder' };
}

function fmtMetricK(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '0';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return String(Math.round(n));
}

function formatPostModalDate(dateStr: string, placeholder: string): string {
  if (!dateStr || dateStr === '–' || dateStr === '-') return placeholder;
  const iso = /^\d{4}-\d{2}-\d{2}/.exec(dateStr);
  if (iso) return iso[0];
  const d = new Date(dateStr);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  return dateStr;
}
import { useCampaign } from '@/hooks/useCampaign';
import { useCampaignBeat } from '@/hooks/useCampaignBeat';
import { useCuration } from '@/hooks/useCuration';
import { useDrafts } from '@/hooks/useDrafts';
import { getPathForPage, type CampaignSubTab } from '@/routes';
import { streamCreatorsContent, getImageProxyUrl, type V1CreatorContentPage } from '@/api/creatorApi';
import type { CreatorCardContent } from '@/utils/creatorContentTransform';
import { transformContentArrayToCardFormat } from '@/utils/creatorContentTransform';
import { linkifyPlainTextParts } from '@/utils/linkifyPlainText';
import { planRichNoteParts } from '@/utils/richTextNote';
import { VideoPlayer } from './ui/video-player';
import publishedPostModalLayout from './publishedPostModal.module.css';
import pubCard from './publicationPostCard.module.css';

/** Centered play control until the user starts playback (published-post modal). */
function PublishedPostModalVideoWithOverlay({
  videoUrl,
  poster,
  playAriaLabel,
}: {
  videoUrl: string;
  poster?: string;
  playAriaLabel: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showPlayOverlay, setShowPlayOverlay] = useState(true);

  return (
    <div className="flex h-full min-h-0 w-full">
      <div className="relative h-full w-full min-h-0 overflow-hidden">
        <VideoPlayer
          ref={videoRef}
          src={videoUrl}
          controls={!showPlayOverlay}
          poster={poster && poster !== videoUrl ? poster : undefined}
          preload="metadata"
          className="block h-full w-full object-cover object-center"
          disableFullscreen={false}
          onPlay={() => setShowPlayOverlay(false)}
        />
        {showPlayOverlay ? (
          <button
            type="button"
            className={publishedPostModalLayout.playOverlay}
            onClick={() => {
              void videoRef.current?.play();
            }}
            aria-label={playAriaLabel}
          >
            <span className="content-media-control content-media-control--play pointer-events-none" aria-hidden>
              <Play className="ml-1 h-6 w-6 text-white" fill="white" strokeWidth={0} aria-hidden />
            </span>
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** True if URL looks like a video file (draft list must not use as <img src>). */
function isDraftListVideoUrl(url: string): boolean {
  if (!url) return false;
  const [base] = url.split('?');
  return /\.(mp4|mov|avi|webm|ogg|m4v)$/i.test(base);
}

type DraftListMedia = { url: string; resource_type?: 'image' | 'video'; is_raw?: boolean; media_id?: string };

type DraftListPreview = { mode: 'image'; url: string } | { mode: 'video'; url: string } | { mode: 'none' };

/** Prefer first final image for list <img>; else first final video for inline <video> preview. */
function pickDraftListPreview(medias: DraftListMedia[]): DraftListPreview {
  const finalMedias = medias.filter(m => !m.is_raw);
  const pool = finalMedias.length > 0 ? finalMedias : medias;
  if (pool.length === 0) return { mode: 'none' };
  const firstImage = pool.find(m => m.resource_type === 'image');
  if (firstImage?.url) return { mode: 'image', url: firstImage.url };
  const firstVideo = pool.find(m => m.resource_type === 'video');
  if (firstVideo?.url) return { mode: 'video', url: firstVideo.url };
  const first = pool[0];
  if (!first?.url) return { mode: 'none' };
  if (first.resource_type === 'video' || isDraftListVideoUrl(first.url)) return { mode: 'video', url: first.url };
  return { mode: 'image', url: first.url };
}

/** Same pool as pickDraftListPreview: prefer final (non-raw) medias for carousel. */
function draftFinalMediaPool(medias: DraftListMedia[]): DraftListMedia[] {
  const finalMedias = medias.filter(m => !m.is_raw);
  return finalMedias.length > 0 ? finalMedias : medias;
}

/** Preview for one media item (carousel slide). */
function previewForDraftMedia(m: DraftListMedia | undefined): DraftListPreview {
  if (!m?.url) return { mode: 'none' };
  if (m.resource_type === 'image') return { mode: 'image', url: m.url };
  if (m.resource_type === 'video') return { mode: 'video', url: m.url };
  if (isDraftListVideoUrl(m.url)) return { mode: 'video', url: m.url };
  return { mode: 'image', url: m.url };
}

function pickRawListPreview(url: string, resourceType?: 'image' | 'video'): { mode: 'image' | 'video'; url: string } {
  if (resourceType === 'video' || isDraftListVideoUrl(url)) return { mode: 'video', url };
  return { mode: 'image', url };
}

interface Campaign {
  id: string;
  brand: string;
  brandLogo: string;
  name: string;
  platforms: ('instagram' | 'tiktok' | 'youtube')[];
  budget: string;
  dateStart: string;
  dateEnd: string;
  status: 'LAUNCHED' | 'DONE';
}

/**
 * API `status` is CoreCampaign.get_status() (e.g. LAUNCHED = live client campaign).
 * UI pill: LAUNCHED → "Active", DONE → "Done".
 * Anything not explicitly finished/cancelled is shown as Active so the column matches client expectations.
 */
const CLIENT_LIST_DONE_STATUSES = new Set(
  ['DONE', 'CANCELED', 'COMPLETED', 'PAID'].map((s) => s.toUpperCase())
);

function mapApiCampaignToCampaign(
  c: ClientCampaign,
  listFilter: 'all' | 'LAUNCHED' | 'DONE' = 'all'
): Campaign {
  let status: 'LAUNCHED' | 'DONE';
  if (listFilter === 'LAUNCHED') {
    status = 'LAUNCHED';
  } else if (listFilter === 'DONE') {
    status = 'DONE';
  } else {
    const upper = (c.status ?? '').toString().toUpperCase();
    if (c.is_canceled) {
      status = 'DONE';
    } else {
      status = CLIENT_LIST_DONE_STATUSES.has(upper) ? 'DONE' : 'LAUNCHED';
    }
  }
  const amount = c.budget?.amount ?? 0;
  const currency = c.budget?.currency ?? '';
  const budgetStr = `${Number(amount).toLocaleString('en-US', { maximumFractionDigits: 0 })} ${currency}`;
  const platforms = (c.platforms ?? []).map((p) => p.toLowerCase() as 'instagram' | 'tiktok' | 'youtube');
  return {
    id: c.campaign_id,
    brand: c.brand_name ?? '',
    brandLogo: c.brand_logo_url ?? '',
    name: c.name ?? '',
    platforms,
    budget: budgetStr,
    dateStart: c.date_start ?? '',
    dateEnd: c.date_end ?? '',
    status,
  };
}

/** Labels for decline checklist → merged into API `feedback` (POST draft reject). */
const DECLINE_REVIEW_KEY_LABELS: Record<string, string> = {
  image_video: 'Image / Video',
  caption: 'Caption',
  hashtags: 'Hashtags / Mentions',
  brand_link: 'Brand Link / CTA',
  timing: 'Timing / Schedule',
  compliance: 'Compliance / Disclosure',
  other: 'Other',
};

function beatClientStatusPill(statusRaw: string | undefined): { label: string; dot: string; pillBg: string; text: string } {
  const s = (statusRaw ?? '').toUpperCase();
  if (s === 'DONE')
    return { label: statusRaw || 'Done', dot: 'bg-emerald-500', pillBg: 'bg-emerald-50', text: 'text-emerald-700' };
  if (s === 'ONGOING')
    return { label: statusRaw || 'Ongoing', dot: 'bg-amber-500', pillBg: 'bg-amber-50', text: 'text-amber-800' };
  if (s === 'PLANNED')
    return { label: statusRaw || 'Planned', dot: 'bg-slate-400', pillBg: 'bg-slate-100', text: 'text-slate-600' };
  if (s === 'DRAFT')
    return { label: statusRaw || 'Draft', dot: 'bg-slate-400', pillBg: 'bg-slate-100', text: 'text-slate-600' };
  if (s === 'CANCELED')
    return { label: statusRaw || 'Canceled', dot: 'bg-red-400', pillBg: 'bg-red-50', text: 'text-red-700' };
  return {
    label: statusRaw && statusRaw !== '–' ? statusRaw : 'Status',
    dot: 'bg-slate-400',
    pillBg: 'bg-slate-100',
    text: 'text-slate-600',
  };
}

const ALL_BRANDS_VALUE = 'All Brands';

/** `<select>` value for merged campaigns across every team (not a real team_id). */
const ALL_TEAMS_CAMPAIGNS_VALUE = '__all_teams__';

/** Visual for one beat row (product-type glyphs or social marks). */
type BeatRowIcon = 'content' | 'whitelisting' | 'instagram' | 'tiktok' | 'youtube' | 'spark-ads';

/** One row in the Beats list (from API). */
interface BeatRow {
  id: string;
  name: string;
  dates: string;
  status: string;
  icon: BeatRowIcon;
  platform?: string;
}

function beatBriefProductLabel(t: (key: string) => string, productKey: string): string {
  const i18nKey = `campaigns.beatBrief.productTypes.${productKey}`;
  const label = t(i18nKey);
  return label === i18nKey ? productKey.replace(/_/g, ' ') : label;
}

/** Calendar day YYYY-MM-DD for comparison (ISO date prefix or local date from timestamp). */
function beatCalendarDayKey(iso: string | undefined): string {
  if (!iso || typeof iso !== 'string') return '';
  const trimmed = iso.trim();
  const m = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
}

type BeatDateLabelCategory = 'publication' | 'whitelist' | 'paid' | 'generic';

function beatDateLabelCategory(beatType: string | undefined, platform: string | undefined): BeatDateLabelCategory {
  const u = (beatType ?? platform ?? '').toUpperCase();
  if (u.includes('WHITELIST')) return 'whitelist';
  if (['BOOST', 'PAIDAD', 'AMPLI'].some((x) => u.includes(x))) return 'paid';
  if (!u.trim()) return 'generic';
  return 'publication';
}

function formatBeatRowDates(b: ApiBeat, formatDate: (iso: string) => string, t: TFunction): string {
  const start = b.date_start;
  const end = b.date_end;
  const d0 = beatCalendarDayKey(start);
  const d1 = beatCalendarDayKey(end);
  const cat = beatDateLabelCategory(b.beat_type, b.platform);

  if (!d0 && !d1) return '–';

  const keySingle = `campaigns.beats.dateSingle.${cat}`;
  const keyRange = `campaigns.beats.dateRange.${cat}`;

  if (d0 && d1) {
    if (d0 === d1) {
      return t(keySingle, { date: formatDate(start!) });
    }
    return t(keyRange, { start: formatDate(start!), end: formatDate(end!) });
  }
  const onlyIso = d0 ? start! : end!;
  return t(keySingle, { date: formatDate(onlyIso) });
}

function normalizeBeatPlatformField(p: string | undefined): 'instagram' | 'tiktok' | 'youtube' | undefined {
  if (!p || typeof p !== 'string') return undefined;
  const lower = p.trim().toLowerCase();
  if (lower === 'instagram' || lower === 'ig') return 'instagram';
  if (lower === 'tiktok') return 'tiktok';
  if (lower === 'youtube' || lower === 'yt') return 'youtube';
  return undefined;
}

/** Infer Instagram / TikTok / YouTube from `platform` and/or beat name / type copy. */
function inferSocialIconFromApiBeat(b: ApiBeat): 'instagram' | 'tiktok' | 'youtube' | undefined {
  const fromField = normalizeBeatPlatformField(b.platform);
  if (fromField) return fromField;
  const combined = `${b.beat_type ?? ''} ${b.beat_name ?? ''} ${b.platform ?? ''}`.toUpperCase();
  if (combined.includes('TIKTOK')) return 'tiktok';
  if (combined.includes('YOUTUBE')) return 'youtube';
  if (combined.includes('INSTAGRAM')) return 'instagram';
  return undefined;
}

function mapApiBeatToRowIcon(b: ApiBeat): BeatRowIcon {
  const beatTypeUpper = (b.beat_type ?? b.platform ?? '').toUpperCase();
  if (beatTypeUpper.includes('IGC') || beatTypeUpper === 'CONTENT') return 'content';
  if (beatTypeUpper.includes('WHITELIST')) return 'whitelisting';
  const social = inferSocialIconFromApiBeat(b);
  if (social) return social;
  return 'spark-ads';
}

function mapApiBeatsToRows(beats: ApiBeat[], formatDate: (iso: string) => string, t: TFunction): BeatRow[] {
  return beats.map((b) => {
    const dates = formatBeatRowDates(b, formatDate, t);
    const icon = mapApiBeatToRowIcon(b);
    return {
      id: b.beat_id,
      name: b.beat_name ?? '–',
      dates,
      status: b.status ?? '–',
      icon,
      platform: b.platform,
    };
  });
}

/** One row in the Suggestions (Profiles) list. */
interface SuggestionRow {
  /** Collaboration curation id (`…-col`) — for accept/reject APIs. */
  id: string;
  /** Influencer id for `/creators/:id` → Discovery profile (not the same as `id`). */
  influencerId: string;
  name: string;
  handle: string;
  avatar: string;
  beat: string;
  followers: string;
  engRate: string;
  status: 'pending' | 'approved' | 'declined';
  location: string;
  category: string;
  platform: string;
  /** First curated beat id — opens Beat Brief overlay when the pill is activated. */
  beatId: string;
  /** From API `influencer.notes` (plain or HTML per backend). */
  notes?: string;
}

function formatFollowers(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function mapCurationToSuggestionRow(c: ClientCuration): SuggestionRow {
  const statusMap: Record<string, 'pending' | 'approved' | 'declined'> = {
    CURATION: 'pending',
    ACCEPTED: 'approved',
    REJECTED: 'declined',
  };
  const inf = c.influencer ?? {};
  const firstBeat = c.beats?.[0];
  let beatName = '–';
  let beatId = '';
  if (firstBeat && typeof firstBeat === 'object' && firstBeat !== null) {
    const fb = firstBeat as { beat_name?: string; beat_id?: string };
    if (typeof fb.beat_name === 'string' && fb.beat_name.trim()) beatName = fb.beat_name.trim();
    if (typeof fb.beat_id === 'string' && fb.beat_id.trim()) beatId = fb.beat_id.trim();
  }
  if (!beatId) {
    const rawIds = (c as { beat_ids?: string[] }).beat_ids;
    if (Array.isArray(rawIds) && rawIds[0] && typeof rawIds[0] === 'string') {
      beatId = rawIds[0].trim();
    }
  }
  // API sends engagement_rate as 0–1 (e.g. 0.05 = 5%); display as percentage
  const engagementRate = Number(inf.engagement_rate);
  const engRatePct = Number.isFinite(engagementRate)
    ? (engagementRate <= 1 ? engagementRate * 100 : engagementRate)
    : 0;
  const notesRaw = typeof inf.notes === 'string' ? inf.notes.trim() : '';
  const influencerId =
    typeof inf.influencer_id === 'string' && inf.influencer_id.trim() ? inf.influencer_id.trim() : '';
  return {
    id: c.curation_id,
    influencerId,
    name: inf.name ?? inf.handle ?? '–',
    handle: inf.handle ?? '–',
    avatar: inf.mugshot_url ?? '',
    beat: beatName,
    beatId,
    followers: formatFollowers(Number(inf.followers_count) || 0),
    engRate: `${engRatePct.toFixed(1)}%`,
    status: statusMap[c.status] ?? 'pending',
    location: '–',
    category: '–',
    platform: inf.platform ?? c.platform ?? '–',
    ...(notesRaw ? { notes: notesRaw } : {}),
  };
}

/** Draft row for Content Approval tab (mapped from GET /drafts). */
interface DraftVersionUI {
  version: number;
  versionId?: string;
  thumbnail: string;
  rawThumbnail?: string;
  listFinalPreview: { mode: 'image'; url: string } | { mode: 'video'; url: string } | { mode: 'none' };
  listRawPreview: { mode: 'image'; url: string } | { mode: 'video'; url: string } | null;
  draftDate: string;
  caption: string;
  feedback?: string;
  reason?: string;
  status: 'pending' | 'approved' | 'declined';
  medias?: { url: string; resource_type?: 'image' | 'video'; is_raw?: boolean; media_id?: string }[];
  permalink?: string;
}

interface DraftItemUI {
  id: number;
  /** Discovery profile id when drafts API sends `influencer_id`. */
  influencerId?: string;
  creator: string;
  handle: string;
  avatar: string;
  contentType: string;
  contentPlatform: ClientDraftContentPlatform;
  publicationDate?: string;
  isVideo: boolean;
  versions: DraftVersionUI[];
  apiDraftVersionId?: string;
}

/** One row in the Review Content modal (final assets + raw); `key` is stable for form state. */
interface DraftReviewFileRow {
  key: string;
  id: number;
  mediaId?: string;
  type: 'image' | 'video';
  url: string;
  name: string;
  isRaw: boolean;
}

/** Media rows for the Review Content modal (Figma parity). */
function buildDraftReviewFileRows(version: DraftVersionUI, t: TFunction): DraftReviewFileRow[] {
  const pool = draftFinalMediaPool(version.medias ?? []);
  const out: DraftReviewFileRow[] = [];
  let n = 0;
  for (const m of pool) {
    const pv = previewForDraftMedia(m);
    if (pv.mode === 'none') continue;
    n += 1;
    const mediaId =
      typeof m.media_id === 'string' && m.media_id.trim() ? m.media_id.trim() : undefined;
    const key = mediaId ?? `__idx_${n}`;
    out.push({
      key,
      id: n,
      mediaId,
      type: pv.mode === 'video' ? 'video' : 'image',
      url: pv.url,
      name:
        pv.mode === 'video'
          ? t('contentReview.draftReviewModal.fileVideo', { n })
          : t('contentReview.draftReviewModal.fileImage', { n }),
      isRaw: false,
    });
  }
  if (version.rawThumbnail) {
    out.push({
      key: '__raw__',
      id: n + 1,
      mediaId: undefined,
      type: 'video',
      url: version.rawThumbnail,
      name: t('contentReview.draftReviewModal.rawFile'),
      isRaw: true,
    });
  }
  return out;
}

/** Caption in review modal as plain text (API may return HTML; avoids broken nested layouts). */
function captionPlainTextForReview(raw: string): string {
  let s = raw.trim();
  if (!s) return '';
  if (/<[a-z][\s\S]*>/i.test(s)) {
    s = s.replace(/<[^>]+>/g, ' ');
  }
  if (typeof document !== 'undefined') {
    const ta = document.createElement('textarea');
    ta.innerHTML = s;
    s = ta.value;
  } else {
    s = s
      .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
      .replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCharCode(parseInt(h, 16)))
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
  }
  s = s
    .replace(/\s+/g, ' ')
    .trim();
  return s;
}

const DRAFT_REVIEW_TOPIC_KEYS = ['audio', 'visuals', 'hashtags', 'trackingLinks', 'toneOfVoice'] as const;
type DraftReviewTopicKey = (typeof DRAFT_REVIEW_TOPIC_KEYS)[number];

function buildClientDraftReviewBody(
  t: TFunction,
  files: DraftReviewFileRow[],
  draftReviewFeedback: { caption: string; files: Record<string, string> },
  draftReviewToggles: { caption: boolean; files: Record<string, boolean> },
  draftReviewGeneralTopics: DraftReviewTopicKey[],
  draftReviewGeneralText: string,
  mode: 'decline' | 'approve',
): ClientDraftReviewBody {
  const captionFb = draftReviewFeedback.caption.trim();
  const captions: NonNullable<ClientDraftReviewBody['captions']> = [
    {
      feedback: captionFb || undefined,
      action: draftReviewToggles.caption ? 'APPROVE' : 'REJECT',
    },
  ];

  const medias: NonNullable<ClientDraftReviewBody['medias']> = [];
  const orphanParts: string[] = [];

  for (const f of files) {
    const fb = (draftReviewFeedback.files[f.key] ?? '').trim();
    const ok = draftReviewToggles.files[f.key] !== false;
    const action: 'APPROVE' | 'REJECT' = ok ? 'APPROVE' : 'REJECT';
    if (f.mediaId) {
      medias.push({
        media_id: f.mediaId,
        feedback: fb || undefined,
        action,
      });
    } else {
      if (fb) {
        orphanParts.push(t('contentReview.draftReviewModal.compose.fileFeedback', { name: f.name, text: fb }));
      }
      if (!ok) {
        orphanParts.push(t('contentReview.draftReviewModal.compose.fileNotOk', { name: f.name }));
      }
    }
  }

  const generalParts: string[] = [];
  if (draftReviewGeneralTopics.length > 0) {
    const list = draftReviewGeneralTopics
      .map((topicKey) => t(`contentReview.draftReviewModal.topics.${topicKey}`))
      .join(', ');
    generalParts.push(t('contentReview.draftReviewModal.compose.topics', { list }));
  }
  if (draftReviewGeneralText.trim()) {
    generalParts.push(draftReviewGeneralText.trim());
  }
  if (orphanParts.length > 0) {
    generalParts.push(orphanParts.join('\n\n'));
  }
  if (mode === 'decline') {
    if (!draftReviewToggles.caption) {
      generalParts.push(t('contentReview.draftReviewModal.compose.captionNotOk'));
    }
    for (const f of files) {
      if (f.mediaId && draftReviewToggles.files[f.key] === false) {
        generalParts.push(t('contentReview.draftReviewModal.compose.fileNotOk', { name: f.name }));
      }
    }
  }

  let feedback = generalParts.filter(Boolean).join('\n\n');
  if (mode === 'decline' && !feedback.trim()) {
    feedback = t('contentReview.draftReviewModal.compose.defaultDecline');
  }

  return {
    feedback,
    captions,
    medias: medias.length > 0 ? medias : undefined,
  };
}

interface SeeCampaignsPageProps {
  campaignId?: string;
  subTab?: CampaignSubTab;
  beatId?: string;
  onNavigate?: (page: string) => void;
  navigate?: NavigateFunction;
}

/** Map a creator-api content card to legacy right-sheet hero fields. */
function resolveLegacyHeroFromCreatorContent(item: CreatorCardContent): {
  contentImage: string;
  videoUrl?: string;
  embedPlayerUrl?: string;
  contentKind?: 'video' | 'single_image' | 'carousel' | 'embed' | 'placeholder';
  mediaType?: 'image' | 'video';
  permalink?: string;
} {
  const permalink = item.permalink?.trim() ?? undefined;
  const embedCandidate = item.embedUrl?.trim();
  const embedPlayerUrl =
    embedCandidate && /^https?:\/\//i.test(embedCandidate)
      ? embedCandidate
      : embedUrlForSocialPermalink(permalink ?? embedCandidate ?? null) ?? undefined;
  const image = (item.image ?? '').trim();

  if (embedPlayerUrl && !isVideoFileUrl(image)) {
    return {
      contentImage: image,
      embedPlayerUrl,
      contentKind: 'embed',
      permalink,
    };
  }
  if (image && isVideoFileUrl(image)) {
    return {
      contentImage: image,
      videoUrl: image,
      mediaType: 'video',
      contentKind: 'video',
      permalink,
    };
  }
  if (image) {
    return {
      contentImage: image,
      mediaType: 'image',
      contentKind: 'single_image',
      permalink,
    };
  }
  return {
    contentImage: '',
    contentKind: 'placeholder',
    permalink,
  };
}

/** One tile in the campaign suggestions content strip (creator-api preview). */
function SuggestionContentStripThumb({
  item,
  token,
  onOpen,
}: {
  item: CreatorCardContent;
  token: string | undefined;
  onOpen: () => void;
}) {
  const [src, setSrc] = useState(item.image);
  const [triedProxy, setTriedProxy] = useState(false);

  useEffect(() => {
    setSrc(item.image);
    setTriedProxy(false);
  }, [item.image]);

  const proxy = item.imageProxyUrl ?? getImageProxyUrl(item.image, token ?? null) ?? undefined;
  const isVideo = isVideoFileUrl(item.image);

  return (
    <button
      type="button"
      className="relative h-[208px] w-[120px] shrink-0 overflow-hidden rounded-xl border border-gray-100 bg-gray-50 text-left transition-colors hover:border-[#be43ff]/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#be43ff]/40"
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      aria-label={item.title || 'Content'}
    >
      {isVideo ? (
        <video
          src={item.image}
          muted
          playsInline
          preload="metadata"
          className="block h-full w-full object-cover"
        />
      ) : (
        <img
          src={src}
          alt=""
          className="block h-full w-full object-cover"
          onError={() => {
            if (!triedProxy && proxy && src !== proxy) {
              setTriedProxy(true);
              setSrc(proxy);
            }
          }}
        />
      )}
      <div
        className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-opacity hover:bg-black/25 hover:opacity-100"
        aria-hidden
      >
        <Play className="ml-1 h-6 w-6 text-white drop-shadow-md" fill="white" />
      </div>
    </button>
  );
}

export function SeeCampaignsPage({
  campaignId: campaignIdFromUrl,
  subTab: subTabFromUrl,
  beatId: beatIdFromUrl,
  onNavigate,
  navigate: navigateToPath,
}: SeeCampaignsPageProps = {}) {
  const { t } = useTranslation();
  const { user, setUser } = useAuth();
  const [teamSwitchLoading, setTeamSwitchLoading] = useState(false);
  const { data: teamsSearchData, isLoading: teamsSearchLoading } = useTeamsSearch();
  const clientTeams = teamsSearchData?.teams ?? [];
  const clientTeamsSortedByName = useMemo(() => sortClientTeamsByName(clientTeams), [clientTeams]);
  const allTeamIds = useMemo(() => clientTeamsSortedByName.map((x) => x.team_id), [clientTeamsSortedByName]);

  const [currentPage, setCurrentPage] = useState(1);
  /** List filter: 'all' merges all teams via campaigns/search?team_id per team; else a single team_id (+ server current team on select). */
  const [campaignsTeamScope, setCampaignsTeamScope] = useState<'all' | string>('all');

  const handleCampaignsTeamSelect = useCallback(
    async (value: string) => {
      if (value === ALL_TEAMS_CAMPAIGNS_VALUE) {
        setCampaignsTeamScope('all');
        setCurrentPage(1);
        return;
      }
      if (value === campaignsTeamScope && campaignsTeamScope !== 'all') {
        return;
      }
      const token = getTokenFromStorage();
      if (!token || !setUser) return;
      setTeamSwitchLoading(true);
      try {
        const updated = await selectClientTeam(value, token);
        updateAuthInStorage({ token, user: updated });
        setUser(updated);
        setCampaignsTeamScope(value);
        setCurrentPage(1);
      } catch {
        showError(t('campaigns.teamSwitchError'));
      } finally {
        setTeamSwitchLoading(false);
      }
    },
    [campaignsTeamScope, setUser, t]
  );
  const [selectedBrand, setSelectedBrand] = useState(ALL_BRANDS_VALUE);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [currentSubPage, setCurrentSubPage] = useState<CampaignSubTab>('overview');
  const [selectedApiBeat, setSelectedApiBeat] = useState<BeatRow | null>(null);
  const [suggestionsFilter, setSuggestionsFilter] = useState<ContentReviewStatusTab>('pending');
  const [draftsFilter, setDraftsFilter] = useState<ContentReviewStatusTab>('pending');
  const suggestionsDefaultAppliedRef = useRef(false);
  const draftsDefaultAppliedRef = useRef(false);
  const [searchQuery, setSearchQuery] = useState('');
  /** Debounced value for API only — keeps the search input mounted and avoids refetch on every keystroke. */
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'LAUNCHED' | 'DONE'>('all');
  const [channelFilter, setChannelFilter] = useState<'all' | 'instagram' | 'tiktok' | 'youtube'>('all');
  const [budgetFilter, setBudgetFilter] = useState<'all' | 'under150k' | '150k-250k' | 'over250k'>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'budget-high' | 'budget-low' | 'name-az' | 'name-za'>('newest');
  type ContentPlayerPlatform = 'instagram' | 'tiktok' | 'youtube';
  /** Collaborations / published posts only — isolated from drafts & curation player. */
  type PublishedPostPlayerState = {
    reviewCollaborator: Collaborator;
    reviewPosts: Post[];
    reviewIndex: number;
    contentImage: string;
    contentTitle: string;
    mediaType?: 'image' | 'video';
    videoUrl?: string;
    contentKind?: 'video' | 'single_image' | 'carousel' | 'embed' | 'placeholder';
    embedPlayerUrl?: string;
    permalink?: string;
    platform?: ContentPlayerPlatform;
  };
  /** Content approval (drafts) + curation thumbnails — right sheet only. */
  type LegacyContentPlayerState = {
    creatorName: string;
    creatorAvatar: string;
    contentImage: string;
    contentTitle: string;
    /** Drafts / URL-only carousel (mutually exclusive with `structuredItems`). */
    allContent: string[];
    /** Suggestions: items from creator-api SSE (carousel uses these). */
    structuredItems?: CreatorCardContent[];
    currentIndex: number;
    mediaType?: 'image' | 'video';
    videoUrl?: string;
    contentKind?: 'video' | 'single_image' | 'carousel' | 'embed' | 'placeholder';
    embedPlayerUrl?: string;
    permalink?: string;
    creatorHandle?: string;
    platform?: ContentPlayerPlatform;
  };
  const [publishedPostPlayer, setPublishedPostPlayer] = useState<PublishedPostPlayerState | null>(null);
  const [legacyContentPlayer, setLegacyContentPlayer] = useState<LegacyContentPlayerState | null>(null);

  /** Creator id → recent content tiles (campaign suggestions; creator-api SSE). */
  const [suggestionCreatorContent, setSuggestionCreatorContent] = useState<Map<string, CreatorCardContent[]>>(
    () => new Map()
  );
  const suggestionCreatorContentRef = useRef(suggestionCreatorContent);
  useEffect(() => {
    suggestionCreatorContentRef.current = suggestionCreatorContent;
  }, [suggestionCreatorContent]);

  const [suggestionContentLoadingIds, setSuggestionContentLoadingIds] = useState<Set<string>>(() => new Set());
  const lastSuggestionContentStreamKeyRef = useRef<string | null>(null);
  const suggestionContentStreamAbortRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearchQuery(searchQuery), 350);
    return () => window.clearTimeout(id);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery]);

  const normalizePlatform = (p: string | undefined): ContentPlayerPlatform | undefined => {
    if (!p || typeof p !== 'string') return undefined;
    const lower = p.toLowerCase();
    if (lower === 'instagram') return 'instagram';
    if (lower === 'tiktok') return 'tiktok';
    if (lower === 'youtube') return 'youtube';
    return undefined;
  };
  const PUBLISHED_PRODUCT_STATUSES = new Set(['PUBLISHED', 'MEASURED']);
  const DRAFT_PRODUCT_STATUS = 'NO_DRAFT';
  const CANCELED_PRODUCT_STATUS = 'CANCELED';
  const PENDING_PRODUCT_STATUSES = new Set([
    'CM_REVIEW',
    'CM_REVIEW_FEEDBACK',
    'CLIENT_REVIEW',
    'DRAFT_APPROVED',
    'DRAFT_REJECTED',
  ]);
  type ModalPostStatus = 'published' | 'draft' | 'pending' | 'canceled';
  const resolveModalPostStatus = (post: Post, collab: Collaborator): ModalPostStatus => {
    const statusUpper = (post.product_status ?? '').trim().toUpperCase();
    if (PUBLISHED_PRODUCT_STATUSES.has(statusUpper)) return 'published';
    if (statusUpper === DRAFT_PRODUCT_STATUS) return 'draft';
    if (statusUpper === CANCELED_PRODUCT_STATUS) return 'canceled';
    if (PENDING_PRODUCT_STATUSES.has(statusUpper)) return 'pending';
    if (statusUpper) return 'pending';
    if (collab.status === 'Published') return 'published';
    if (collab.status === 'Approved') return 'pending';
    return 'pending';
  };
  const resolveContentPlatform = (
    post: Post,
    collab: Collaborator,
    urls: Array<string | undefined | null>
  ): ContentPlayerPlatform => {
    for (const candidate of urls) {
      const inferred = inferPlatformFromSocialUrl(candidate ?? undefined);
      if (inferred) return inferred;
    }
    const fromCopy = inferPlatformFromContentHints([post.caption, post.type]);
    if (fromCopy) return fromCopy;
    return normalizePlatform(post.platform ?? collab.platform) ?? 'instagram';
  };
  const resolvePlayerPlatform = (post: Post, collab: Collaborator): ContentPlayerPlatform =>
    normalizePlatform(post.platform ?? collab.platform) ?? 'instagram';
  const platformNameI18nKey: Record<ContentPlayerPlatform, string> = {
    instagram: 'contentReview.platformNames.instagram',
    tiktok: 'contentReview.platformNames.tiktok',
    youtube: 'contentReview.platformNames.youtube',
  };

  const openPublishedPostInPlayer = (ctx: OpenPublishedPostContext) => {
    const { collaborator, post, postIndex, allPosts } = ctx;
    const p = allPosts[postIndex] ?? post;
    const media = resolvePublishedMediaForPlayer(p, collaborator);
    setLegacyContentPlayer(null);
    setPublishedPostPlayer({
      reviewCollaborator: collaborator,
      reviewPosts: [p],
      reviewIndex: 0,
      contentTitle: `${p.type} by @${collaborator.handle}`,
      permalink: p.permalink?.trim(),
      platform: resolvePlayerPlatform(p, collaborator),
      ...media,
    });
  };

  useEffect(() => {
    if (!publishedPostPlayer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPublishedPostPlayer(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [publishedPostPlayer]);
  const [profileSidebarOpen, setProfileSidebarOpen] = useState(false);
  /** Discovery influencer id when opening the campaign profile sidebar (same embed as `/creators/:id`). */
  const [profileSidebarInfluencerId, setProfileSidebarInfluencerId] = useState<string | null>(null);

  const discoveryProfileEmbedSrc = useMemo(() => {
    if (!profileSidebarInfluencerId) return '';
    const token = getTokenFromStorage();
    if (!token) return '';
    try {
      return buildDiscoveryEmbedSrc(
        `/creators/${encodeURIComponent(profileSidebarInfluencerId)}`,
        token
      );
    } catch {
      return '';
    }
  }, [profileSidebarInfluencerId]);

  useEffect(() => {
    if (!profileSidebarOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setProfileSidebarOpen(false);
        setProfileSidebarInfluencerId(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [profileSidebarOpen]);

  const [draftSelectedVersions, setDraftSelectedVersions] = useState<Record<number, number>>({});
  const [draftVersionDropdownOpen, setDraftVersionDropdownOpen] = useState<number | null>(null);
  const [draftShowRaw, setDraftShowRaw] = useState<Record<number, boolean>>({});
  /** Slide index per draft+version for Content Approval final-media carousel (`${draftId}-v${version}`). */
  const [draftFinalMediaSlide, setDraftFinalMediaSlide] = useState<Record<string, number>>({});
  const [draftDeclineModal, setDraftDeclineModal] = useState<{
    creator: string;
    contentType: string;
    versionId: string;
  } | null>(null);
  const [draftDeclineChecked, setDraftDeclineChecked] = useState<string[]>([]);
  const [draftDeclineFeedback, setDraftDeclineFeedback] = useState('');
  const [draftAcceptModal, setDraftAcceptModal] = useState<{
    creator: string;
    contentType: string;
    versionId: string;
  } | null>(null);
  /** Figma-style full-screen review (single entry point from Content Approval cards). */
  const [draftReviewModal, setDraftReviewModal] = useState<{
    draftId: number;
    allDrafts: DraftItemUI[];
    selectedVersionIndex?: number;
    readOnly?: boolean;
  } | null>(null);
  const [draftReviewFeedback, setDraftReviewFeedback] = useState<{ caption: string; files: Record<string, string> }>({
    caption: '',
    files: {},
  });
  const [draftReviewToggles, setDraftReviewToggles] = useState<{ caption: boolean; files: Record<string, boolean> }>({
    caption: true,
    files: {},
  });
  const [draftReviewGeneralTopics, setDraftReviewGeneralTopics] = useState<DraftReviewTopicKey[]>([]);
  const [draftReviewGeneralText, setDraftReviewGeneralText] = useState('');
  const [draftMutationLoading, setDraftMutationLoading] = useState<'accept' | 'decline' | null>(null);
  const [declineProfileModal, setDeclineProfileModal] = useState<{ id: string; name: string; handle: string } | null>(null);
  const [confirmApproveDeclinedModal, setConfirmApproveDeclinedModal] = useState<{
    id: string;
    name: string;
    handle: string;
  } | null>(null);
  const [declineProfileReason, setDeclineProfileReason] = useState('');
  const [pendingCurationId, setPendingCurationId] = useState<string | null>(null);
  const [usernameCopyTooltipOpen, setUsernameCopyTooltipOpen] = useState(false);

  useEffect(() => {
    if (!draftReviewModal) return;
    document.documentElement.classList.add('draft-review-modal-lock');
    document.body.classList.add('draft-review-modal-lock');
    return () => {
      document.documentElement.classList.remove('draft-review-modal-lock');
      document.body.classList.remove('draft-review-modal-lock');
    };
  }, [draftReviewModal]);

  /** Sync browser back with closing the Content Approval review modal (same as X / backdrop). */
  const draftReviewHistoryPushedRef = useRef(false);
  const skipDraftReviewPopStateRef = useRef(false);

  /** Fullscreen media viewer (react-photo-view) — separate history entry so Back closes the overlay only. */
  const contentApprovalPhotoHistoryPushedRef = useRef(false);
  const skipContentApprovalPhotoPopStateRef = useRef(false);
  const closeContentApprovalPhotoViaPopStateRef = useRef(false);
  const [contentApprovalPhotoProviderKey, setContentApprovalPhotoProviderKey] = useState(0);

  const closeDraftReviewModal = useCallback(() => {
    setDraftReviewModal(null);
    if (draftReviewHistoryPushedRef.current) {
      draftReviewHistoryPushedRef.current = false;
      skipDraftReviewPopStateRef.current = true;
      window.history.back();
    }
  }, []);

  useEffect(() => {
    const onPopState = () => {
      if (skipDraftReviewPopStateRef.current) {
        skipDraftReviewPopStateRef.current = false;
        return;
      }
      setDraftReviewModal((open) => {
        if (!open) return open;
        // Stay open if we only popped a stacked layer (e.g. fullscreen photo) and review entry remains.
        if ((window.history.state as { beatlyDraftReview?: boolean } | null)?.beatlyDraftReview) {
          return open;
        }
        draftReviewHistoryPushedRef.current = false;
        return null;
      });
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    const onPopState = () => {
      if (skipContentApprovalPhotoPopStateRef.current) {
        skipContentApprovalPhotoPopStateRef.current = false;
        return;
      }
      if (!contentApprovalPhotoHistoryPushedRef.current) return;
      closeContentApprovalPhotoViaPopStateRef.current = true;
      contentApprovalPhotoHistoryPushedRef.current = false;
      setContentApprovalPhotoProviderKey((k) => k + 1);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  /** Draft review modal: ArrowLeft/ArrowRight match outer chevrons (skip while typing in fields). */
  useEffect(() => {
    if (!draftReviewModal) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const el = e.target;
      if (el instanceof HTMLElement) {
        const tag = el.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        if (el.isContentEditable) return;
      }
      const draftIndex = draftReviewModal.allDrafts.findIndex((d) => d.id === draftReviewModal.draftId);
      if (draftIndex < 0) return;
      const hasPrevious = draftIndex > 0;
      const hasNext = draftIndex < draftReviewModal.allDrafts.length - 1;
      if (e.key === 'ArrowLeft') {
        if (!hasPrevious) return;
        e.preventDefault();
        const prev = draftReviewModal.allDrafts[draftIndex - 1];
        setDraftReviewFeedback({ caption: '', files: {} });
        setDraftReviewToggles({ caption: true, files: {} });
        setDraftReviewGeneralTopics([]);
        setDraftReviewGeneralText('');
        setDraftReviewModal({ ...draftReviewModal, draftId: prev.id, selectedVersionIndex: undefined });
        return;
      }
      if (!hasNext) return;
      e.preventDefault();
      const next = draftReviewModal.allDrafts[draftIndex + 1];
      setDraftReviewFeedback({ caption: '', files: {} });
      setDraftReviewToggles({ caption: true, files: {} });
      setDraftReviewGeneralTopics([]);
      setDraftReviewGeneralText('');
      setDraftReviewModal({ ...draftReviewModal, draftId: next.id, selectedVersionIndex: undefined });
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [draftReviewModal]);

  const pageSize = 10;
  const campaignsListFilters = useMemo(() => {
    const platform =
      channelFilter === 'all'
        ? undefined
        : (channelFilter.toUpperCase() as 'INSTAGRAM' | 'TIKTOK' | 'YOUTUBE');
    return {
      search: debouncedSearchQuery.trim() || undefined,
      brandName: selectedBrand !== ALL_BRANDS_VALUE ? selectedBrand : undefined,
      listStatus: statusFilter !== 'all' ? statusFilter : undefined,
      budgetFilter: budgetFilter !== 'all' ? budgetFilter : undefined,
      sortBy,
      platform,
    };
  }, [debouncedSearchQuery, selectedBrand, statusFilter, channelFilter, budgetFilter, sortBy]);

  const { data: campaignsData, isLoading: campaignsLoading, isError: campaignsError } = useCampaigns({
    pageNumber: currentPage - 1,
    pageSize,
    teamScope: campaignsTeamScope,
    allTeamIds,
    filters: campaignsListFilters,
  });

  const campaigns: Campaign[] = useMemo(
    () =>
      (campaignsData.campaigns ?? []).map((row) =>
        mapApiCampaignToCampaign(row, statusFilter)
      ),
    [campaignsData.campaigns, statusFilter]
  );

  /**
   * Client-side filter by campaign name / brand when search is active.
   * The API `search` param is not always reliable for substring matches on titles; this keeps the list aligned with what users type.
   */
  const campaignsListFiltered = useMemo(() => {
    const q = debouncedSearchQuery.trim().toLowerCase();
    if (!q) return campaigns;
    return campaigns.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.brand.toLowerCase().includes(q),
    );
  }, [campaigns, debouncedSearchQuery]);

  const campaignIdForDetail = campaignIdFromUrl ?? selectedCampaign?.id;
  /** Real campaign ids from API look like `123456-cpg`. */
  const isCampaignIdValid = Boolean(
    campaignIdForDetail && campaignIdForDetail.length >= 8 && campaignIdForDetail.includes('-')
  );
  const {
    data: campaignDetail,
    isLoading: campaignDetailLoading,
    isError: campaignDetailError,
  } = useCampaign({
    campaignId: campaignIdForDetail,
    enabled: Boolean(campaignIdForDetail),
  });

  const campaignReportUrl = useMemo(
    () => (campaignDetail?.report_url ?? '').trim(),
    [campaignDetail?.report_url]
  );

  const {
    data: curationData,
    isLoading: curationLoading,
    isFetching: curationFetching,
    isFetched: curationFetched,
    isError: curationError,
    refetch: refetchCuration,
  } = useCuration({
    campaignId: campaignIdForDetail,
    enabled: Boolean(campaignIdForDetail),
  });

  const {
    data: collaborationsResponse,
    isLoading: collaborationsLoading,
    isError: collaborationsError,
  } = useCampaignCollaborations({
    campaignId: campaignIdForDetail,
    enabled: Boolean(campaignIdForDetail && isCampaignIdValid),
  });

  const {
    data: draftsDataApi,
    isLoading: draftsLoading,
    isFetching: draftsFetching,
    isFetched: draftsFetched,
    isError: draftsError,
    refetch: refetchDrafts,
  } = useDrafts({
    campaignId: campaignIdForDetail,
    enabled: Boolean(campaignIdForDetail),
  });

  const beatIdForDetail = selectedApiBeat?.id;
  const { data: beatDetail, isLoading: beatDetailLoading, isError: beatDetailError } = useCampaignBeat({
    beatId: beatIdForDetail,
    enabled: Boolean(selectedApiBeat),
  });

  const displayCampaign: Campaign | null = useMemo(() => {
    if (selectedCampaign && selectedCampaign.id === campaignIdForDetail) return selectedCampaign;
    if (campaignDetail) return mapApiCampaignToCampaign(campaignDetail);
    return null;
  }, [selectedCampaign, campaignIdForDetail, campaignDetail]);

  // Sync selected campaign and sub tab from URL when campaignId or subTab from route change
  useEffect(() => {
    if (campaignIdFromUrl) {
      const campaign = campaigns.find((c) => c.id === campaignIdFromUrl);
      if (campaign) {
        setSelectedCampaign(campaign);
      }
      if (subTabFromUrl) {
        setCurrentSubPage(subTabFromUrl);
      } else {
        setCurrentSubPage('overview');
      }
    } else {
      setSelectedCampaign(null);
      setCurrentSubPage('overview');
    }
  }, [campaignIdFromUrl, subTabFromUrl, campaigns]);

  // When we landed via URL and campaign detail loaded, set selectedCampaign from API so state is in sync
  useEffect(() => {
    if (campaignIdFromUrl && campaignDetail && !selectedCampaign) {
      setSelectedCampaign(mapApiCampaignToCampaign(campaignDetail));
    }
  }, [campaignIdFromUrl, campaignDetail, selectedCampaign]);

  // ── Unique brands from campaigns (for header selector) ──
  const brandOptions = useMemo(() => {
    const brands = [...new Set(campaigns.map(c => c.brand).filter(Boolean))] as string[];
    brands.sort((a, b) => a.localeCompare(b));
    return [ALL_BRANDS_VALUE, ...brands];
  }, [campaigns]);

  const activeFilterCount = [
    Boolean(searchQuery.trim()),
    selectedBrand !== ALL_BRANDS_VALUE,
    statusFilter !== 'all',
    channelFilter !== 'all',
    budgetFilter !== 'all',
  ].filter(Boolean).length;

  const countTotal = campaignsData.count_total ?? 0;
  const searchApplied = debouncedSearchQuery.trim().length > 0;
  const listCountTotal = searchApplied ? campaignsListFiltered.length : countTotal;
  const totalPages = Math.max(1, Math.ceil(listCountTotal / pageSize));
  const hasMore = searchApplied
    ? currentPage * pageSize < listCountTotal
    : Boolean(campaignsData.has_more ?? false);
  const currentCampaigns = campaignsListFiltered;

  const statusConfig: Record<string, { dot: string; text: string; bg: string; label: string }> = {
    LAUNCHED: { dot: 'bg-[#be43ff]', text: 'text-[#be43ff]', bg: 'bg-[#be43ff]/8', label: 'Active' },
    DONE: { dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', label: 'Done' },
  };

  const PlatformIcon = ({ platform, size = 'md' }: { platform: 'instagram' | 'tiktok' | 'youtube'; size?: 'sm' | 'md' }) => {
    const isSm = size === 'sm';
    const wrapper = isSm ? 'w-4 h-4' : 'w-[26px] h-[26px]';
    const icon = isSm ? 'w-2.5 h-2.5' : 'w-3 h-3';
    if (platform === 'instagram') {
      return (
        <div className={`${wrapper} rounded-full border-[1.5px] border-white bg-pink-50 flex items-center justify-center shrink-0`} title={t('contentReview.platformNames.instagram')}>
          <Instagram className={`${icon} text-pink-600`} />
        </div>
      );
    }
    if (platform === 'tiktok') {
      return (
        <div className={`${wrapper} rounded-full border-[1.5px] border-white bg-gray-100 flex items-center justify-center shrink-0`} title={t('contentReview.platformNames.tiktok')}>
          <svg className={icon} viewBox="0 0 24 24" fill="none">
            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" fill="#101828"/>
          </svg>
        </div>
      );
    }
    if (platform === 'youtube') {
      return (
        <div className={`${wrapper} rounded-full border-[1.5px] border-white bg-red-50 flex items-center justify-center shrink-0`} title={t('contentReview.platformNames.youtube')}>
          <Youtube className={`${icon} text-red-600`} />
        </div>
      );
    }
    return null;
  };

  const formatDateShort = useCallback((dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }, []);

  /** Date only for Content Approval cards (hide time; product decision). Returns '–' if invalid. */
  const formatDateOnly = (dateStr: string | undefined): string => {
    if (dateStr == null || dateStr === '') return '–';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '–';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const beatsForList: BeatRow[] = useMemo(() => {
    if (campaignDetail?.beats?.length) {
      return mapApiBeatsToRows(campaignDetail.beats, formatDateShort, t);
    }
    return [];
  }, [campaignDetail?.beats, formatDateShort, t]);

  // Sync selected beat from URL (so /campaigns/:id/overview/beat/:beatId opens the right beat).
  // When the row exists in campaign beats, prefer it; if not yet in list, keep any in-memory selection
  // (e.g. minimal row from collaborations brief link) until beats load or user clears URL.
  useEffect(() => {
    if (beatIdFromUrl && beatsForList.length > 0) {
      const row = beatsForList.find((b) => String(b.id) === String(beatIdFromUrl));
      if (row) {
        setSelectedApiBeat(row);
      }
    } else if (!beatIdFromUrl) {
      setSelectedApiBeat(null);
    }
  }, [beatIdFromUrl, beatsForList]);

  const openBeatBriefFromReview = useCallback(
    (beatId: string) => {
      const row = beatsForList.find((b) => String(b.id) === String(beatId));
      setSelectedApiBeat(
        row ?? {
          id: String(beatId),
          name: '–',
          dates: '–',
          status: '–',
          icon: 'content',
        }
      );
      if (campaignIdForDetail && navigateToPath) {
        navigateToPath(
          getPathForPage('see-campaigns', {
            campaignId: campaignIdForDetail,
            subTab: currentSubPage,
            beatId: String(beatId),
          })
        );
      }
    },
    [beatsForList, campaignIdForDetail, currentSubPage, navigateToPath]
  );

  const suggestionsForList: SuggestionRow[] = useMemo(() => {
    if (!curationData?.length) return [];
    return curationData.map(mapCurationToSuggestionRow);
  }, [curationData]);

  /** Stable id set for suggestion content SSE (avoids restarting on unrelated re-renders). */
  const suggestionContentStreamIdsKey = useMemo(() => {
    if (!curationData?.length) return `${campaignIdForDetail ?? ''}|`;
    const ids = [
      ...new Set(
        curationData
          .map((c) => {
            const inf = c.influencer ?? {};
            return typeof inf.influencer_id === 'string' ? inf.influencer_id.trim() : '';
          })
          .filter(Boolean)
      ),
    ].sort();
    return `${campaignIdForDetail ?? ''}|${ids.join(',')}`;
  }, [campaignIdForDetail, curationData]);

  useEffect(() => {
    lastSuggestionContentStreamKeyRef.current = null;
    if (suggestionContentStreamAbortRef.current) {
      suggestionContentStreamAbortRef.current();
      suggestionContentStreamAbortRef.current = null;
    }
    setSuggestionCreatorContent(new Map());
    setSuggestionContentLoadingIds(new Set());
  }, [campaignIdForDetail]);

  useEffect(() => {
    if (currentSubPage !== 'profiles') {
      if (suggestionContentStreamAbortRef.current) {
        suggestionContentStreamAbortRef.current();
        suggestionContentStreamAbortRef.current = null;
      }
      return;
    }
    if (!campaignIdForDetail || !isCampaignIdValid) return;
    if (!curationFetched || curationFetching) return;

    const token = getTokenFromStorage();
    if (!token) return;

    const rows = !curationData?.length ? [] : curationData.map(mapCurationToSuggestionRow);
    const ids = [
      ...new Set(rows.map((s) => s.influencerId).filter((id): id is string => Boolean(id && id.trim()))),
    ].sort();
    if (ids.length === 0) return;

    const streamKey = suggestionContentStreamIdsKey;
    const mapSnap = suggestionCreatorContentRef.current;
    const idsToFetch = ids.filter((id) => !mapSnap.has(id));

    if (lastSuggestionContentStreamKeyRef.current === streamKey && idsToFetch.length === 0) {
      return;
    }

    if (idsToFetch.length === 0) {
      lastSuggestionContentStreamKeyRef.current = streamKey;
      return;
    }

    if (suggestionContentStreamAbortRef.current) {
      suggestionContentStreamAbortRef.current();
      suggestionContentStreamAbortRef.current = null;
    }

    lastSuggestionContentStreamKeyRef.current = streamKey;

    setSuggestionContentLoadingIds((prev) => {
      const next = new Set(prev);
      idsToFetch.forEach((id) => next.add(id));
      return next;
    });

    const platformByCreatorId = new Map(rows.filter((s) => s.influencerId).map((s) => [s.influencerId, s.platform]));

    const handleContentPage = (page: V1CreatorContentPage) => {
      const platform = platformByCreatorId.get(page.creator_id);
      const transformed = transformContentArrayToCardFormat(page.contents, platform, token);
      setSuggestionCreatorContent((prev) => {
        const next = new Map(prev);
        next.set(page.creator_id, transformed);
        suggestionCreatorContentRef.current = next;
        return next;
      });
      setSuggestionContentLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(page.creator_id);
        return next;
      });
    };

    const { abort, promise } = streamCreatorsContent(idsToFetch, token, handleContentPage, 5);
    suggestionContentStreamAbortRef.current = abort;

    void promise
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === 'AbortError') return;
        showError(err instanceof Error ? err.message : t('campaigns.profiles.suggestionContentStreamError'));
        setSuggestionCreatorContent((prev) => {
          const next = new Map(prev);
          idsToFetch.forEach((id) => {
            if (!next.has(id)) next.set(id, []);
          });
          suggestionCreatorContentRef.current = next;
          return next;
        });
      })
      .finally(() => {
        suggestionContentStreamAbortRef.current = null;
        setSuggestionContentLoadingIds((prev) => {
          const next = new Set(prev);
          idsToFetch.forEach((id) => next.delete(id));
          return next;
        });
      });

    return () => {
      abort();
      suggestionContentStreamAbortRef.current = null;
    };
  }, [
    currentSubPage,
    campaignIdForDetail,
    isCampaignIdValid,
    curationFetched,
    curationFetching,
    curationData,
    suggestionContentStreamIdsKey,
    t,
    showError,
  ]);

  /** Collaborations tab: GET /client/campaign/:id/collaborations; drafts fill missing previews (same assets as Content Approval). */
  const collaborationsForReview: Collaborator[] = useMemo(() => {
    const rows = collaborationsResponse?.collaborations ?? [];
    const fromCollabs = mapCampaignCollaborationsToCollaborators(rows);
    return mergeDraftsIntoCollaboratorsForReview(fromCollabs, draftsDataApi ?? []);
  }, [collaborationsResponse, draftsDataApi]);

  /** Approved Profiles cards: merge delivery from GET collaborations (all client-accepted rows, not only ONGOING/DONE). */
  const deliveryCollaborationByCurationId = useMemo(() => {
    const raw = collaborationsResponse?.collaborationsClientAccepted ?? [];
    const m = new Map<string, ClientCampaignCollaboration>();
    for (const c of raw) {
      const id = c.collaboration_id;
      if (typeof id === 'string' && id.trim()) {
        m.set(id.trim(), c);
      }
    }
    return m;
  }, [collaborationsResponse?.collaborationsClientAccepted]);

  /** Drafts tab: map API response to UI shape. When backend has no /drafts endpoint, draftsDataApi is never set and we show empty/error. */
  const draftsForList: DraftItemUI[] = useMemo(() => {
    if (!draftsDataApi?.length) return [];
    return draftsDataApi
      .map((item, index) => {
        const versions = (item.versions ?? []).map((v: ClientDraftVersion, i: number) => {
          const medias = Array.isArray(v.medias) ? v.medias : [];
          const rawMedias = medias.filter(m => m.is_raw);
          const firstRaw = rawMedias[0];
          const listFinalPreview = pickDraftListPreview(medias);
          let thumbnail = '';
          if (listFinalPreview.mode === 'image') thumbnail = listFinalPreview.url;
          else if (
            typeof v.thumbnail_url === 'string' &&
            v.thumbnail_url &&
            !isDraftListVideoUrl(v.thumbnail_url)
          ) {
            thumbnail = v.thumbnail_url;
          }
          const rawThumbnail = firstRaw?.url;
          const listRawPreview = firstRaw?.url
            ? pickRawListPreview(firstRaw.url, firstRaw.resource_type)
            : null;
          return {
            version: typeof v.version === 'number' ? v.version : i + 1,
            versionId: typeof v.version_id === 'string' && v.version_id.trim() ? v.version_id.trim() : undefined,
            thumbnail,
            rawThumbnail,
            listFinalPreview,
            listRawPreview,
            draftDate: typeof v.draft_date === 'string' ? v.draft_date : '–',
            caption: typeof v.caption === 'string' ? v.caption : '',
            feedback: typeof v.feedback === 'string' ? v.feedback : undefined,
            reason: typeof v.reason === 'string' ? v.reason : undefined,
            status: (v.status === 'pending' || v.status === 'approved' || v.status === 'declined' ? v.status : 'pending') as 'pending' | 'approved' | 'declined',
            medias: medias.length > 0
              ? medias.map((m) => ({
                  url: m.url,
                  resource_type:
                    m.resource_type === 'video' || m.resource_type === 'image'
                      ? (m.resource_type as 'image' | 'video')
                      : undefined,
                  is_raw: m.is_raw,
                  media_id:
                    typeof m.media_id === 'string' && m.media_id.trim() ? m.media_id.trim() : undefined,
                }))
              : undefined,
            permalink: typeof v.permalink === 'string' && v.permalink.length > 0 ? v.permalink : undefined,
          };
        });
        const apiDraftVersionId = (() => {
          const top = typeof item.draft_id === 'string' && item.draft_id.trim() ? item.draft_id.trim() : undefined;
          if (top) return top;
          const last = versions[versions.length - 1];
          return last?.versionId;
        })();
        const influencerIdDraft =
          typeof item.influencer_id === 'string' && item.influencer_id.trim() ? item.influencer_id.trim() : undefined;
        return {
          id: typeof item.id === 'number' ? item.id : index + 1,
          ...(influencerIdDraft ? { influencerId: influencerIdDraft } : {}),
          creator: typeof item.creator_name === 'string' ? item.creator_name : '–',
          handle: typeof item.handle === 'string' ? item.handle : '–',
          avatar: typeof item.avatar_url === 'string' ? item.avatar_url : '',
          contentType: typeof item.content_type === 'string' ? item.content_type : 'Content',
          contentPlatform: ((): ClientDraftContentPlatform => {
            if (item.content_platform === 'tiktok' || item.content_platform === 'youtube') return item.content_platform;
            return 'instagram';
          })(),
          publicationDate: typeof item.publication_date === 'string' ? item.publication_date : undefined,
          isVideo: Boolean(item.is_video),
          versions,
          apiDraftVersionId,
        };
      })
      .filter(d => d.versions.length > 0);
  }, [draftsDataApi]);

  // Reset tab state when campaign changes (layout effect so we don't paint the previous campaign's tab).
  // suggestionsFilter/draftsFilter are not keyed by campaign in React state; without this, "Approved" from
  // campaign A sticks when opening Profiles on campaign B that has pending items.
  useLayoutEffect(() => {
    if (!campaignIdForDetail || !isCampaignIdValid) return;
    suggestionsDefaultAppliedRef.current = false;
    draftsDefaultAppliedRef.current = false;
    setSuggestionsFilter('pending');
    setDraftsFilter('pending');
  }, [campaignIdForDetail, isCampaignIdValid]);

  // Default tab (Profiles + drafts): pending → approved → declined → pending if all empty. Only after
  // curation/drafts has settled — avoids isLoading false + empty cache flipping tab before real data arrives.
  useEffect(() => {
    if (!campaignIdForDetail || !isCampaignIdValid) return;
    if (!curationFetched || curationFetching) return;
    if (suggestionsDefaultAppliedRef.current) return;
    suggestionsDefaultAppliedRef.current = true;
    const pendingCount = suggestionsForList.filter(s => s.status === 'pending').length;
    const approvedCount = suggestionsForList.filter(s => s.status === 'approved').length;
    const declinedCount = suggestionsForList.filter(s => s.status === 'declined').length;
    setSuggestionsFilter(
      defaultContentReviewStatusFilter({ pending: pendingCount, approved: approvedCount, declined: declinedCount }),
    );
  }, [campaignIdForDetail, isCampaignIdValid, curationFetched, curationFetching, suggestionsForList]);

  useEffect(() => {
    if (!campaignIdForDetail || !isCampaignIdValid) return;
    if (!draftsFetched || draftsFetching) return;
    if (draftsDefaultAppliedRef.current) return;
    draftsDefaultAppliedRef.current = true;
    const latestStatuses = draftsForList.map(d => d.versions[d.versions.length - 1]?.status);
    const pendingCount = latestStatuses.filter(s => s === 'pending').length;
    const approvedCount = latestStatuses.filter(s => s === 'approved').length;
    const declinedCount = latestStatuses.filter(s => s === 'declined').length;
    setDraftsFilter(
      defaultContentReviewStatusFilter({ pending: pendingCount, approved: approvedCount, declined: declinedCount }),
    );
  }, [campaignIdForDetail, isCampaignIdValid, draftsFetched, draftsFetching, draftsForList]);

  return (
    <div
      className={
        campaignIdForDetail
          ? 'flex min-h-0 flex-col overflow-hidden bg-[#f8f9fa] h-[calc(100vh-4rem)] lg:h-[calc(100vh-3.5rem)]'
          : 'min-h-screen bg-[#f8f9fa]'
      }
    >
      {/* ═══════════════════════════════════════════════ */}
      {/*  CAMPAIGN LIST VIEW                            */}
      {/* ═══════════════════════════════════════════════ */}
      {!campaignIdForDetail && (
        <div className="max-w-[1400px] mx-auto space-y-6 p-4 md:p-6 lg:p-8">

          {/* ── Hero Banner with embedded stats ── */}
          <div className="bg-gradient-to-r from-[#2e024c] to-[#5a1a8a] rounded-[24px] overflow-hidden relative">
            <div className="absolute inset-0 opacity-10" style={{
              backgroundImage: 'radial-gradient(circle at 80% 20%, #be43ff 0%, transparent 50%), radial-gradient(circle at 20% 80%, #a9f600 0%, transparent 50%)'
            }} />
            <div className="relative z-10">
              {/* Top section — title + actions */}
              <div className="flex flex-col gap-5 p-4 pb-4 md:flex-row md:items-start md:justify-between md:p-8 md:pb-5">
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full w-fit mb-3">
                    <Activity className="w-3.5 h-3.5 text-[#a9f600]" />
                    <span className="text-white/80 text-[11px]" style={{ fontWeight: 600 }}>Campaign Hub</span>
                  </div>
                  <h1 className="text-white text-[28px] lg:text-[32px]" style={{ fontWeight: 700 }}>Campaigns</h1>
                  <p className="text-white/50 text-sm mt-1.5 max-w-md">
                    Manage and track all your creator campaigns.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2 md:gap-3 md:shrink-0 md:pt-0.5">
                  {/* Team selector (multi-team clients): GET /app/v2/teams/search + POST .../select */}
                  {user?.is_client && clientTeamsSortedByName.length > 1 && (
                    <div className="relative">
                      <span className="sr-only" id="campaigns-team-select-label">{t('nav.team')}</span>
                      <select
                        aria-labelledby="campaigns-team-select-label"
                        aria-busy={teamSwitchLoading || teamsSearchLoading}
                        value={campaignsTeamScope === 'all' ? ALL_TEAMS_CAMPAIGNS_VALUE : campaignsTeamScope}
                        onChange={(e) => void handleCampaignsTeamSelect(e.target.value)}
                        disabled={teamSwitchLoading || teamsSearchLoading}
                        className="appearance-none bg-white/10 backdrop-blur-sm border border-white/15 rounded-xl px-4 py-2.5 pr-10 text-[13px] text-white hover:bg-white/15 focus:outline-none cursor-pointer transition-colors disabled:opacity-60"
                        style={{ fontWeight: 600 }}
                      >
                        <option value={ALL_TEAMS_CAMPAIGNS_VALUE} className="text-[#101828]">
                          {t('campaigns.allTeams')}
                        </option>
                        {clientTeamsSortedByName.map((team) => (
                          <option key={team.team_id} value={team.team_id} className="text-[#101828]">
                            {team.name || team.team_id}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-white/50">
                        {teamSwitchLoading || teamsSearchLoading ? (
                          <Loader2
                            className="w-4 h-4 animate-spin"
                            aria-hidden={false}
                            aria-label={
                              teamSwitchLoading
                                ? t('campaigns.teamSwitchInProgress')
                                : t('campaigns.teamsListLoading')
                            }
                          />
                        ) : (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Brand Selector */}
                  <div className="relative">
                    <select
                      value={selectedBrand}
                      onChange={(e) => { setSelectedBrand(e.target.value); setCurrentPage(1); }}
                      className="appearance-none bg-white/10 backdrop-blur-sm border border-white/15 rounded-xl px-4 py-2.5 pr-10 text-[13px] text-white hover:bg-white/15 focus:outline-none cursor-pointer transition-colors"
                      style={{ fontWeight: 600 }}
                    >
                      {brandOptions.map((brand) => (
                        <option key={brand} value={brand} className="text-[#101828]">{brand}</option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-white/50">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <button
                    onClick={() => onNavigate?.('campaigns')}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#be43ff] text-white text-[13px] hover:bg-[#a830e0] active:scale-[0.97] transition-all shadow-lg shadow-[#be43ff]/25"
                    style={{ fontWeight: 600 }}
                  >
                    <Plus className="w-4 h-4" />
                    New Campaign
                  </button>
                </div>
              </div>


            </div>
          </div>

          {/* ── Row 2: Campaign List Card ── */}
          <div className="bg-white rounded-[24px] border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.02)] overflow-hidden">
            {campaignsError && (
              <div className="mx-4 mt-4 p-4 rounded-xl bg-red-50 border border-red-200 flex items-center gap-2 text-red-800 text-sm md:mx-8 md:mt-6" role="alert">
                <AlertCircle className="w-4 h-4 shrink-0" aria-hidden />
                <span>Failed to load campaigns. Please try again.</span>
              </div>
            )}
            <>
            {/* Card Header — Search + Filters + Sort (always mounted so the input keeps focus while loading) */}
            <div className="space-y-3 px-4 pb-4 pt-5 md:px-8 md:pb-4 md:pt-7">
              {/* Row 1: Title + Search */}
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="text-[18px] text-[#101828]" style={{ fontWeight: 700 }}>All Campaigns</h3>
                  <span className="text-[11px] text-[#98a2b3] bg-[#f8f9fa] px-2.5 py-1 rounded-lg" style={{ fontWeight: 600 }}>
                    {t('campaigns.list.shownOfTotal', { shown: campaignsListFiltered.length, total: listCountTotal })}
                  </span>
                </div>
                <div className="relative w-full md:w-64 md:shrink-0">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#98a2b3]" />
                  <input
                    type="search"
                    enterKeyHint="search"
                    placeholder={t('campaigns.list.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label={t('campaigns.list.searchAria')}
                    className="w-full pl-9 pr-8 py-2 rounded-xl border border-gray-200 bg-[#f8f9fa] text-[12px] text-[#101828] placeholder:text-[#98a2b3] focus:outline-none focus:ring-1 focus:ring-[#be43ff]/30 focus:border-[#be43ff]/40 transition-all"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#98a2b3] hover:text-[#667085]"
                      aria-label={t('campaigns.list.clearSearchAria')}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Row 2: Filters + Sort */}
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  {/* Status pills */}
                  {(['all', 'LAUNCHED', 'DONE'] as const).map((s) => {
                    const active = statusFilter === s;
                    const label = s === 'all' ? 'All' : s === 'LAUNCHED' ? 'Active' : 'Done';
                    const dot = s === 'LAUNCHED' ? 'bg-[#be43ff]' : s === 'DONE' ? 'bg-emerald-500' : '';
                    return (
                      <button
                        key={s}
                        onClick={() => { setStatusFilter(s); setCurrentPage(1); }}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] transition-colors ${
                          active ? 'bg-[#101828] text-white' : 'text-[#667085] hover:bg-[#f8f9fa]'
                        }`}
                        style={{ fontWeight: 600 }}
                      >
                        {dot && <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-white/60' : dot}`} />}
                        {label}
                      </button>
                    );
                  })}

                  <div className="w-px h-5 bg-gray-200 mx-1" />

                  {/* Channel filter */}
                  <div className="relative">
                    <select
                      value={channelFilter}
                      onChange={(e) => { setChannelFilter(e.target.value as typeof channelFilter); setCurrentPage(1); }}
                      aria-label={t('contentReview.filters.channelSelectAria')}
                      className={`appearance-none px-3 py-1.5 pr-7 rounded-xl text-[11px] border transition-colors cursor-pointer focus:outline-none ${
                        channelFilter !== 'all'
                          ? 'bg-[#be43ff]/8 border-[#be43ff]/20 text-[#be43ff]'
                          : 'bg-white border-gray-200 text-[#667085] hover:bg-[#f8f9fa]'
                      }`}
                      style={{ fontWeight: 600 }}
                    >
                      <option value="all">{t('contentReview.filters.allChannels')}</option>
                      <option value="instagram">{t('contentReview.platformNames.instagram')}</option>
                      <option value="tiktok">{t('contentReview.platformNames.tiktok')}</option>
                      <option value="youtube">{t('contentReview.platformNames.youtube')}</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <svg className="w-3 h-3 text-[#98a2b3]" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                    </div>
                  </div>

                  {/* Budget filter */}
                  <div className="relative">
                    <select
                      value={budgetFilter}
                      onChange={(e) => { setBudgetFilter(e.target.value as typeof budgetFilter); setCurrentPage(1); }}
                      className={`appearance-none px-3 py-1.5 pr-7 rounded-xl text-[11px] border transition-colors cursor-pointer focus:outline-none ${
                        budgetFilter !== 'all'
                          ? 'bg-[#be43ff]/8 border-[#be43ff]/20 text-[#be43ff]'
                          : 'bg-white border-gray-200 text-[#667085] hover:bg-[#f8f9fa]'
                      }`}
                      style={{ fontWeight: 600 }}
                    >
                      <option value="all">All Budgets</option>
                      <option value="under150k">Under 150K</option>
                      <option value="150k-250k">150K – 250K</option>
                      <option value="over250k">Over 250K</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <svg className="w-3 h-3 text-[#98a2b3]" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                    </div>
                  </div>

                  {/* Clear filters */}
                  {activeFilterCount > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedBrand(ALL_BRANDS_VALUE);
                        setStatusFilter('all');
                        setChannelFilter('all');
                        setBudgetFilter('all');
                        setCurrentPage(1);
                      }}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-[11px] text-[#be43ff] hover:bg-[#be43ff]/5 transition-colors"
                      style={{ fontWeight: 600 }}
                      aria-label={t('campaigns.list.clearFiltersAria')}
                    >
                      <X className="w-3 h-3 shrink-0" aria-hidden />
                      {t('campaigns.list.clearFilters')}
                    </button>
                  )}
                </div>

                {/* Sort */}
                <div className="relative shrink-0 md:ml-0">
                  <div className="flex items-center gap-1.5">
                    <ArrowUpDown className="w-3 h-3 text-[#98a2b3]" />
                    <select
                      value={sortBy}
                      onChange={(e) => { setSortBy(e.target.value as typeof sortBy); setCurrentPage(1); }}
                      className="max-w-[100vw] appearance-none bg-transparent text-[11px] text-[#667085] pr-5 cursor-pointer focus:outline-none"
                      style={{ fontWeight: 600 }}
                    >
                      <option value="newest">Newest First</option>
                      <option value="oldest">Oldest First</option>
                      <option value="budget-high">Budget: High → Low</option>
                      <option value="budget-low">Budget: Low → High</option>
                      <option value="name-az">Name: A → Z</option>
                      <option value="name-za">Name: Z → A</option>
                    </select>
                    <div className="pointer-events-none absolute right-0 inset-y-0 flex items-center">
                      <svg className="w-3 h-3 text-[#98a2b3]" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Table Header */}
            <div className="hidden grid-cols-12 gap-4 border-b border-gray-100 px-8 py-2.5 md:grid">
              <div className="col-span-2">
                <span className="text-[10px] text-[#98a2b3]" style={{ fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>Brand</span>
              </div>
              <div className="col-span-3">
                <span className="text-[10px] text-[#98a2b3]" style={{ fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>Campaign</span>
              </div>
              <div className="col-span-2">
                <span className="text-[10px] text-[#98a2b3]" style={{ fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>Channels</span>
              </div>
              <div className="col-span-2">
                <span className="text-[10px] text-[#98a2b3]" style={{ fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>Budget</span>
              </div>
              <div className="col-span-2">
                <span className="text-[10px] text-[#98a2b3]" style={{ fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>Duration</span>
              </div>
              <div className="col-span-1 text-right">
                <span className="text-[10px] text-[#98a2b3]" style={{ fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>Status</span>
              </div>
            </div>

            {/* Campaign Rows — loading overlay keeps header + input mounted */}
            <div
              className="relative min-h-[12rem] px-4 py-2"
              aria-busy={campaignsLoading}
            >
              {campaignsLoading && (
                <div
                  className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/70 backdrop-blur-[1px]"
                  role="status"
                  aria-live="polite"
                >
                  <Loader2 className="h-8 w-8 animate-spin text-[#be43ff]" aria-hidden />
                  <span className="sr-only">{t('campaigns.list.loadingListSr')}</span>
                </div>
              )}
              {!campaignsLoading && campaigns.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Search className="w-8 h-8 text-[#d0d5dd] mb-3" aria-hidden />
                  <p className="text-[14px] text-[#667085]" style={{ fontWeight: 600 }}>{t('campaigns.list.emptyTitle')}</p>
                  <p className="text-[12px] text-[#98a2b3] mt-1">{t('campaigns.list.emptyHint')}</p>
                </div>
              )}
              {currentCampaigns.map((campaign, _idx) => {
                const stCfg = statusConfig[campaign.status] || statusConfig.DONE;
                const openCampaign = () => {
                  if (navigateToPath) {
                    navigateToPath(`/campaigns/${campaign.id}`);
                  } else {
                    setSelectedCampaign(campaign);
                    setCurrentSubPage('overview');
                  }
                };
                return (
                  <Fragment key={campaign.id}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={openCampaign}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openCampaign();
                        }
                      }}
                      className="hidden cursor-pointer grid-cols-12 items-center gap-4 rounded-2xl px-4 py-3.5 transition-all duration-150 group hover:bg-[#f8f9fa]/70 md:grid"
                    >
                      {/* Brand */}
                      <div className="col-span-2 flex items-center gap-3">
                        <div className="w-9 h-9 shrink-0 overflow-hidden rounded-xl border border-gray-100 bg-gray-50 transition-colors group-hover:border-[#be43ff]/35">
                          <img
                            src={campaign.brandLogo || brandLogo}
                            alt={campaign.brand}
                            className="h-full w-full object-cover"
                          />
                        </div>
                        <span className="truncate text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>{campaign.brand}</span>
                      </div>

                      {/* Name */}
                      <div className="col-span-3 flex min-w-0 items-center">
                        <span className="truncate text-[13px] text-[#101828]" style={{ fontWeight: 500 }}>{campaign.name}</span>
                      </div>

                      {/* Channels */}
                      <div className="col-span-2 flex items-center">
                        <div className="-space-x-1 flex">
                          {campaign.platforms.map((platform, pidx) => (
                            <PlatformIcon key={pidx} platform={platform} />
                          ))}
                        </div>
                      </div>

                      {/* Budget */}
                      <div className="col-span-2 flex items-center">
                        <span className="text-[12px] text-[#101828]" style={{ fontWeight: 600 }}>{campaign.budget}</span>
                      </div>

                      {/* Date */}
                      <div className="col-span-2 flex items-center">
                        <div className="flex items-center gap-1.5 text-[11px] text-[#667085]">
                          <Calendar className="h-3 w-3 shrink-0 text-[#98a2b3]" />
                          <span className="truncate">{formatDateShort(campaign.dateStart)} – {formatDateShort(campaign.dateEnd)}</span>
                        </div>
                      </div>

                      {/* Status */}
                      <div className="col-span-1 flex items-center justify-end">
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 ${stCfg.bg}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${stCfg.dot}`} />
                          <span className={`text-[10px] ${stCfg.text}`} style={{ fontWeight: 600 }}>{stCfg.label}</span>
                        </span>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={openCampaign}
                      className="mb-2 flex w-full flex-col gap-3 rounded-2xl border border-gray-100 bg-white p-4 text-left shadow-sm transition-colors hover:bg-[#f8f9fa]/80 md:hidden"
                    >
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 shrink-0 overflow-hidden rounded-xl border border-gray-100 bg-gray-50">
                          <img
                            src={campaign.brandLogo || brandLogo}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>{campaign.brand}</p>
                          <p className="mt-0.5 truncate text-[13px] text-[#344054]" style={{ fontWeight: 500 }}>{campaign.name}</p>
                        </div>
                        <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 ${stCfg.bg}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${stCfg.dot}`} />
                          <span className={`text-[10px] ${stCfg.text}`} style={{ fontWeight: 600 }}>{stCfg.label}</span>
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px] text-[#667085]">
                        <div className="-space-x-1 flex">
                          {campaign.platforms.map((platform, pidx) => (
                            <PlatformIcon key={pidx} platform={platform} />
                          ))}
                        </div>
                        <span style={{ fontWeight: 600 }} className="text-[#101828]">{campaign.budget}</span>
                        <span className="flex min-w-0 items-center gap-1">
                          <Calendar className="h-3 w-3 shrink-0 text-[#98a2b3]" />
                          {formatDateShort(campaign.dateStart)} – {formatDateShort(campaign.dateEnd)}
                        </span>
                      </div>
                    </button>
                  </Fragment>
                );
              })}
            </div>

            {/* Pagination Footer */}
            <div className="flex flex-wrap items-center justify-center gap-2 border-t border-gray-100 px-4 py-5 md:px-8">
              <button
                type="button"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1 || campaignsLoading}
                className="w-8 h-8 rounded-xl flex items-center justify-center hover:bg-[#f8f9fa] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4 text-[#667085]" />
              </button>
              <span className="text-[12px] text-[#667085] px-3" style={{ fontWeight: 600 }}>
                {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage >= totalPages || !hasMore || campaignsLoading}
                className="w-8 h-8 rounded-xl flex items-center justify-center hover:bg-[#f8f9fa] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4 text-[#667085]" />
              </button>
            </div>
            </>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/*  CAMPAIGN DETAIL VIEW                          */}
      {/* ═══════════════════════════════════════════════ */}
      {campaignIdForDetail && (
        <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden md:flex-row">
          {/* Second Sidebar — vertical list on mobile (full width); fixed column from md */}
          <SecondarySidebar
            className="max-md:h-auto max-md:min-h-0 max-md:border-b max-md:border-gray-200/80 md:h-full"
            width="w-full md:w-64"
          >
            <SecondarySidebarHeader>
              <button
                onClick={() => {
                  if (navigateToPath) {
                    navigateToPath('/campaigns');
                  } else {
                    setSelectedCampaign(null);
                  }
                }}
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Campaigns
              </button>
            </SecondarySidebarHeader>

            <nav
              className="flex flex-col gap-1 p-3 md:flex-1 md:gap-0.5"
              aria-label={t('campaigns.detailSubNavAria')}
            >
              <SecondarySidebarNavItem
                className="min-w-0 !w-full"
                onClick={() => {
                  if (navigateToPath && campaignIdForDetail) {
                    navigateToPath(`/campaigns/${campaignIdForDetail}/overview`);
                  } else {
                    setCurrentSubPage('overview');
                  }
                }}
                isActive={currentSubPage === 'overview'}
                icon={<BarChart3 className="w-4 h-4" />}
              >
                Overview
              </SecondarySidebarNavItem>
              <SecondarySidebarNavItem
                className="min-w-0 !w-full"
                onClick={() => {
                  if (navigateToPath && campaignIdForDetail) {
                    navigateToPath(
                      getPathForPage('see-campaigns', { campaignId: campaignIdForDetail, subTab: 'profiles' })
                    );
                  } else {
                    setCurrentSubPage('profiles');
                  }
                }}
                isActive={currentSubPage === 'profiles'}
                icon={<Sparkles className="w-4 h-4" />}
              >
                Profiles
              </SecondarySidebarNavItem>
              <SecondarySidebarNavItem
                className="min-w-0 !w-full"
                onClick={() => {
                  if (navigateToPath && campaignIdForDetail) {
                    navigateToPath(`/campaigns/${campaignIdForDetail}/drafts`);
                  } else {
                    setCurrentSubPage('drafts');
                  }
                }}
                isActive={currentSubPage === 'drafts'}
                icon={<FileText className="w-4 h-4" />}
              >
                Content Approval
              </SecondarySidebarNavItem>
              <SecondarySidebarNavItem
                className="min-w-0 !w-full"
                onClick={() => {
                  if (navigateToPath && campaignIdForDetail) {
                    navigateToPath(`/campaigns/${campaignIdForDetail}/collaborations`);
                  } else {
                    setCurrentSubPage('collaborations');
                  }
                }}
                isActive={currentSubPage === 'collaborations'}
                icon={<Users className="w-4 h-4" />}
              >
                {t('campaigns.publicationsTab')}
              </SecondarySidebarNavItem>
              <SecondarySidebarNavItem
                className="min-w-0 !w-full"
                onClick={() => {
                  if (navigateToPath && campaignIdForDetail) {
                    navigateToPath(`/campaigns/${campaignIdForDetail}/summary`);
                  } else {
                    setCurrentSubPage('summary');
                  }
                }}
                isActive={currentSubPage === 'summary'}
                icon={<BarChart3 className="w-4 h-4" />}
              >
                {t('campaigns.resultsTab')}
              </SecondarySidebarNavItem>
            </nav>
          </SecondarySidebar>

          {/* Main Content Area — scrolls independently; sidebar stays fixed (flex min-h-0 chain) */}
          <div className="min-h-0 min-w-0 flex-1 space-y-5 overflow-y-auto p-4 md:p-6 lg:p-8">

            {campaignDetailError && (
              <div className="p-4 rounded-xl bg-red-50 border border-red-200 flex items-center gap-2 text-red-800 text-sm" role="alert">
                <AlertCircle className="w-4 h-4 shrink-0" aria-hidden />
                <span>Failed to load campaign. Please try again.</span>
              </div>
            )}

            {campaignDetailLoading && !campaignDetail && (
              <div className="flex items-center justify-center py-16" role="status" aria-live="polite">
                <Loader2 className="w-8 h-8 text-[#be43ff] animate-spin" aria-hidden />
                <span className="sr-only">Loading campaign</span>
              </div>
            )}

            {/* Detail Hero Card — with embedded stats (overview only), from API when loaded */}
            {currentSubPage === 'overview' && displayCampaign && (
            <div className="bg-gradient-to-r from-[#2e024c] to-[#5a1a8a] rounded-[24px] overflow-hidden relative">
              <div className="absolute inset-0 opacity-10" style={{
                backgroundImage: 'radial-gradient(circle at 70% 30%, #be43ff 0%, transparent 50%), radial-gradient(circle at 30% 80%, #a9f600 0%, transparent 50%)'
              }} />
              <div className="relative z-10">
                {/* Top section — brand info */}
                <div className="flex flex-col gap-4 p-4 pb-4 md:flex-row md:items-center md:gap-5 md:p-8 md:pb-5">
                  <div className="h-14 w-14 shrink-0 overflow-hidden rounded-2xl border border-white/15 bg-white/10 backdrop-blur-md">
                    <img src={displayCampaign.brandLogo || brandLogo} alt={displayCampaign.brand} className="h-full w-full object-cover" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-col items-start gap-2 md:flex-row md:items-center md:gap-3">
                      <h1 className="truncate text-[24px] text-white" style={{ fontWeight: 700 }}>
                        {displayCampaign.name}
                      </h1>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full shrink-0 ${
                        displayCampaign.status === 'LAUNCHED' ? 'bg-[#a9f600]/20' : 'bg-white/10'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          displayCampaign.status === 'LAUNCHED' ? 'bg-[#a9f600]' : 'bg-white/50'
                        }`} />
                        <span className="text-[10px] text-white" style={{ fontWeight: 600 }}>
                          {displayCampaign.status === 'LAUNCHED' ? 'Active' : 'Completed'}
                        </span>
                      </span>
                    </div>
                    <p className="text-white/50 text-[13px]">{displayCampaign.brand} · {formatDateShort(displayCampaign.dateStart)} – {formatDateShort(displayCampaign.dateEnd)}</p>
                  </div>
                </div>

                {/* Embedded stats strip — from API (total_influencer_count, beats length, budget) */}
                <div className="px-4 pb-4 pt-1 md:px-6">
                  <div className="grid grid-cols-1 gap-2.5 md:grid-cols-3">
                    {[
                      { label: 'Influencers', value: campaignDetail?.total_influencer_count != null ? String(campaignDetail.total_influencer_count) : '–', icon: Users },
                      { label: 'Total Beats', value: campaignDetail?.beats != null ? String(campaignDetail.beats.length) : '–', icon: BarChart3 },
                      { label: 'Budget', value: displayCampaign.budget, icon: DollarSign },
                    ].map((stat, i) => (
                      <div key={i} className="bg-white/[0.08] backdrop-blur-sm rounded-2xl border border-white/[0.08] px-4 py-3.5">
                        <div className="flex items-center gap-2 mb-1.5">
                          <stat.icon className="w-3.5 h-3.5 text-white/40 shrink-0" />
                          <span className="text-[9px] text-white/40 truncate" style={{ fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>
                            {stat.label}
                          </span>
                        </div>
                        <span className="text-[17px] text-white block" style={{ fontWeight: 700, paddingLeft: '1.4rem' }}>{stat.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Campaign phases from API `todos` (GET campaign) — same source as beatly-web-app */}
                {Array.isArray(campaignDetail?.todos) && campaignDetail.todos.length > 0 ? (
                  <div className="px-4 pb-6 pt-1 md:px-8">
                    {/* Mobile: vertical list — labels wrap; no horizontal scroll */}
                    <ul className="flex flex-col gap-3 md:hidden" aria-label={t('campaigns.phaseStepperAria')}>
                      {campaignDetail.todos.map((todo, i) => (
                        <li key={`${todo.name}-m-${i}`} className="flex gap-3">
                          <div
                            className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                              todo.status === 'DONE'
                                ? 'bg-[#a9f600]/20'
                                : todo.status === 'ONGOING'
                                  ? 'bg-[#a9f600]/15'
                                  : 'bg-white/10'
                            }`}
                          >
                            {todo.status === 'DONE' ? (
                              <CheckCircle2 className="h-3.5 w-3.5 text-[#a9f600]" aria-hidden />
                            ) : todo.status === 'ONGOING' ? (
                              <span className="flex h-3.5 w-3.5 items-center justify-center" aria-hidden title="In progress">
                                <span className="h-2 w-2 animate-pulse rounded-full bg-[#a9f600] shadow-[0_0_0_2px_rgba(169,246,0,0.35)]" />
                              </span>
                            ) : (
                              <Clock className="h-3.5 w-3.5 text-white/45" aria-hidden />
                            )}
                          </div>
                          <span
                            className="min-w-0 flex-1 text-[11px] leading-snug text-white/75"
                            style={{ fontWeight: 500 }}
                          >
                            {todo.name}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* Tablet/desktop: horizontal stepper */}
                    <div className="hidden md:flex md:w-full md:items-center md:overflow-visible">
                      {campaignDetail.todos.map((todo, i, arr) => (
                        <div key={`${todo.name}-${i}`} className="flex shrink-0 items-center last:md:flex-initial md:min-w-0 md:flex-1">
                          <div className="flex flex-col items-center gap-1.5">
                            <div
                              className={`flex h-6 w-6 items-center justify-center rounded-full ${
                                todo.status === 'DONE'
                                  ? 'bg-[#a9f600]/20'
                                  : todo.status === 'ONGOING'
                                    ? 'bg-[#a9f600]/15'
                                    : 'bg-white/10'
                              }`}
                            >
                              {todo.status === 'DONE' ? (
                                <CheckCircle2 className="h-3.5 w-3.5 text-[#a9f600]" aria-hidden />
                              ) : todo.status === 'ONGOING' ? (
                                <span
                                  className="flex h-3.5 w-3.5 items-center justify-center"
                                  aria-hidden
                                  title="In progress"
                                >
                                  <span className="h-2 w-2 rounded-full bg-[#a9f600] shadow-[0_0_0_2px_rgba(169,246,0,0.35)] animate-pulse" />
                                </span>
                              ) : (
                                <Clock className="h-3.5 w-3.5 text-white/45" aria-hidden />
                              )}
                            </div>
                            <span className="whitespace-nowrap text-[9px] text-white/50" style={{ fontWeight: 500 }}>
                              {todo.name}
                            </span>
                          </div>
                          {i < arr.length - 1 ? (
                            <div
                              className={`mx-2 mb-4 h-px flex-1 ${
                                todo.status === 'DONE' ? 'bg-[#a9f600]/30' : 'bg-white/15'
                              }`}
                            />
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
            )}

            {/* Sub-page content */}
            {currentSubPage === 'overview' && (
              <div className="space-y-5">
                {selectedApiBeat ? (
                  <div className="max-w-[900px] mx-auto space-y-5">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedApiBeat(null);
                        if (campaignIdForDetail && navigateToPath) {
                          navigateToPath(
                            getPathForPage('see-campaigns', {
                              campaignId: campaignIdForDetail,
                              subTab: 'overview',
                            })
                          );
                        }
                      }}
                      className="inline-flex items-center gap-2 text-[13px] text-[#667085] hover:text-[#101828] transition-colors"
                      style={{ fontWeight: 600 }}
                    >
                      <ArrowLeft className="w-4 h-4 shrink-0" aria-hidden />
                      {t('campaigns.beatBrief.backToBeats')}
                    </button>

                    {beatDetailLoading && (
                      <div className="flex items-center justify-center py-16" role="status" aria-live="polite">
                        <Loader2 className="w-8 h-8 text-[#be43ff] animate-spin" aria-hidden />
                        <span className="sr-only">{t('campaigns.beatBrief.loadingBeatBriefSr')}</span>
                      </div>
                    )}

                    {beatDetailError && (
                      <div className="p-4 rounded-xl bg-red-50 border border-red-200 flex items-center gap-2 text-red-800 text-sm" role="alert">
                        <AlertCircle className="w-4 h-4 shrink-0" aria-hidden />
                        <span>{t('campaigns.beatBrief.loadBeatBriefError')}</span>
                      </div>
                    )}

                    {!beatDetailLoading && !beatDetailError && beatDetail && (
                      <>
                        <div className="bg-white rounded-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-8">
                          <div className="flex items-start justify-between mb-6">
                            <div className="flex items-center gap-3">
                              <span className="w-3 h-3 rounded-full bg-emerald-500 shrink-0" />
                              <h1 className="text-[24px] text-[#101828]" style={{ fontWeight: 700 }}>{beatDetail.beat_name || selectedApiBeat.name}</h1>
                            </div>
                            {(beatDetail.platform || selectedApiBeat.platform) && (
                              <span className="text-[12px] text-[#98a2b3] bg-[#f8f9fa] px-3 py-1.5 rounded-lg shrink-0" style={{ fontWeight: 600 }}>{beatDetail.platform || selectedApiBeat.platform}</span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-start gap-6 mb-6">
                            <div>
                              <span className="text-[10px] text-[#98a2b3] block mb-0.5" style={{ fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>Duration</span>
                              <span className="text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>{selectedApiBeat.dates}</span>
                            </div>
                            <div>
                              <span className="text-[10px] text-[#98a2b3] block mb-0.5" style={{ fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>Status</span>
                              <span className="text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>{beatDetail.status || selectedApiBeat.status}</span>
                            </div>
                            {(() => {
                              const raw = beatDetail.product_delivery;
                              const pd = typeof raw === 'string' && raw.trim() ? raw.trim().toUpperCase() : '';
                              if (!pd || pd === 'NONE') return null;
                              const deliveryKey = `campaigns.beatBrief.productDelivery.${pd}`;
                              const deliveryLabel = t(deliveryKey);
                              const deliveryText =
                                deliveryLabel === deliveryKey ? pd.replace(/_/g, ' ') : deliveryLabel;
                              return (
                                <div key="product-delivery">
                                  <span className="text-[10px] text-[#98a2b3] block mb-0.5" style={{ fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>
                                    {t('campaigns.beatBrief.productDeliveryLabel')}
                                  </span>
                                  <span className="text-[13px] text-[#101828] uppercase" style={{ fontWeight: 600 }}>{deliveryText}</span>
                                </div>
                              );
                            })()}
                          </div>
                          {(() => {
                            const entries = listClientBeatProductEntries(beatDetail.products);
                            if (entries.length === 0) return null;
                            return (
                              <div className="flex flex-wrap gap-3 mb-2">
                                {entries.map(({ key, count }) => (
                                  <div
                                    key={key}
                                    className="min-w-[100px] rounded-xl border border-gray-200 bg-[#fafafa] px-4 py-3"
                                  >
                                    <div className="text-[10px] text-[#98a2b3] uppercase mb-1" style={{ fontWeight: 700, letterSpacing: '0.06em' }}>
                                      {beatBriefProductLabel(t, key)}
                                    </div>
                                    <div className="text-[20px] text-[#101828] tabular-nums" style={{ fontWeight: 700 }}>{count}</div>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </div>

                        <div className="bg-white rounded-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-8">
                          <h2 className="text-[18px] text-[#101828] mb-5" style={{ fontWeight: 700 }}>{t('contentReview.columns.brief')}</h2>
                          {(() => {
                            const brief = beatDetail.brief?.description || beatDetail.campaign_brief?.description;
                            const files = beatDetail.brief?.files?.length ? beatDetail.brief.files : beatDetail.campaign_brief?.files;
                            if (!brief && (!files || files.length === 0)) {
                              return <p className="text-[13px] text-[#667085] italic">{t('campaigns.beatBrief.noBriefForBeat')}</p>;
                            }
                            return (
                              <>
                                {brief && (
                                  <>
                                    <style>{`
                              .brief-html-content p { margin-bottom: 1.5rem; }
                              .brief-html-content p:last-child { margin-bottom: 0; }
                              .brief-html-content a { color: #2563eb !important; text-decoration: underline !important; font-weight: 500; cursor: pointer; word-break: break-all; }
                              .brief-html-content a:hover { opacity: 0.9; }
                              .brief-html-content ul { list-style-type: disc !important; padding-left: 1.5rem !important; margin-bottom: 1.25rem; }
                              .brief-html-content ol { list-style-type: decimal !important; padding-left: 1.5rem !important; margin-bottom: 1.25rem; }
                              .brief-html-content li { display: list-item !important; margin-bottom: 0.375rem; }
                              .brief-html-content strong { font-weight: 700; }
                              .brief-html-content h2 { font-size: 15px; font-weight: 700; margin-top: 1.5rem; margin-bottom: 0.5rem; }
                              .brief-html-content h2:first-child { margin-top: 0; }
                              .brief-html-content h3 { font-size: 14px; font-weight: 700; margin-top: 1rem; margin-bottom: 0.5rem; }
                            `}</style>
                                    <div
                                      className="brief-html-content text-[13px] text-[#344054] leading-relaxed mb-6"
                                      style={{ fontWeight: 500 }}
                                      dangerouslySetInnerHTML={{ __html: brief }}
                                    />
                                  </>
                                )}
                                {files && files.length > 0 && (
                                  <div className="mt-4">
                                    <h3 className="text-[14px] text-[#101828] mb-2" style={{ fontWeight: 700 }}>{t('campaigns.beatBrief.attachmentsHeading')}</h3>
                                    <ul className="space-y-2">
                                      {files.map((f, i) => (
                                        <li key={i}>
                                          <a href={f.url} target="_blank" rel="noreferrer noopener" className="text-[13px] text-[#be43ff] hover:underline inline-flex items-center gap-1.5">
                                            <FileText className="w-3.5 h-3.5" />
                                            {f.filename}
                                          </a>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>

                        <div className="h-8" />
                      </>
                    )}
                  </div>
                ) : (
                <>
                {/* Beats Card — from API (campaignDetail.beats); no mock */}
                <div className="bg-white rounded-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-7">
                  <h2 className="text-[16px] text-[#101828] mb-5" style={{ fontWeight: 700 }}>Beats</h2>
                  <div className="space-y-2.5">
                    {campaignDetailLoading && campaignIdForDetail ? (
                      <p className="text-[13px] text-[#667085] py-4">Loading beats...</p>
                    ) : beatsForList.length === 0 ? (
                      <p className="text-[13px] text-[#667085] py-4">No beats in this campaign.</p>
                    ) : (
                      beatsForList.map((row) => {
                        const beatPill = beatClientStatusPill(row.status);
                        const isSocialIcon =
                          row.icon === 'instagram' || row.icon === 'tiktok' || row.icon === 'youtube';
                        const BeatIcon = row.icon === 'content' ? Camera
                          : row.icon === 'whitelisting' ? ExternalLink
                          : Sparkles;
                        const iconColor = row.icon === 'content' ? 'text-[#be43ff] bg-[#be43ff]/8'
                          : row.icon === 'whitelisting' ? 'text-blue-600 bg-blue-50'
                          : 'text-amber-600 bg-amber-50';
                        return (
                          <div
                            key={row.id}
                            className="flex items-center justify-between bg-[#f8f9fa] rounded-xl p-3.5 hover:bg-gray-100/70 transition-colors cursor-pointer group"
                            onClick={() => {
                              setSelectedApiBeat(row);
                              if (campaignIdForDetail && navigateToPath) {
                                navigateToPath(getPathForPage('see-campaigns', { campaignId: campaignIdForDetail, subTab: currentSubPage, beatId: String(row.id) }));
                              }
                            }}
                          >
                            <div className="flex items-center gap-3.5">
                              <div
                                className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 shadow-[0_1px_2px_rgba(0,0,0,0.04)] group-hover:shadow-[0_2px_4px_rgba(0,0,0,0.06)] transition-shadow ${
                                  isSocialIcon ? 'bg-white border border-gray-100' : iconColor
                                }`}
                              >
                                {row.icon === 'instagram' || row.icon === 'tiktok' || row.icon === 'youtube' ? (
                                  <PlatformIcon platform={row.icon} size="md" />
                                ) : (
                                  <BeatIcon className="w-4 h-4" />
                                )}
                              </div>
                              <div>
                                <div className="text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>{row.name}</div>
                                <div className="text-[10px] text-[#667085] whitespace-pre-line mt-0.5">{row.dates}</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span
                                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 ${beatPill.pillBg}`}
                              >
                                <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${beatPill.dot}`} />
                                <span className={`text-[10px] ${beatPill.text}`} style={{ fontWeight: 600 }}>
                                  {beatPill.label}
                                </span>
                              </span>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
                </>
                )}

              </div>
            )}

            {/* ═══════════════════════════════════════════════ */}
            {/*  PROFILES SUB-PAGE                             */}
            {/* ═══════════════════════════════════════════════ */}
            {currentSubPage === 'profiles' && (() => {
              const pendingCount = suggestionsForList.filter(s => s.status === 'pending').length;
              const approvedCount = suggestionsForList.filter(s => s.status === 'approved').length;
              const declinedCount = suggestionsForList.filter(s => s.status === 'declined').length;
              const filtered = suggestionsForList.filter(s => s.status === suggestionsFilter);

              if (curationError) {
                return (
                  <div className="space-y-5" role="alert">
                    <div className="p-4 rounded-xl bg-red-50 border border-red-200 flex items-center gap-2 text-red-800 text-sm">
                      <AlertCircle className="w-4 h-4 shrink-0" aria-hidden />
                      <span>Failed to load profiles. Please try again.</span>
                    </div>
                  </div>
                );
              }
              if (curationLoading && !curationData?.length) {
                return (
                  <div className="flex items-center justify-center py-16" role="status" aria-live="polite">
                    <Loader2 className="w-8 h-8 text-[#be43ff] animate-spin" aria-hidden />
                    <span className="sr-only">Loading profiles</span>
                  </div>
                );
              }
              if (!curationLoading && !curationError && suggestionsForList.length === 0) {
                return (
                  <div className="rounded-[24px] bg-white px-4 py-10 text-center shadow-[0_1px_3px_rgba(0,0,0,0.04)] md:px-8 md:py-12" aria-label="No profiles">
                    <p className="text-sm text-[#667085]" style={{ fontWeight: 500 }}>No profiles in this campaign yet.</p>
                  </div>
                );
              }

              return (
                <div className="space-y-5">
                  {/* Suggestions Title Card */}
                  <div className="rounded-[24px] bg-white px-4 py-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] md:px-8 md:py-6">
                    <h2 className="mb-4 text-[22px] text-[#101828]" style={{ fontWeight: 700 }}>Profiles</h2>
                    {/* Filter tabs */}
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div className="flex flex-wrap items-center gap-2">
                        {([
                          { key: 'pending' as const, label: 'Pending approval', count: pendingCount, color: 'bg-amber-500' },
                          { key: 'approved' as const, label: 'Approved', count: approvedCount, color: 'bg-[#a9f600]' },
                          { key: 'declined' as const, label: 'Declined', count: declinedCount, color: 'bg-red-400' },
                        ]).map(tab => (
                          <button
                            key={tab.key}
                            onClick={() => setSuggestionsFilter(tab.key)}
                            className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-[12px] transition-colors ${
                              suggestionsFilter === tab.key
                                ? 'bg-[#101828] text-white'
                                : 'text-[#667085] hover:bg-[#f8f9fa]'
                            }`}
                            style={{ fontWeight: 600 }}
                          >
                            {tab.label}
                            <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] ${
                              suggestionsFilter === tab.key ? 'bg-white/20 text-white' : 'bg-[#f8f9fa] text-[#667085]'
                            }`} style={{ fontWeight: 700 }}>{tab.count}</span>
                          </button>
                        ))}
                      </div>
                      <span className="shrink-0 text-[12px] text-[#98a2b3] md:text-right" style={{ fontWeight: 500 }}>{filtered.length} creator{filtered.length !== 1 ? 's' : ''}</span>
                    </div>
                  </div>

                  {/* Creator Cards */}
                  <div className="space-y-3">
                    {filtered.map(creator => (
                      <div
                        key={creator.id}
                        className="group cursor-pointer overflow-hidden rounded-[20px] border border-gray-100 bg-white transition-all hover:shadow-[0_4px_16px_rgba(0,0,0,0.06)]"
                      >
                        <div className="flex flex-col items-stretch gap-4 px-4 py-4 md:flex-row md:gap-5 md:px-6 md:py-5">
                          {/* Avatar */}
                          <div className="flex-shrink-0 flex items-start pt-0.5">
                            <div
                              className="flex h-20 w-20 cursor-pointer items-center justify-center overflow-hidden rounded-full border-2 border-gray-100 bg-gray-200 text-xl text-[#667085] transition-colors group-hover:border-[#be43ff]/40"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (creator.influencerId) {
                                  setProfileSidebarInfluencerId(creator.influencerId);
                                  setProfileSidebarOpen(true);
                                }
                              }}
                            >
                              {creator.avatar ? (
                                <img src={creator.avatar} alt={creator.name} className="w-full h-full object-cover" />
                              ) : (
                                <span style={{ fontWeight: 600 }}>{(creator.name || '?').charAt(0).toUpperCase()}</span>
                              )}
                            </div>
                          </div>

                          {/* Middle — Details */}
                          <div className="flex-1 min-w-0 flex flex-col justify-between py-1">
                            <div>
                              {/* Name row */}
                              <div className="flex items-start justify-between mb-1">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-0.5">
                                    <span
                                      className="text-[15px] text-[#101828] group-hover:text-[#be43ff] transition-colors cursor-pointer hover:underline"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (creator.influencerId) {
                                          setProfileSidebarInfluencerId(creator.influencerId);
                                          setProfileSidebarOpen(true);
                                        }
                                      }}
                                      style={{ fontWeight: 600 }}
                                    >{creator.name}</span>
                                    <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                                      <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                                    </div>
                                  </div>
                                  <p className="text-[12px] text-[#be43ff]" style={{ fontWeight: 500 }}>@{creator.handle}</p>
                                </div>
                              </div>

                              {/* Location + category + platform (hide placeholders so we don't show "– · – · INSTAGRAM") */}
                              <div className="flex items-center gap-2 mt-1.5 mb-0 flex-wrap pb-2">
                                {creator.location && creator.location !== '–' && (
                                  <>
                                    <div className="flex items-center gap-1 text-[11px] text-[#667085]">
                                      <MapPin className="w-3 h-3" />
                                      <span>{creator.location}</span>
                                    </div>
                                    <span className="text-gray-200">·</span>
                                  </>
                                )}
                                {creator.category && creator.category !== '–' && (
                                  <>
                                    <span className="px-2 py-0.5 bg-[#be43ff]/8 text-[#be43ff] rounded-full text-[10px]" style={{ fontWeight: 600 }}>{creator.category}</span>
                                    <span className="text-gray-200">·</span>
                                  </>
                                )}
                                <div className="flex items-center gap-1 px-2 py-0.5 bg-[#f8f9fa] rounded-md text-[#667085]">
                                  <PlatformIcon platform={normalizePlatform(creator.platform) ?? 'instagram'} size="sm" />
                                  <span className="text-[10px]" style={{ fontWeight: 500 }}>{creator.platform}</span>
                                </div>
                              </div>

                              <div className="flex items-center gap-5 mt-3 mb-0">
                                <div className="text-[12px]">
                                  <span className="text-[#101828]" style={{ fontWeight: 700 }}>{creator.followers}</span>
                                  <span className="text-[#98a2b3] ml-1" style={{ fontWeight: 500 }}>Followers</span>
                                </div>
                                <div className="text-[12px]">
                                  <span className="text-[#101828]" style={{ fontWeight: 700 }}>{creator.engRate}</span>
                                  <span className="text-[#98a2b3] ml-1" style={{ fontWeight: 500 }}>Engagement</span>
                                </div>
                              </div>

                              {creator.status === 'approved' && (
                                <ProfileDeliveryAddressRow
                                  collaboration={deliveryCollaborationByCurationId.get(creator.id)}
                                />
                              )}

                              {creator.notes && (
                                <div
                                  className="relative z-10 mt-4 mb-4 cursor-text rounded-lg border border-[#be43ff]/15 bg-[#be43ff]/5 px-3.5 py-3.5 text-left shadow-sm"
                                  role="region"
                                  aria-label={t('campaigns.profiles.notesLabel')}
                                  onClick={(e) => {
                                    if ((e.target as HTMLElement).closest('a')) return;
                                    e.stopPropagation();
                                  }}
                                  onPointerDown={(e) => {
                                    if ((e.target as HTMLElement).closest('a')) return;
                                    e.stopPropagation();
                                  }}
                                >
                                  <span
                                    className="block text-[13px] font-extrabold tracking-tight opacity-100"
                                    style={{ color: '#101828' }}
                                  >
                                    {t('campaigns.profiles.notesLabel')}
                                  </span>
                                  {(() => {
                                    const notePlan = planRichNoteParts(creator.notes);
                                    if (notePlan.mode === 'html') {
                                      return (
                                        <div
                                          className="curation-notes-html mt-2 text-[12px] leading-snug text-[#0f172a] [&_a]:cursor-pointer [&_a]:break-words [&_a]:rounded [&_a]:font-semibold [&_a]:text-blue-700 [&_a]:underline [&_a]:decoration-blue-600 [&_a]:decoration-2 [&_a]:underline-offset-[3px] [&_a:hover]:bg-blue-100/90 [&_a:hover]:text-blue-800 [&_p]:my-1 [&_ul]:my-1 [&_li]:my-0"
                                          dangerouslySetInnerHTML={{ __html: notePlan.html }}
                                        />
                                      );
                                    }
                                    return (
                                      <div className="mt-2 whitespace-pre-wrap text-[12px] leading-snug text-[#0f172a]">
                                        {linkifyPlainTextParts(notePlan.text).map((part, i) =>
                                          part.type === 'text' ? (
                                            <span key={i}>{part.value}</span>
                                          ) : (
                                            <a
                                              key={i}
                                              href={part.href}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="relative z-20 inline cursor-pointer break-all rounded px-0.5 text-[12px] font-semibold text-blue-700 underline decoration-blue-600 decoration-2 underline-offset-[3px] outline-offset-2 hover:bg-blue-100/90 hover:text-blue-800 focus-visible:ring-2 focus-visible:ring-blue-500/40"
                                            >
                                              {part.value}
                                            </a>
                                          )
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>

                            {/* Beat + Actions (followers / engagement shown above Notes) */}
                            <div className="flex flex-col gap-2.5 mt-3">
                              <div className="flex items-center justify-between">
                                {creator.beatId ? (
                                  <button
                                    type="button"
                                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#be43ff]/8 px-2.5 py-1 text-[10px] text-[#be43ff] transition-colors hover:bg-[#be43ff]/15"
                                    style={{ fontWeight: 600 }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openBeatBriefFromReview(creator.beatId);
                                    }}
                                    aria-label={t('campaigns.profiles.openBeatBriefAria', { beat: creator.beat })}
                                  >
                                    <Camera className="w-3 h-3 shrink-0" aria-hidden />
                                    {creator.beat}
                                  </button>
                                ) : (
                                  <span
                                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#be43ff]/8 px-2.5 py-1 text-[10px] text-[#be43ff]"
                                    style={{ fontWeight: 600 }}
                                  >
                                    <Camera className="w-3 h-3 shrink-0" aria-hidden />
                                    {creator.beat}
                                  </span>
                                )}
                                {/* Action Buttons */}
                                <div className="flex items-center gap-2.5">
                                {creator.status === 'pending' && (
                                  <>
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        const token = getTokenFromStorage();
                                        if (!token) return;
                                        setPendingCurationId(creator.id);
                                        try {
                                          await acceptCuratedInfluencer(creator.id, token);
                                          await refetchCuration();
                                        } finally {
                                          setPendingCurationId(null);
                                        }
                                      }}
                                      disabled={pendingCurationId === creator.id}
                                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#a9f600] text-[#1a2e00] text-[11px] hover:bg-[#98e000] transition-colors shadow-[0_1px_3px_rgba(169,246,0,0.3)] disabled:opacity-60 disabled:cursor-not-allowed"
                                      style={{ fontWeight: 700 }}
                                    >
                                      {pendingCurationId === creator.id ? (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2.5} />
                                      ) : (
                                        <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                                      )}
                                      {t('campaigns.profiles.acceptProfile')}
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setDeclineProfileReason('');
                                        setDeclineProfileModal({ id: creator.id, name: creator.name, handle: creator.handle });
                                      }}
                                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-white border border-gray-200 text-[#667085] text-[11px] hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors"
                                      style={{ fontWeight: 600 }}
                                    >
                                      <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                                      Decline
                                    </button>
                                  </>
                                )}
                                {creator.status === 'approved' && (
                                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#a9f600]/15 text-[11px] text-[#4a7a00]" style={{ fontWeight: 600 }}>
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                    Approved
                                  </span>
                                )}
                                {creator.status === 'declined' && (
                                  <div className="flex flex-wrap items-center justify-end gap-2.5">
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-50 text-[11px] text-red-500" style={{ fontWeight: 600 }}>
                                      <X className="w-3.5 h-3.5" />
                                      Declined
                                    </span>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setConfirmApproveDeclinedModal({
                                          id: creator.id,
                                          name: creator.name,
                                          handle: creator.handle,
                                        });
                                      }}
                                      disabled={pendingCurationId === creator.id}
                                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#a9f600] text-[#1a2e00] text-[11px] hover:bg-[#98e000] transition-colors shadow-[0_1px_3px_rgba(169,246,0,0.3)] disabled:opacity-60 disabled:cursor-not-allowed"
                                      style={{ fontWeight: 700 }}
                                    >
                                      {pendingCurationId === creator.id ? (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2.5} />
                                      ) : (
                                        <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                                      )}
                                      {t('campaigns.profiles.acceptProfile')}
                                    </button>
                                  </div>
                                )}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Right Side — fixed rail width (5×120 + 4×gap) so every row keeps the same layout; tiles justify-end when fewer than 5 */}
                          <div className="flex h-[208px] w-[632px] shrink-0 flex-col justify-center">
                            <div
                              className="relative flex h-[208px] w-full flex-nowrap justify-end gap-2 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                              style={{ minHeight: 208 }}
                              aria-busy={
                                Boolean(
                                  creator.influencerId && suggestionContentLoadingIds.has(creator.influencerId)
                                )
                              }
                              aria-label={
                                creator.influencerId && suggestionContentLoadingIds.has(creator.influencerId)
                                  ? t('campaigns.profiles.suggestionContentStripLoading')
                                  : undefined
                              }
                            >
                              {(() => {
                                const token = getTokenFromStorage();
                                const cid = creator.influencerId;
                                const items = cid ? suggestionCreatorContent.get(cid)?.slice(0, 5) ?? [] : [];
                                const loadingStrip = Boolean(cid && suggestionContentLoadingIds.has(cid));
                                const showLoadingCells = loadingStrip && items.length === 0;
                                if (showLoadingCells) {
                                  return Array.from({ length: 5 }, (_, i) => (
                                    <div
                                      key={`ld-${cid}-${i}`}
                                      className="relative flex h-[208px] w-[120px] shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-gray-50/90"
                                      aria-hidden
                                    >
                                      <Loader2
                                        className="h-7 w-7 shrink-0 animate-spin text-[#be43ff]"
                                        strokeWidth={2}
                                        aria-hidden
                                      />
                                    </div>
                                  ));
                                }
                                if (!loadingStrip && items.length === 0) {
                                  return (
                                    <div
                                      className="flex h-[208px] w-full shrink-0 items-center justify-center rounded-xl border border-dashed border-gray-200 px-3 text-center text-[11px] leading-snug text-gray-500"
                                      role="status"
                                    >
                                      {t('campaigns.profiles.noRecentContent')}
                                    </div>
                                  );
                                }
                                return items.map((item, index) => (
                                  <SuggestionContentStripThumb
                                    key={`${cid}-${index}-${item.permalink ?? item.image}`}
                                    item={item}
                                    token={token}
                                    onOpen={() => {
                                      setPublishedPostPlayer(null);
                                      const hero = resolveLegacyHeroFromCreatorContent(item);
                                      const strip = items;
                                      const multiKind =
                                        strip.length > 1
                                          ? hero.contentKind === 'video' || hero.mediaType === 'video'
                                            ? 'video'
                                            : 'carousel'
                                          : hero.contentKind;
                                      setLegacyContentPlayer({
                                        creatorName: creator.name,
                                        creatorAvatar: creator.avatar,
                                        contentTitle: `${item.title || t('campaigns.profiles.contentFallbackTitle')} by @${creator.handle}`,
                                        allContent: strip.map((x) => x.image),
                                        structuredItems: strip,
                                        currentIndex: index,
                                        creatorHandle: creator.handle,
                                        platform: normalizePlatform(creator.platform),
                                        permalink: hero.permalink ?? item.permalink ?? undefined,
                                        ...hero,
                                        contentKind: multiKind ?? hero.contentKind,
                                      });
                                    }}
                                  />
                                ));
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* ═══════════════════════════════════════════════ */}
            {/*  DECLINE FEEDBACK MODAL                        */}
            {/* ═══════════════════════════════════════════════ */}
            <AnimatePresence>
              {draftDeclineModal && (() => {
                const reviewElements = [
                  { key: 'image_video', label: 'Image / Video', icon: <Image className="w-4 h-4" />, desc: 'Visual content needs changes' },
                  { key: 'caption', label: 'Caption', icon: <Type className="w-4 h-4" />, desc: 'Caption text needs revision' },
                  { key: 'hashtags', label: 'Hashtags / Mentions', icon: <Hash className="w-4 h-4" />, desc: 'Missing or incorrect tags' },
                  { key: 'brand_link', label: 'Brand Link / CTA', icon: <Link className="w-4 h-4" />, desc: 'Link or call-to-action issue' },
                  { key: 'timing', label: 'Timing / Schedule', icon: <Clock className="w-4 h-4" />, desc: 'Publication date needs adjustment' },
                  { key: 'compliance', label: 'Compliance / Disclosure', icon: <AlertCircle className="w-4 h-4" />, desc: 'Missing #ad or legal disclosure' },
                  { key: 'other', label: 'Other', icon: <MoreVertical className="w-4 h-4" />, desc: 'Something else needs attention' },
                ];
                const toggleCheck = (key: string) => {
                  setDraftDeclineChecked(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
                };
                return (
                  <>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
                      onClick={() => setDraftDeclineModal(null)}
                    />
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: 10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: 10 }}
                      transition={{ duration: 0.2 }}
                      className="fixed inset-0 z-50 flex items-center justify-center p-4"
                      onClick={() => setDraftDeclineModal(null)}
                    >
                      <div
                        className="bg-white rounded-[24px] shadow-2xl w-full max-w-[520px] max-h-[85vh] overflow-hidden flex flex-col"
                        onClick={e => e.stopPropagation()}
                      >
                        {/* Header */}
                        <div className="px-7 pt-7 pb-4 border-b border-gray-100">
                          <div className="flex items-center justify-between mb-1">
                            <h3 className="text-[18px] text-[#101828]" style={{ fontWeight: 700 }}>Decline Draft</h3>
                            <button onClick={() => setDraftDeclineModal(null)} className="w-8 h-8 rounded-xl flex items-center justify-center hover:bg-[#f8f9fa] transition-colors">
                              <X className="w-4 h-4 text-[#667085]" />
                            </button>
                          </div>
                          <p className="text-[12px] text-[#667085]" style={{ fontWeight: 500 }}>
                            <span className="text-[#344054]" style={{ fontWeight: 600 }}>@{draftDeclineModal.creator}</span> · {draftDeclineModal.contentType}
                          </p>
                        </div>

                        {/* Body — scrollable */}
                        <div className="flex-1 overflow-y-auto px-7 py-5">
                          {/* Review element checkboxes */}
                          <p className="text-[12px] text-[#344054] mb-3" style={{ fontWeight: 600 }}>What needs to be revised?</p>
                          <div className="grid grid-cols-2 gap-2 mb-6">
                            {reviewElements.map(el => {
                              const isChecked = draftDeclineChecked.includes(el.key);
                              return (
                                <button
                                  key={el.key}
                                  onClick={() => toggleCheck(el.key)}
                                  className={`flex items-start gap-3 p-3 rounded-2xl border-2 text-left transition-all ${
                                    isChecked
                                      ? 'border-[#be43ff] bg-[#be43ff]/5'
                                      : 'border-gray-100 hover:border-gray-200 bg-white'
                                  }`}
                                >
                                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${
                                    isChecked ? 'bg-[#be43ff]/10 text-[#be43ff]' : 'bg-[#f8f9fa] text-[#98a2b3]'
                                  }`}>
                                    {el.icon}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <span className={`text-[12px] block ${isChecked ? 'text-[#be43ff]' : 'text-[#344054]'}`} style={{ fontWeight: 600 }}>{el.label}</span>
                                    <span className="text-[10px] text-[#98a2b3] block mt-0.5" style={{ fontWeight: 500 }}>{el.desc}</span>
                                  </div>
                                  <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5 transition-colors ${
                                    isChecked ? 'bg-[#be43ff] border-[#be43ff]' : 'border-gray-300'
                                  }`}>
                                    {isChecked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                                  </div>
                                </button>
                              );
                            })}
                          </div>

                          {/* Feedback textarea */}
                          <p className="text-[12px] text-[#344054] mb-2" style={{ fontWeight: 600 }}>Feedback for the creator</p>
                          <textarea
                            value={draftDeclineFeedback}
                            onChange={e => setDraftDeclineFeedback(e.target.value)}
                            placeholder="Explain what needs to change and how to improve it..."
                            className="w-full rounded-2xl border border-gray-200 bg-[#f8f9fa] px-4 py-3 text-[12px] text-[#344054] placeholder:text-[#98a2b3] resize-none focus:outline-none focus:ring-2 focus:ring-[#be43ff]/20 focus:border-[#be43ff]/40 transition-all"
                            style={{ fontWeight: 500 }}
                            rows={4}
                          />
                        </div>

                        {/* Footer */}
                        <div className="px-7 py-5 border-t border-gray-100 flex items-center justify-between">
                          <button
                            type="button"
                            onClick={() => setDraftDeclineModal(null)}
                            className="px-4 py-2 rounded-xl text-[12px] text-[#667085] hover:bg-[#f8f9fa] transition-colors"
                            style={{ fontWeight: 600 }}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              if (!draftDeclineModal) return;
                              const trimmedNote = draftDeclineFeedback.trim();
                              const labels = draftDeclineChecked.map(k => DECLINE_REVIEW_KEY_LABELS[k] ?? k);
                              const feedback =
                                labels.length && trimmedNote
                                  ? `${labels.join(', ')}\n\n${trimmedNote}`
                                  : labels.length
                                    ? labels.join(', ')
                                    : trimmedNote;
                              if (!feedback) {
                                showError('Add feedback or select what needs revision.');
                                return;
                              }
                              const token = getTokenFromStorage();
                              if (!token) {
                                showError('Not signed in.');
                                return;
                              }
                              setDraftMutationLoading('decline');
                              try {
                                await postClientDraftReject(draftDeclineModal.versionId, token, { feedback });
                                setDraftDeclineModal(null);
                                setDraftDeclineChecked([]);
                                setDraftDeclineFeedback('');
                                await refetchDrafts();
                              } catch (e) {
                                showError(e instanceof Error ? e.message : 'Could not decline draft.');
                              } finally {
                                setDraftMutationLoading(null);
                              }
                            }}
                            disabled={
                              draftMutationLoading === 'decline' ||
                              (draftDeclineChecked.length === 0 && !draftDeclineFeedback.trim())
                            }
                            className={`inline-flex items-center gap-1.5 px-5 py-2 rounded-xl text-[12px] transition-all ${
                              draftDeclineChecked.length > 0 || draftDeclineFeedback.trim()
                                ? 'bg-red-500 text-white hover:bg-red-600 shadow-[0_2px_8px_rgba(239,68,68,0.25)]'
                                : 'bg-gray-100 text-[#98a2b3] cursor-not-allowed'
                            } disabled:opacity-60 disabled:cursor-not-allowed`}
                            style={{ fontWeight: 700 }}
                          >
                            {draftMutationLoading === 'decline' ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
                            ) : (
                              <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                            )}
                            Decline Draft
                            {draftDeclineChecked.length > 0 && (
                              <span className="ml-0.5 bg-white/20 text-white px-1.5 py-0.5 rounded-md text-[10px]" style={{ fontWeight: 700 }}>{draftDeclineChecked.length}</span>
                            )}
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  </>
                );
              })()}
            </AnimatePresence>

            {/* ═══════════════════════════════════════════════ */}
            {/*  ACCEPT CONFIRMATION MODAL                     */}
            {/* ═══════════════════════════════════════════════ */}
            <AnimatePresence>
              {draftAcceptModal && (
                <>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
                    onClick={() => setDraftAcceptModal(null)}
                  />
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    onClick={() => setDraftAcceptModal(null)}
                  >
                    <div
                      className="bg-white rounded-[24px] shadow-2xl w-full max-w-[400px] overflow-hidden"
                      onClick={e => e.stopPropagation()}
                    >
                      <div className="px-7 pt-7 pb-2 text-center">
                        <div className="w-14 h-14 rounded-2xl bg-[#a9f600]/15 flex items-center justify-center mx-auto mb-4">
                          <CheckCircle2 className="w-7 h-7 text-[#4a7a00]" />
                        </div>
                        <h3 className="text-[18px] text-[#101828] mb-1" style={{ fontWeight: 700 }}>Accept this draft?</h3>
                        <p className="text-[12px] text-[#667085]" style={{ fontWeight: 500 }}>
                          <span className="text-[#344054]" style={{ fontWeight: 600 }}>@{draftAcceptModal.creator}</span> · {draftAcceptModal.contentType}
                        </p>
                        <p className="text-[11px] text-[#98a2b3] mt-2" style={{ fontWeight: 500 }}>
                          This will notify the creator that their draft has been approved and is ready for publication.
                        </p>
                      </div>
                      <div className="px-7 py-5 flex items-center justify-center gap-3">
                        <button
                          type="button"
                          onClick={() => draftMutationLoading !== 'accept' && setDraftAcceptModal(null)}
                          disabled={draftMutationLoading === 'accept'}
                          className="px-5 py-2 rounded-xl bg-white border border-gray-200 text-[#667085] text-[12px] hover:bg-[#f8f9fa] transition-colors disabled:opacity-50"
                          style={{ fontWeight: 600 }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!draftAcceptModal) return;
                            const token = getTokenFromStorage();
                            if (!token) {
                              showError('Not signed in.');
                              return;
                            }
                            setDraftMutationLoading('accept');
                            try {
                              await postClientDraftApprove(draftAcceptModal.versionId, token, { feedback: '' });
                              setDraftAcceptModal(null);
                              await refetchDrafts();
                            } catch (e) {
                              showError(e instanceof Error ? e.message : 'Could not approve draft.');
                            } finally {
                              setDraftMutationLoading(null);
                            }
                          }}
                          disabled={draftMutationLoading === 'accept'}
                          className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl bg-[#a9f600] text-[#1a2e00] text-[12px] hover:bg-[#98e000] transition-colors shadow-[0_2px_8px_rgba(169,246,0,0.3)] disabled:opacity-60 disabled:cursor-not-allowed"
                          style={{ fontWeight: 700 }}
                        >
                          {draftMutationLoading === 'accept' ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
                          ) : (
                            <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                          )}
                          Accept Draft
                        </button>
                      </div>
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>

            {/* ═══════════════════════════════════════════════ */}
            {/*  REVIEW CONTENT MODAL (Figma — Accept / Decline)   */}
            {/*  Portaled to body: fixed + z-index above layout/overflow stacking contexts */}
            {/* ═══════════════════════════════════════════════ */}
            {typeof document !== 'undefined' &&
              createPortal(
                <AnimatePresence>
                  {draftReviewModal && (() => {
                const currentDraft = draftReviewModal.allDrafts.find((d) => d.id === draftReviewModal.draftId);
                if (!currentDraft) return null;
                const versionIdx =
                  draftReviewModal.selectedVersionIndex !== undefined
                    ? draftReviewModal.selectedVersionIndex
                    : currentDraft.versions.length - 1;
                const currentVersion = currentDraft.versions[versionIdx];
                if (!currentVersion) return null;
                const isReadOnly =
                  Boolean(draftReviewModal.readOnly) || currentVersion.status !== 'pending';
                const draftIndex = draftReviewModal.allDrafts.findIndex((d) => d.id === draftReviewModal.draftId);
                const hasPrevious = draftIndex > 0;
                const hasNext = draftIndex >= 0 && draftIndex < draftReviewModal.allDrafts.length - 1;
                const versionIdForApi =
                  currentVersion.versionId?.trim() || currentDraft.apiDraftVersionId?.trim() || '';
                const files = buildDraftReviewFileRows(currentVersion, t);
                const captionDisplay = captionPlainTextForReview(currentVersion.caption || '');
                const goToPreviousDraft = () => {
                  if (!hasPrevious) return;
                  const prev = draftReviewModal.allDrafts[draftIndex - 1];
                  setDraftReviewFeedback({ caption: '', files: {} });
                  setDraftReviewToggles({ caption: true, files: {} });
                  setDraftReviewGeneralTopics([]);
                  setDraftReviewGeneralText('');
                  setDraftReviewModal({ ...draftReviewModal, draftId: prev.id, selectedVersionIndex: undefined });
                };

                const goToNextDraft = () => {
                  if (!hasNext) return;
                  const next = draftReviewModal.allDrafts[draftIndex + 1];
                  setDraftReviewFeedback({ caption: '', files: {} });
                  setDraftReviewToggles({ caption: true, files: {} });
                  setDraftReviewGeneralTopics([]);
                  setDraftReviewGeneralText('');
                  setDraftReviewModal({ ...draftReviewModal, draftId: next.id, selectedVersionIndex: undefined });
                };

                /** Outer chevrons: queue navigation between drafts only (not between media files — those are stacked in the modal body). */
                const showOuterLeft = hasPrevious;
                const showOuterRight = hasNext;

                const onOuterLeft = () => {
                  if (hasPrevious) goToPreviousDraft();
                };
                const onOuterRight = () => {
                  if (hasNext) goToNextDraft();
                };

                return (
                  <motion.div
                    key={draftReviewModal.draftId}
                    role="presentation"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 z-[99999] flex min-h-0 items-center justify-center p-3 sm:p-4 md:p-8"
                    style={{ zIndex: 99999 }}
                  >
                    {/* Backdrop + content are siblings (z-0 vs z-10). Avoid pointer-events-none on a full-screen layer — it lets wheel/touch fall through to the dim and scroll does nothing while body is locked. */}
                    <div
                      role="presentation"
                      className="absolute inset-0 z-0 bg-black/60"
                      aria-hidden
                      onClick={closeDraftReviewModal}
                    />
                    <div
                      role="presentation"
                      data-draft-review-modal-panel
                      className="relative z-10 flex min-h-0 min-w-0 w-full max-w-[calc(100vw-1.5rem)] items-stretch justify-center gap-2 sm:gap-3 md:gap-5 lg:gap-6"
                    >
                        {showOuterLeft && (
                          <motion.button
                            type="button"
                            initial={{ opacity: 0, x: 16 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 16 }}
                            transition={{ duration: 0.2 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onOuterLeft();
                            }}
                            className="pointer-events-auto flex h-11 w-11 shrink-0 self-center items-center justify-center rounded-full border border-gray-200 bg-white shadow-2xl transition-colors hover:bg-gray-50 sm:h-12 sm:w-12"
                            aria-label={t('contentReview.draftReviewModal.previousDraftAria')}
                          >
                            <ChevronLeft className="h-5 w-5 text-foreground" />
                          </motion.button>
                        )}
                        <motion.div
                          role="dialog"
                          aria-modal="true"
                          aria-labelledby="draft-review-modal-title"
                          initial={{ opacity: 0, scale: 0.95, y: 20 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95, y: 20 }}
                          transition={{ duration: 0.2 }}
                          className="draft-review-modal-dialog min-h-0 w-full min-w-0 flex-1 overflow-hidden rounded-[32px] border border-gray-200/80 bg-white shadow-2xl"
                        >
                        <header className="shrink-0 bg-white px-6 py-4 border-b border-gray-200">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-start gap-2">
                              <div>
                                <h3 id="draft-review-modal-title" className="text-[19px] text-[#101828]" style={{ fontWeight: 700 }}>
                                  {t('contentReview.draftReviewModal.title')}
                                </h3>
                                <p className="text-[11px] text-[#98a2b3] mt-0.5" style={{ fontWeight: 600 }}>
                                  {t('contentReview.draftReviewModal.draftCounter', {
                                    current: draftIndex + 1,
                                    total: draftReviewModal.allDrafts.length,
                                  })}
                                </p>
                              </div>
                              {isReadOnly && (
                                <span
                                  className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-[10px] text-red-600"
                                  style={{ fontWeight: 600 }}
                                >
                                  {t('contentReview.draftReviewModal.readOnlyBadge')}
                                </span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={closeDraftReviewModal}
                              className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                              aria-label={t('contentReview.draftReviewModal.closeAria')}
                            >
                              <X className="w-4 h-4 text-muted-foreground" />
                            </button>
                          </div>
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                              {/* Light gray pills on white modal shell (Figma: single white surface; pills carry contrast) */}
                              <div className="flex min-w-0 items-center gap-2.5 rounded-2xl border border-gray-200/80 bg-[#f8f9fa] px-3.5 py-2.5">
                                <img
                                  src={currentDraft.avatar}
                                  alt=""
                                  className="h-8 w-8 shrink-0 rounded-full object-cover ring-2 ring-gray-100"
                                />
                                <div className="min-w-0">
                                  <p className="truncate text-[12px] text-[#101828]" style={{ fontWeight: 600 }}>
                                    @{currentDraft.handle}
                                  </p>
                                  <p className="truncate text-[10px] text-[#667085]">{currentDraft.creator}</p>
                                </div>
                              </div>
                              <div className="shrink-0 rounded-2xl border border-gray-200/80 bg-[#f8f9fa] px-3.5 py-2.5">
                                <p
                                  className="mb-0.5 text-[10px] text-[#98a2b3]"
                                  style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}
                                >
                                  {t('contentReview.draftReviewModal.contentLabel')}
                                </p>
                                <p className="text-[12px] text-[#101828]" style={{ fontWeight: 600 }}>
                                  {currentDraft.contentType}
                                </p>
                              </div>
                            </div>
                            {currentDraft.versions.length > 1 && (
                              <div className="flex flex-col items-end gap-1.5 shrink-0">
                                <p
                                  className="text-[10px] text-[#98a2b3]"
                                  style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}
                                >
                                  {t('contentReview.draftReviewModal.versionLabel')}
                                </p>
                                <div className="flex items-center gap-1.5 flex-wrap justify-end">
                                  {currentDraft.versions.map((v, idx) => (
                                    <button
                                      key={v.version}
                                      type="button"
                                      onClick={() => {
                                        setDraftReviewModal({ ...draftReviewModal, selectedVersionIndex: idx });
                                        setDraftReviewFeedback({
                                          caption: v.feedback || '',
                                          files: {},
                                        });
                                        setDraftReviewToggles({
                                          caption: v.status === 'approved',
                                          files: {},
                                        });
                                        setDraftReviewGeneralTopics([]);
                                        setDraftReviewGeneralText('');
                                      }}
                                      className={`px-3 py-1.5 rounded-lg text-[11px] transition-all ${
                                        versionIdx === idx
                                          ? 'bg-[#be43ff] text-white shadow-sm'
                                          : 'bg-[#f8f9fa] text-[#667085] hover:bg-gray-200'
                                      }`}
                                      style={{ fontWeight: 700 }}
                                    >
                                      v{v.version}
                                      {v.status === 'approved' && <Check className="w-3 h-3 inline-block ml-0.5" />}
                                      {v.status === 'declined' && <X className="w-3 h-3 inline-block ml-0.5" />}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </header>

                        <main
                          className="draft-review-modal-main min-h-0 min-w-0 touch-pan-y bg-white px-8 py-5 space-y-6 [scrollbar-gutter:stable]"
                        >
                          <div>
                            <div className="flex items-start gap-6">
                              <div className="min-w-0 flex-1">
                                <h4 className="text-[12px] text-[#344054] mb-2" style={{ fontWeight: 700 }}>
                                  {t('contentReview.draftReviewModal.captionHeading')}
                                </h4>
                                <p className="mb-3 text-[13px] leading-relaxed text-[#667085] break-words whitespace-pre-wrap">
                                  {captionDisplay || t('contentReview.draftReviewModal.noCaption')}
                                </p>
                              </div>
                              <div className="w-64 shrink-0">
                                <label className="text-[11px] text-[#98a2b3] block mb-1.5" style={{ fontWeight: 600 }}>
                                  {t('contentReview.draftReviewModal.feedbackLabel')}
                                </label>
                                <textarea
                                  value={draftReviewFeedback.caption}
                                  onChange={(e) =>
                                    setDraftReviewFeedback((prev) => ({ ...prev, caption: e.target.value }))
                                  }
                                  placeholder={
                                    isReadOnly
                                      ? t('contentReview.draftReviewModal.captionFeedbackReadOnlyPlaceholder')
                                      : t('contentReview.draftReviewModal.captionFeedbackPlaceholder')
                                  }
                                  className={`w-full rounded-xl border border-gray-200 px-3 py-2 text-[13px] text-[#344054] placeholder:text-[#98a2b3] resize-none focus:outline-none focus:ring-2 focus:ring-[#be43ff]/20 focus:border-[#be43ff]/40 transition-all ${
                                    isReadOnly ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'
                                  }`}
                                  rows={3}
                                  disabled={isReadOnly}
                                  readOnly={isReadOnly}
                                />
                                <div className="flex items-center justify-end gap-3 mt-3">
                                  <button
                                    type="button"
                                    onClick={() => !isReadOnly && setDraftReviewToggles((prev) => ({ ...prev, caption: true }))}
                                    disabled={isReadOnly}
                                    className={`px-4 py-2 rounded-xl text-[11px] transition-all ${
                                      draftReviewToggles.caption
                                        ? 'bg-[#a9f600] text-[#1a2e00] shadow-[0_1px_3px_rgba(169,246,0,0.3)]'
                                        : 'bg-[#f8f9fa] text-[#667085] hover:bg-gray-200'
                                    } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    style={{ fontWeight: 700 }}
                                  >
                                    <Check className="w-3.5 h-3.5 inline-block mr-1" strokeWidth={2.5} />
                                    {t('contentReview.draftReviewModal.ok')}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => !isReadOnly && setDraftReviewToggles((prev) => ({ ...prev, caption: false }))}
                                    disabled={isReadOnly}
                                    className={`px-4 py-2 rounded-xl text-[11px] transition-all ${
                                      !draftReviewToggles.caption
                                        ? 'bg-red-500 text-white shadow-[0_1px_3px_rgba(239,68,68,0.3)]'
                                        : 'bg-[#f8f9fa] text-[#667085] hover:bg-gray-200'
                                    } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    style={{ fontWeight: 700 }}
                                  >
                                    <X className="w-3.5 h-3.5 inline-block mr-1" strokeWidth={2.5} />
                                    {t('contentReview.draftReviewModal.notOk')}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>

                          {files.length === 0 ? (
                            <div className="border-t border-gray-100 pt-6">
                              <p className="text-[13px] text-[#98a2b3]" style={{ fontWeight: 500 }}>
                                {t('contentReview.draftReviewModal.noMediaFiles')}
                              </p>
                            </div>
                          ) : (
                            <div className="border-t border-gray-100 pt-6 space-y-4">
                              {files.map((file, fileIdx) => (
                                <div
                                  key={file.key}
                                  className="rounded-2xl border border-gray-200/80 bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
                                >
                                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
                                    <div className="w-full shrink-0 sm:w-64">
                                      <div
                                        className="relative bg-gray-100 rounded-2xl overflow-hidden"
                                        style={{ aspectRatio: '4/5' }}
                                      >
                                        {file.type === 'video' ? (
                                          <video
                                            key={file.url}
                                            src={file.url}
                                            muted
                                            playsInline
                                            preload="metadata"
                                            className="w-full h-full object-cover"
                                          />
                                        ) : (
                                          <img key={file.url} src={file.url} alt="" className="w-full h-full object-cover" />
                                        )}
                                        <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white/95 backdrop-blur-sm rounded-lg border border-gray-200 shadow-sm">
                                          {file.isRaw ? (
                                            <EyeOff className="w-3 h-3 text-[#667085]" />
                                          ) : (
                                            <Eye className="w-3 h-3 text-[#667085]" />
                                          )}
                                          <span className="text-[10px] text-[#344054]" style={{ fontWeight: 600 }}>
                                            {file.isRaw
                                              ? t('contentReview.draftReviewModal.rawBadge')
                                              : t('contentReview.draftReviewModal.finalBadge')}
                                          </span>
                                        </div>
                                        {file.type === 'video' && (
                                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                            <div className="w-12 h-12 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center">
                                              <Play className="w-5 h-5 text-white ml-0.5" />
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                      <div className="mt-2 flex items-center justify-between gap-2">
                                        <p
                                          className="text-[11px] text-[#667085] min-w-0 truncate"
                                          style={{ fontWeight: 600 }}
                                        >
                                          {file.name}
                                        </p>
                                        {files.length > 1 && (
                                          <span
                                            className="shrink-0 text-[10px] text-[#98a2b3]"
                                            style={{ fontWeight: 600 }}
                                            aria-hidden
                                          >
                                            {t('contentReview.draftReviewModal.assetCounter', {
                                              current: fileIdx + 1,
                                              total: files.length,
                                            })}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <label
                                        className="text-[11px] text-[#98a2b3] block mb-1.5"
                                        style={{ fontWeight: 600 }}
                                      >
                                        {t('contentReview.draftReviewModal.feedbackLabel')}
                                      </label>
                                      <textarea
                                        value={draftReviewFeedback.files[file.key] || ''}
                                        onChange={(e) =>
                                          setDraftReviewFeedback((prev) => ({
                                            ...prev,
                                            files: { ...prev.files, [file.key]: e.target.value },
                                          }))
                                        }
                                        placeholder={
                                          isReadOnly
                                            ? t('contentReview.draftReviewModal.captionFeedbackReadOnlyPlaceholder')
                                            : t('contentReview.draftReviewModal.fileFeedbackPlaceholder')
                                        }
                                        className={`w-full rounded-xl border border-gray-200 px-3 py-2 text-[13px] text-[#344054] placeholder:text-[#98a2b3] resize-none focus:outline-none focus:ring-2 focus:ring-[#be43ff]/20 focus:border-[#be43ff]/40 transition-all ${
                                          isReadOnly ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'
                                        }`}
                                        rows={4}
                                        disabled={isReadOnly}
                                        readOnly={isReadOnly}
                                      />
                                      <div className="flex items-center justify-end gap-3 mt-3">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            !isReadOnly &&
                                            setDraftReviewToggles((prev) => ({
                                              ...prev,
                                              files: { ...prev.files, [file.key]: true },
                                            }))
                                          }
                                          disabled={isReadOnly}
                                          className={`px-4 py-2 rounded-xl text-[11px] transition-all ${
                                            draftReviewToggles.files[file.key] !== false
                                              ? 'bg-[#a9f600] text-[#1a2e00] shadow-[0_1px_3px_rgba(169,246,0,0.3)]'
                                              : 'bg-[#f8f9fa] text-[#667085] hover:bg-gray-200'
                                          } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                                          style={{ fontWeight: 700 }}
                                        >
                                          <Check className="w-3.5 h-3.5 inline-block mr-1" strokeWidth={2.5} />
                                          {t('contentReview.draftReviewModal.ok')}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            !isReadOnly &&
                                            setDraftReviewToggles((prev) => ({
                                              ...prev,
                                              files: { ...prev.files, [file.key]: false },
                                            }))
                                          }
                                          disabled={isReadOnly}
                                          className={`px-4 py-2 rounded-xl text-[11px] transition-all ${
                                            draftReviewToggles.files[file.key] === false
                                              ? 'bg-red-500 text-white shadow-[0_1px_3px_rgba(239,68,68,0.3)]'
                                              : 'bg-[#f8f9fa] text-[#667085] hover:bg-gray-200'
                                          } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                                          style={{ fontWeight: 700 }}
                                        >
                                          <X className="w-3.5 h-3.5 inline-block mr-1" strokeWidth={2.5} />
                                          {t('contentReview.draftReviewModal.notOk')}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="border-t border-gray-100 pt-6">
                            <h4 className="text-[12px] text-[#344054] mb-3" style={{ fontWeight: 700 }}>
                              {t('contentReview.draftReviewModal.generalFeedbackHeading')}
                            </h4>
                            <div className="mb-4">
                              <p className="text-[11px] text-[#98a2b3] mb-2" style={{ fontWeight: 600 }}>
                                {t('contentReview.draftReviewModal.topicsOptional')}
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {DRAFT_REVIEW_TOPIC_KEYS.map((topicKey) => {
                                  const isChecked = draftReviewGeneralTopics.includes(topicKey);
                                  return (
                                    <button
                                      key={topicKey}
                                      type="button"
                                      onClick={() => {
                                        if (!isReadOnly) {
                                          setDraftReviewGeneralTopics((prev) =>
                                            isChecked ? prev.filter((k) => k !== topicKey) : [...prev, topicKey],
                                          );
                                        }
                                      }}
                                      disabled={isReadOnly}
                                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] transition-all border ${
                                        isChecked
                                          ? 'bg-[#be43ff]/10 border-[#be43ff]/30 text-[#be43ff]'
                                          : 'bg-[#f8f9fa] border-gray-200 text-[#667085] hover:border-gray-300'
                                      } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                      style={{ fontWeight: 600 }}
                                    >
                                      <span
                                        className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${
                                          isChecked ? 'bg-[#be43ff] border-[#be43ff]' : 'bg-white border-gray-300'
                                        }`}
                                      >
                                        {isChecked && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                                      </span>
                                      {t(`contentReview.draftReviewModal.topics.${topicKey}`)}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                            <div>
                              <label className="text-[13px] text-[#98a2b3] block mb-1.5" style={{ fontWeight: 600 }}>
                                {t('contentReview.draftReviewModal.additionalComments')}
                              </label>
                              <textarea
                                value={draftReviewGeneralText}
                                onChange={(e) => setDraftReviewGeneralText(e.target.value)}
                                placeholder={
                                  isReadOnly
                                    ? t('contentReview.draftReviewModal.additionalCommentsReadOnlyPlaceholder')
                                    : t('contentReview.draftReviewModal.additionalCommentsPlaceholder')
                                }
                                className={`w-full rounded-xl border border-gray-200 px-3 py-2.5 text-[13px] text-[#344054] placeholder:text-[#98a2b3] resize-none focus:outline-none focus:ring-2 focus:ring-[#be43ff]/20 focus:border-[#be43ff]/40 transition-all ${
                                  isReadOnly ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'
                                }`}
                                rows={4}
                                disabled={isReadOnly}
                                readOnly={isReadOnly}
                              />
                            </div>
                          </div>
                        </main>

                        <footer className="shrink-0 bg-white px-8 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
                          <button
                            type="button"
                            onClick={closeDraftReviewModal}
                            className="px-5 py-2.5 rounded-xl bg-white border border-gray-200 text-[#667085] text-[12px] hover:bg-[#f8f9fa] transition-colors"
                            style={{ fontWeight: 600 }}
                          >
                            {isReadOnly ? t('contentReview.draftReviewModal.close') : t('contentReview.draftReviewModal.cancel')}
                          </button>
                          {!isReadOnly && (
                            <>
                              <button
                                type="button"
                                onClick={async () => {
                                  if (!versionIdForApi) {
                                    showError(t('contentReview.draftReviewModal.errors.missingVersionId'));
                                    return;
                                  }
                                  const token = getTokenFromStorage();
                                  if (!token) {
                                    showError(t('contentReview.draftReviewModal.errors.notSignedIn'));
                                    return;
                                  }
                                  setDraftMutationLoading('decline');
                                  try {
                                    const reviewBody = buildClientDraftReviewBody(
                                      t,
                                      files,
                                      draftReviewFeedback,
                                      draftReviewToggles,
                                      draftReviewGeneralTopics,
                                      draftReviewGeneralText,
                                      'decline',
                                    );
                                    await postClientDraftReject(versionIdForApi, token, reviewBody);
                                    closeDraftReviewModal();
                                    await refetchDrafts();
                                  } catch (e) {
                                    showError(
                                      e instanceof Error ? e.message : t('contentReview.draftReviewModal.errors.declineFailed'),
                                    );
                                  } finally {
                                    setDraftMutationLoading(null);
                                  }
                                }}
                                disabled={draftMutationLoading !== null}
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border border-red-200 text-red-600 text-[12px] hover:bg-red-50 transition-colors disabled:opacity-50"
                                style={{ fontWeight: 700 }}
                              >
                                <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                                {t('contentReview.draftReviewModal.decline')}
                              </button>
                              <button
                                type="button"
                                onClick={async () => {
                                  if (!versionIdForApi) {
                                    showError(t('contentReview.draftReviewModal.errors.missingVersionId'));
                                    return;
                                  }
                                  const token = getTokenFromStorage();
                                  if (!token) {
                                    showError(t('contentReview.draftReviewModal.errors.notSignedIn'));
                                    return;
                                  }
                                  setDraftMutationLoading('accept');
                                  try {
                                    if (!draftReviewToggles.caption) {
                                      showError(t('contentReview.draftReviewModal.errors.approveRequiresAllOk'));
                                      return;
                                    }
                                    for (const f of files) {
                                      if (draftReviewToggles.files[f.key] === false) {
                                        showError(t('contentReview.draftReviewModal.errors.approveRequiresAllOk'));
                                        return;
                                      }
                                    }
                                    const reviewBody = buildClientDraftReviewBody(
                                      t,
                                      files,
                                      draftReviewFeedback,
                                      draftReviewToggles,
                                      draftReviewGeneralTopics,
                                      draftReviewGeneralText,
                                      'approve',
                                    );
                                    await postClientDraftApprove(versionIdForApi, token, reviewBody);
                                    closeDraftReviewModal();
                                    await refetchDrafts();
                                  } catch (e) {
                                    showError(
                                      e instanceof Error ? e.message : t('contentReview.draftReviewModal.errors.approveFailed'),
                                    );
                                  } finally {
                                    setDraftMutationLoading(null);
                                  }
                                }}
                                disabled={draftMutationLoading !== null}
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#a9f600] text-[#1a2e00] text-[12px] hover:bg-[#98e000] transition-colors shadow-[0_2px_8px_rgba(169,246,0,0.3)] disabled:opacity-50"
                                style={{ fontWeight: 700 }}
                              >
                                {draftMutationLoading === 'accept' ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
                                ) : (
                                  <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                                )}
                                {t('contentReview.draftReviewModal.accept')}
                              </button>
                            </>
                          )}
                        </footer>
                      </motion.div>
                        {showOuterRight && (
                          <motion.button
                            type="button"
                            initial={{ opacity: 0, x: -16 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -16 }}
                            transition={{ duration: 0.2 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onOuterRight();
                            }}
                            className="pointer-events-auto flex h-11 w-11 shrink-0 self-center items-center justify-center rounded-full border border-gray-200 bg-white shadow-2xl transition-colors hover:bg-gray-50 sm:h-12 sm:w-12"
                            aria-label={t('contentReview.draftReviewModal.nextDraftAria')}
                          >
                            <ChevronRight className="h-5 w-5 text-foreground" />
                          </motion.button>
                        )}
                      </div>
                  </motion.div>
                );
              })()}
                </AnimatePresence>,
                document.body,
              )}

            {/* ═══════════════════════════════════════════════ */}
            {/*  DECLINE PROFILE (CURATION) CONFIRMATION MODAL  */}
            {/* ═══════════════════════════════════════════════ */}
            <AnimatePresence>
              {declineProfileModal && (
                <>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
                    onClick={() => { setDeclineProfileModal(null); setDeclineProfileReason(''); }}
                  />
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    onClick={() => { setDeclineProfileModal(null); setDeclineProfileReason(''); }}
                  >
                    <div
                      className="bg-white rounded-[24px] shadow-2xl w-full max-w-[400px] overflow-hidden"
                      onClick={e => e.stopPropagation()}
                    >
                      <div className="px-7 pt-7 pb-4 border-b border-gray-100">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                              <AlertTriangle className="w-5 h-5 text-red-500" />
                            </div>
                            <h3 className="text-[18px] text-[#101828]" style={{ fontWeight: 700 }}>Decline profile?</h3>
                          </div>
                          <button
                            onClick={() => { setDeclineProfileModal(null); setDeclineProfileReason(''); }}
                            className="w-8 h-8 rounded-xl flex items-center justify-center hover:bg-[#f8f9fa] transition-colors"
                          >
                            <X className="w-4 h-4 text-[#667085]" />
                          </button>
                        </div>
                        <p className="text-[12px] text-[#667085] mt-2" style={{ fontWeight: 500 }}>
                          <span className="text-[#344054]" style={{ fontWeight: 600 }}>{declineProfileModal.name}</span>
                          {declineProfileModal.handle && (
                            <span className="text-[#98a2b3]"> · @{declineProfileModal.handle}</span>
                          )}
                        </p>
                      </div>
                      <div className="px-7 py-4">
                        <label className="block text-[12px] text-[#344054] mb-2" style={{ fontWeight: 600 }}>Reason (optional)</label>
                        <textarea
                          value={declineProfileReason}
                          onChange={e => setDeclineProfileReason(e.target.value)}
                          placeholder="e.g. Not a good fit for this campaign..."
                          className="w-full rounded-2xl border border-gray-200 bg-[#f8f9fa] px-4 py-3 text-[12px] text-[#344054] placeholder:text-[#98a2b3] resize-none focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500/40 transition-all"
                          rows={3}
                        />
                      </div>
                      <div className="px-7 py-5 border-t border-gray-100 flex items-center justify-end gap-3">
                        <button
                          onClick={() => { setDeclineProfileModal(null); setDeclineProfileReason(''); }}
                          className="px-5 py-2 rounded-xl bg-white border border-gray-200 text-[#667085] text-[12px] hover:bg-[#f8f9fa] transition-colors"
                          style={{ fontWeight: 600 }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={async () => {
                            if (!declineProfileModal) return;
                            const token = getTokenFromStorage();
                            if (!token) return;
                            try {
                              await rejectCuratedInfluencer(declineProfileModal.id, token, { reason: declineProfileReason || undefined });
                              await refetchCuration();
                            } finally {
                              setDeclineProfileModal(null);
                              setDeclineProfileReason('');
                            }
                          }}
                          className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl bg-red-500 text-white text-[12px] hover:bg-red-600 transition-colors shadow-[0_2px_8px_rgba(239,68,68,0.25)]"
                          style={{ fontWeight: 700 }}
                        >
                          <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                          Decline
                        </button>
                      </div>
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>

            {/* ═══════════════════════════════════════════════ */}
            {/*  APPROVE PREVIOUSLY DECLINED PROFILE — CONFIRMATION   */}
            {/* ═══════════════════════════════════════════════ */}
            <AnimatePresence>
              {confirmApproveDeclinedModal && (
                <>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
                    onClick={() => setConfirmApproveDeclinedModal(null)}
                  />
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    onClick={() => setConfirmApproveDeclinedModal(null)}
                  >
                    <div
                      className="bg-white rounded-[24px] shadow-2xl w-full max-w-[400px] overflow-hidden"
                      onClick={e => e.stopPropagation()}
                    >
                      <div className="px-7 pt-7 pb-4 border-b border-gray-100">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <div className="w-10 h-10 rounded-xl bg-[#a9f600]/20 flex items-center justify-center">
                              <Check className="w-5 h-5 text-[#3d6600]" strokeWidth={2.5} />
                            </div>
                            <h3 className="text-[18px] text-[#101828]" style={{ fontWeight: 700 }}>
                              {t('campaigns.profiles.approveDeclinedModal.title')}
                            </h3>
                          </div>
                          <button
                            type="button"
                            onClick={() => setConfirmApproveDeclinedModal(null)}
                            className="w-8 h-8 rounded-xl flex items-center justify-center hover:bg-[#f8f9fa] transition-colors"
                          >
                            <X className="w-4 h-4 text-[#667085]" />
                          </button>
                        </div>
                        <p className="text-[12px] text-[#667085] mt-2" style={{ fontWeight: 500 }}>
                          <span className="text-[#344054]" style={{ fontWeight: 600 }}>{confirmApproveDeclinedModal.name}</span>
                          {confirmApproveDeclinedModal.handle && (
                            <span className="text-[#98a2b3]"> · @{confirmApproveDeclinedModal.handle}</span>
                          )}
                        </p>
                        <p className="text-[12px] text-[#667085] mt-3 leading-relaxed" style={{ fontWeight: 500 }}>
                          {t('campaigns.profiles.approveDeclinedModal.description')}
                        </p>
                      </div>
                      <div className="px-7 py-5 border-t border-gray-100 flex items-center justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => setConfirmApproveDeclinedModal(null)}
                          className="px-5 py-2 rounded-xl bg-white border border-gray-200 text-[#667085] text-[12px] hover:bg-[#f8f9fa] transition-colors"
                          style={{ fontWeight: 600 }}
                        >
                          {t('campaigns.profiles.approveDeclinedModal.cancel')}
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!confirmApproveDeclinedModal) return;
                            const token = getTokenFromStorage();
                            if (!token) return;
                            const curationId = confirmApproveDeclinedModal.id;
                            setConfirmApproveDeclinedModal(null);
                            setPendingCurationId(curationId);
                            try {
                              await acceptCuratedInfluencer(curationId, token);
                              await refetchCuration();
                            } catch (err) {
                              showError(
                                err instanceof Error
                                  ? err.message
                                  : t('campaigns.profiles.acceptDeclinedError')
                              );
                            } finally {
                              setPendingCurationId(null);
                            }
                          }}
                          className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl bg-[#a9f600] text-[#1a2e00] text-[12px] hover:bg-[#98e000] transition-colors shadow-[0_2px_8px_rgba(169,246,0,0.3)]"
                          style={{ fontWeight: 700 }}
                        >
                          <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                          {t('campaigns.profiles.approveDeclinedModal.confirm')}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>

            {/* Published post — portaled to body so fixed + z-index sit above layout stacking contexts */}
            {typeof document !== 'undefined' &&
              createPortal(
            <AnimatePresence>
                  {publishedPostPlayer && (() => {
                const activePost = publishedPostPlayer.reviewPosts[publishedPostPlayer.reviewIndex] ?? publishedPostPlayer.reviewPosts[0];
                if (!activePost) return null;
                const collab = publishedPostPlayer.reviewCollaborator;
                const avatarUrl = (collab.avatar ?? '').trim();
                const isVideo = publishedPostPlayer.mediaType === 'video';
                const isEmbed = publishedPostPlayer.contentKind === 'embed' && Boolean(publishedPostPlayer.embedPlayerUrl);
                const isPlaceholder = publishedPostPlayer.contentKind === 'placeholder';
                const metricsFromApi = activePost.metrics_from_api === true;
                const metricPlaceholder = t('contentReview.postModal.datePlaceholder');
                const sharesDisplay = metricsFromApi ? fmtMetricK(activePost.shares) : metricPlaceholder;
                const viewsDisplay =
                  metricsFromApi && activePost.views != null
                    ? fmtMetricK(activePost.views as number)
                    : metricPlaceholder;
                const engagementDisplay = metricsFromApi
                  ? activePost.engagement_total != null && Number.isFinite(activePost.engagement_total)
                    ? fmtMetricK(activePost.engagement_total)
                    : `${activePost.engagementRate.toFixed(1)}%`
                  : metricPlaceholder;
                const likesDisplay = metricsFromApi ? fmtMetricK(activePost.likes) : metricPlaceholder;
                const commentsDisplay = metricsFromApi ? String(activePost.comments) : metricPlaceholder;
                const linkClicksDisplay = metricsFromApi ? fmtMetricK(activePost.linkClicks) : metricPlaceholder;
                const rawPermalink = (activePost.permalink ?? publishedPostPlayer.permalink ?? '').trim();
                const viewHref =
                  viewUrlForPublishedPost(rawPermalink || null) ??
                  viewUrlFromEmbedPlayerSrc(publishedPostPlayer.embedPlayerUrl) ??
                  looseHttpUrlForView(rawPermalink);
                const perm = viewHref ?? '';
                const viewPlatform = resolveContentPlatform(activePost, collab, [
                  perm,
                  rawPermalink,
                  publishedPostPlayer.embedPlayerUrl,
                  publishedPostPlayer.videoUrl,
                  publishedPostPlayer.contentImage,
                ]);
                const modalPostStatus = resolveModalPostStatus(activePost, collab);
                const canOpenPostLink = Boolean(perm);
                const statusPillToneClass =
                  modalPostStatus === 'published'
                    ? publishedPostModalLayout.statusPillPublished
                    : modalPostStatus === 'draft'
                      ? publishedPostModalLayout.statusPillDraft
                      : modalPostStatus === 'canceled'
                        ? publishedPostModalLayout.statusPillCanceled
                        : publishedPostModalLayout.statusPillPending;
                const statusPillKey =
                  modalPostStatus === 'published'
                    ? 'contentReview.postModal.statusPublished'
                    : modalPostStatus === 'draft'
                      ? 'contentReview.postModal.statusDraft'
                      : modalPostStatus === 'canceled'
                        ? 'contentReview.postModal.statusCanceled'
                        : 'contentReview.postModal.statusPending';
                const viewCtaKey =
                  viewPlatform === 'tiktok'
                    ? 'contentReview.postModal.viewOnTiktok'
                    : viewPlatform === 'youtube'
                      ? 'contentReview.postModal.viewOnYoutube'
                      : 'contentReview.postModal.viewOnInstagram';
                const typeIconSm =
                  activePost.type === 'Reel' ? (
                    <Film className="h-2.5 w-2.5 shrink-0 text-white" strokeWidth={2.5} aria-hidden />
                  ) : activePost.type === 'Story' ? (
                    <Camera className="h-2.5 w-2.5 shrink-0 text-white" strokeWidth={2.5} aria-hidden />
                  ) : (
                    <FileText className="h-2.5 w-2.5 shrink-0 text-white" strokeWidth={2.5} aria-hidden />
                  );
                return (
                  <motion.div
                    key="published-post-modal-root"
                    className="fixed inset-0 z-[200] flex min-h-full items-center justify-center bg-black/60 px-[44.5px] py-4 backdrop-blur-xl backdrop-saturate-150 sm:py-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    role="presentation"
                    onClick={() => setPublishedPostPlayer(null)}
                  >
                    <motion.div
                      initial={{ opacity: 0, scale: 0.97, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.98, y: 16 }}
                      transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
                      className={`${publishedPostModalLayout.shell} border border-gray-200/60 bg-white`}
                      onClick={(e) => e.stopPropagation()}
                      role="dialog"
                      aria-modal="true"
                      aria-labelledby="published-post-modal-title"
                    >
                      {/* LEFT: hero media — grid column 1 (50%) */}
                      <div className={publishedPostModalLayout.mediaColumn}>
                        <div className={publishedPostModalLayout.mediaInner}>
                          <div className={publishedPostModalLayout.mediaStage}>
                          {isEmbed && publishedPostPlayer.embedPlayerUrl ? (
                            <iframe
                              src={publishedPostPlayer.embedPlayerUrl}
                              title={t('contentReview.embeddedPostTitle')}
                              className={`${publishedPostModalLayout.mediaLayerInteractive} border-0 bg-black`}
                              sandbox="allow-scripts allow-same-origin allow-popups allow-presentation"
                              referrerPolicy="strict-origin-when-cross-origin"
                            />
                          ) : isPlaceholder ? (
                            <div
                              className={`${publishedPostModalLayout.mediaLayerInteractive} flex flex-col items-center justify-center gap-5 bg-[#d0d5dd] p-8 text-center`}
                            >
                              <p className="max-w-xs text-sm font-medium leading-relaxed text-[#475467]">
                                {t('contentReview.noPreviewThumbnail')}
                              </p>
                            </div>
                          ) : isVideo && publishedPostPlayer.videoUrl ? (
                            <div className={`${publishedPostModalLayout.mediaLayerInteractive} flex min-h-0 flex-col`}>
                              <PublishedPostModalVideoWithOverlay
                                key={publishedPostPlayer.videoUrl}
                                videoUrl={publishedPostPlayer.videoUrl}
                                poster={
                                  publishedPostPlayer.contentImage !== publishedPostPlayer.videoUrl
                                    ? publishedPostPlayer.contentImage
                                    : undefined
                                }
                                playAriaLabel={t('contentReview.postModal.playVideo')}
                              />
                            </div>
                          ) : (
                            <div className={publishedPostModalLayout.mediaLayer}>
                              <div className={publishedPostModalLayout.imageWithFallback}>
                                <ImageWithFallback
                                  src={publishedPostPlayer.contentImage}
                                  alt=""
                                  className={publishedPostModalLayout.imageWithFallbackImg}
                                />
                              </div>
                            </div>
                          )}
                          </div>
                        </div>
                      </div>

                      {/* RIGHT: content panel — vertical stack per Figma (header / scroll / footer) */}
                      <div className={publishedPostModalLayout.rightColumn}>
                        <div className={publishedPostModalLayout.rightHeader}>
                          <div className={publishedPostModalLayout.headerTop}>
                            <div className={publishedPostModalLayout.headerIdentity}>
                              {avatarUrl ? (
                                <img src={avatarUrl} alt="" className={publishedPostModalLayout.avatar} />
                              ) : (
                                <div className={publishedPostModalLayout.avatarFallback} aria-hidden>
                                  {collab.name?.charAt(0)?.toUpperCase() ?? '?'}
                                </div>
                              )}
                              <div className={publishedPostModalLayout.headerIdentityText}>
                                <h2 id="published-post-modal-title" className={publishedPostModalLayout.creatorName}>
                                  {collab.name}
                                </h2>
                                <p className={publishedPostModalLayout.creatorHandle}>@{collab.handle}</p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setPublishedPostPlayer(null)}
                              className={publishedPostModalLayout.headerClose}
                              aria-label={t('contentReview.postModal.close')}
                            >
                              <X className={publishedPostModalLayout.headerCloseIcon} strokeWidth={1.75} />
                            </button>
                          </div>
                          <div className={publishedPostModalLayout.metaRow}>
                            <span
                              className={pubCard.chip}
                              title={activePost.platform ?? collab.platform}
                            >
                              {typeIconSm}
                              <span className={pubCard.chipLabel}>{activePost.type}</span>
                            </span>
                            <span className={`${publishedPostModalLayout.statusPill} ${statusPillToneClass}`}>
                              <span className={publishedPostModalLayout.statusPillLabel}>{t(statusPillKey)}</span>
                            </span>
                            <span className={publishedPostModalLayout.metaDate}>
                              {formatPostModalDate(activePost.date, t('contentReview.postModal.datePlaceholder'))}
                            </span>
                          </div>
                        </div>

                        <div
                          className={publishedPostModalLayout.scrollRegion}
                        >
                          {activePost.platform === 'TikTok' &&
                            typeof activePost.spark_id === 'string' &&
                            activePost.spark_id.trim() !== '' && (
                              <div className={`${publishedPostModalLayout.section} ${publishedPostModalLayout.sectionStackTight}`}>
                                <div className={publishedPostModalLayout.sectionLabelFrame}>
                                  <p className={publishedPostModalLayout.sectionLabel}>
                                    {t('contentReview.postModal.sparkId')}
                                  </p>
                                </div>
                                <div className={publishedPostModalLayout.captionBodyFrame}>
                                  <p className={publishedPostModalLayout.captionBody}>{activePost.spark_id.trim()}</p>
                                </div>
                              </div>
                            )}
                          {activePost.platform === 'Instagram' &&
                            typeof activePost.instagram_ad_id === 'string' &&
                            activePost.instagram_ad_id.trim() !== '' && (
                              <div className={`${publishedPostModalLayout.section} ${publishedPostModalLayout.sectionStackTight}`}>
                                <div className={publishedPostModalLayout.sectionLabelFrame}>
                                  <p className={publishedPostModalLayout.sectionLabel}>
                                    {t('contentReview.postModal.instagramAdId')}
                                  </p>
                                </div>
                                <div className={publishedPostModalLayout.captionBodyFrame}>
                                  <p className={publishedPostModalLayout.captionBody}>{activePost.instagram_ad_id.trim()}</p>
                                </div>
                              </div>
                            )}
                          <div className={`${publishedPostModalLayout.section} ${publishedPostModalLayout.sectionStackTight}`}>
                            <div className={publishedPostModalLayout.sectionLabelFrame}>
                              <p className={publishedPostModalLayout.sectionLabel}>{t('contentReview.postModal.caption')}</p>
                            </div>
                            <div className={publishedPostModalLayout.captionBodyFrame}>
                              <p className={publishedPostModalLayout.captionBody}>
                                {activePost.caption?.trim() ? activePost.caption : metricPlaceholder}
                              </p>
                            </div>
                          </div>

                          <div className={`${publishedPostModalLayout.section} ${publishedPostModalLayout.sectionStack}`}>
                            <div className={publishedPostModalLayout.sectionLabelFrame}>
                              <p
                                id="published-post-modal-performance-heading"
                                className={publishedPostModalLayout.sectionLabel}
                              >
                                {t('contentReview.postModal.performance')}
                              </p>
                            </div>
                            <div
                              className={publishedPostModalLayout.metricGrid}
                              aria-labelledby="published-post-modal-performance-heading"
                            >
                              <div className={publishedPostModalLayout.metricCard}>
                                <div
                                  className={`${publishedPostModalLayout.metricIcon} ${publishedPostModalLayout.metricIconEngagement}`}
                                >
                                  <TrendingUp strokeWidth={2.25} aria-hidden />
                                </div>
                                <div className={publishedPostModalLayout.metricLabelFrame}>
                                  <p className={publishedPostModalLayout.metricLabel}>{t('contentReview.postModal.engagement')}</p>
                                </div>
                                <div className={publishedPostModalLayout.metricValueFrame}>
                                  <p className={publishedPostModalLayout.metricValue}>{engagementDisplay}</p>
                                </div>
                              </div>
                              <div className={publishedPostModalLayout.metricCard}>
                                <div className={`${publishedPostModalLayout.metricIcon} ${publishedPostModalLayout.metricIconShares}`}>
                                  <Share2 strokeWidth={2.25} aria-hidden />
                                </div>
                                <div className={publishedPostModalLayout.metricLabelFrame}>
                                  <p className={publishedPostModalLayout.metricLabel}>{t('contentReview.postModal.shares')}</p>
                                </div>
                                <div className={publishedPostModalLayout.metricValueFrame}>
                                  <p className={publishedPostModalLayout.metricValue}>{sharesDisplay}</p>
                                </div>
                              </div>
                              <div className={publishedPostModalLayout.metricCard}>
                                <div className={`${publishedPostModalLayout.metricIcon} ${publishedPostModalLayout.metricIconLikes}`}>
                                  <Heart strokeWidth={2.25} aria-hidden />
                                </div>
                                <div className={publishedPostModalLayout.metricLabelFrame}>
                                  <p className={publishedPostModalLayout.metricLabel}>{t('contentReview.postModal.likes')}</p>
                                </div>
                                <div className={publishedPostModalLayout.metricValueFrame}>
                                  <p className={publishedPostModalLayout.metricValue}>{likesDisplay}</p>
                                </div>
                              </div>
                              <div className={publishedPostModalLayout.metricCard}>
                                <div className={`${publishedPostModalLayout.metricIcon} ${publishedPostModalLayout.metricIconComments}`}>
                                  <MessageCircle strokeWidth={2.25} aria-hidden />
                                </div>
                                <div className={publishedPostModalLayout.metricLabelFrame}>
                                  <p className={publishedPostModalLayout.metricLabel}>{t('contentReview.postModal.comments')}</p>
                                </div>
                                <div className={publishedPostModalLayout.metricValueFrame}>
                                  <p className={publishedPostModalLayout.metricValue}>{commentsDisplay}</p>
                                </div>
                              </div>
                              <div className={publishedPostModalLayout.metricCard}>
                                <div className={`${publishedPostModalLayout.metricIcon} ${publishedPostModalLayout.metricIconClicks}`}>
                                  <MousePointerClick strokeWidth={2.25} aria-hidden />
                                </div>
                                <div className={publishedPostModalLayout.metricLabelFrame}>
                                  <p className={publishedPostModalLayout.metricLabel}>{t('contentReview.postModal.linkClicks')}</p>
                                </div>
                                <div className={publishedPostModalLayout.metricValueFrame}>
                                  <p className={publishedPostModalLayout.metricValue}>{linkClicksDisplay}</p>
                                </div>
                              </div>
                              <div className={publishedPostModalLayout.metricCard}>
                                <div className={`${publishedPostModalLayout.metricIcon} ${publishedPostModalLayout.metricIconViews}`}>
                                  <Play strokeWidth={2.25} aria-hidden />
                                </div>
                                <div className={publishedPostModalLayout.metricLabelFrame}>
                                  <p className={publishedPostModalLayout.metricLabel}>{t('contentReview.postModal.views')}</p>
                                </div>
                                <div className={publishedPostModalLayout.metricValueFrame}>
                                  <p className={publishedPostModalLayout.metricValue}>{viewsDisplay}</p>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className={`${publishedPostModalLayout.section} ${publishedPostModalLayout.complianceSection}`}>
                            <div className={publishedPostModalLayout.sectionLabelFrame}>
                              <p className={publishedPostModalLayout.sectionLabel}>{t('contentReview.postModal.compliance')}</p>
                            </div>
                            {activePost.compliance_from_api ? (
                              <div className={publishedPostModalLayout.complianceList}>
                                {(
                                  [
                                    {
                                      label: t('contentReview.postModal.brandTagged'),
                                      ok: activePost.compliance.brandTagged,
                                    },
                                    {
                                      label: t('contentReview.postModal.hashtagsUsed'),
                                      ok: activePost.compliance.hashtagUsed,
                                    },
                                    {
                                      label: t('contentReview.postModal.productVisible'),
                                      ok: activePost.compliance.productVisible,
                                    },
                                    {
                                      label: t('contentReview.postModal.adDisclosure'),
                                      ok: activePost.compliance.adDisclosure,
                                    },
                                    {
                                      label: t('contentReview.postModal.linkIncluded'),
                                      ok: activePost.compliance.linkIncluded,
                                    },
                                  ] as const
                                ).map(row => (
                                  <div key={row.label} className={publishedPostModalLayout.complianceRow}>
                                    <span className={publishedPostModalLayout.complianceLabel}>{row.label}</span>
                                    {row.ok === true ? (
                                      <span className={publishedPostModalLayout.complianceOk}>
                                        <Check className="h-3 w-3" strokeWidth={3} aria-hidden />
                                      </span>
                                    ) : (
                                      <span className={publishedPostModalLayout.complianceFail}>{metricPlaceholder}</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className={publishedPostModalLayout.unavailableNotice}>
                                {t('contentReview.postModal.complianceUnavailable')}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className={publishedPostModalLayout.footer}>
                          {canOpenPostLink ? (
                            <a
                              href={perm}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={publishedPostModalLayout.cta}
                              onClick={e => e.stopPropagation()}
                            >
                              <ExternalLink className={publishedPostModalLayout.ctaIcon} aria-hidden />
                              {t(viewCtaKey)}
                            </a>
                          ) : (
                            <button
                              type="button"
                              disabled
                              className={publishedPostModalLayout.cta}
                              title={t('contentReview.postModal.viewPostLinkUnavailable')}
                              onClick={e => e.stopPropagation()}
                            >
                              <ExternalLink className={publishedPostModalLayout.ctaIcon} aria-hidden />
                              {t(viewCtaKey)}
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                );
                  })()}
                </AnimatePresence>,
                document.body,
              )}

            {/* Legacy asset player — drafts + curation thumbnails (right sheet); portaled + layered like Review modal */}
            {typeof document !== 'undefined' &&
              createPortal(
                <AnimatePresence>
                  {legacyContentPlayer && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="fixed inset-0 z-[450] isolate"
                      role="presentation"
                    >
                      <div
                        className="absolute inset-0 z-0 bg-black/40 backdrop-blur-md backdrop-saturate-150"
                        aria-hidden
                        onClick={() => setLegacyContentPlayer(null)}
                      />
                      <motion.div
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'tween', duration: 0.3 }}
                        className="absolute right-0 top-0 bottom-0 z-10 flex w-[min(440px,calc(100vw-1rem))] max-w-full flex-col bg-white shadow-xl"
                      >
                    <div className="border-b border-gray-200 p-4">
                      <div className="flex items-center gap-3">
                        <img src={legacyContentPlayer.creatorAvatar} alt={legacyContentPlayer.creatorName} className="h-12 w-12 rounded-lg object-cover" />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="truncate text-sm text-gray-900">{legacyContentPlayer.creatorName}</h3>
                            {legacyContentPlayer.platform && (
                              <span className="inline-flex shrink-0 items-center gap-1 text-[10px] text-gray-500" style={{ fontWeight: 500 }}>
                                <PlatformIcon platform={legacyContentPlayer.platform} size="sm" />
                                {t(platformNameI18nKey[legacyContentPlayer.platform])}
                              </span>
                            )}
                          </div>
                          {legacyContentPlayer.permalink && (
                            <a
                              href={legacyContentPlayer.permalink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-gray-500 transition-colors hover:text-[#be43ff]"
                            >
                              <span>Post link</span>
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setLegacyContentPlayer(null)}
                          className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                          aria-label="Close"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4">
                      {(() => {
                        const strip = legacyContentPlayer.structuredItems;
                        const stripLen =
                          strip && strip.length > 0 ? strip.length : legacyContentPlayer.allContent.length;
                        const kind =
                          legacyContentPlayer.contentKind ??
                          (stripLen > 1 ? 'carousel' : 'single_image');
                        const isVideo = kind === 'video';
                        const isSingleImage = kind === 'single_image';
                        const isCarousel = kind === 'carousel';
                        const isEmbed = kind === 'embed' && Boolean(legacyContentPlayer.embedPlayerUrl);
                        const isPlaceholder = kind === 'placeholder';
                        return (
                          <>
                            <div className="relative mx-auto mb-4 aspect-[3/4] w-full max-w-[300px] shrink-0 overflow-hidden rounded-lg bg-black">
                              {isEmbed && legacyContentPlayer.embedPlayerUrl ? (
                                <iframe
                                  src={legacyContentPlayer.embedPlayerUrl}
                                  title={t('contentReview.embeddedPostTitle')}
                                  className="absolute inset-0 h-full w-full border-0 bg-black"
                                  sandbox="allow-scripts allow-same-origin allow-popups allow-presentation"
                                  referrerPolicy="strict-origin-when-cross-origin"
                                />
                              ) : isPlaceholder ? (
                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-gradient-to-br from-[#1e1b4b] via-[#312e81] to-[#0f172a] p-5 text-center">
                                  {legacyContentPlayer.contentImage ? (
                                    <img
                                      src={legacyContentPlayer.contentImage}
                                      alt=""
                                      className="h-20 w-20 rounded-full border-4 border-white/25 object-cover shadow-lg"
                                    />
                                  ) : (
                                    <div className="h-20 w-20 rounded-full border-4 border-white/20 bg-white/15" aria-hidden />
                                  )}
                                  <p className="max-w-xs text-[13px] leading-relaxed text-white/90">{t('contentReview.noPreviewInPlayer')}</p>
                                  {legacyContentPlayer.permalink ? (
                                    <a
                                      href={legacyContentPlayer.permalink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-[13px] font-semibold text-[#101828] shadow-md transition-opacity hover:opacity-95"
                                    >
                                      {t('contentReview.openInBrowser')}
                                      <ExternalLink className="h-4 w-4 shrink-0" aria-hidden />
                                    </a>
                                  ) : null}
                                </div>
                              ) : isVideo && legacyContentPlayer.videoUrl ? (
                                <VideoPlayer
                                  src={legacyContentPlayer.videoUrl}
                                  controls
                                  autoPlay
                                  className="h-full w-full object-contain"
                                  poster={legacyContentPlayer.contentImage}
                                  preload="metadata"
                                  disableFullscreen={false}
                                />
                              ) : (
                                <img
                                  src={legacyContentPlayer.contentImage}
                                  alt={legacyContentPlayer.contentTitle}
                                  className="h-full w-full object-contain bg-black"
                                />
                              )}
                            </div>

                            <p className="mb-3 flex flex-wrap items-center gap-2 text-[12px]" style={{ fontWeight: 600 }}>
                              <span className="text-[#be43ff]">{legacyContentPlayer.contentTitle.replace(/\s+by\s+@\S+$/, '').trim()}</span>
                              <span className="text-[#be43ff]"> by </span>
                              <button
                                type="button"
                                onClick={() => {
                                  const toCopy =
                                    legacyContentPlayer.creatorHandle != null
                                      ? `@${legacyContentPlayer.creatorHandle}`
                                      : (legacyContentPlayer.contentTitle.match(/\s+by\s+(@\S+)$/)?.[1] ?? '');
                                  if (toCopy) {
                                    void navigator.clipboard.writeText(toCopy);
                                    setUsernameCopyTooltipOpen(true);
                                    setTimeout(() => setUsernameCopyTooltipOpen(false), 1500);
                                  }
                                }}
                                className="relative cursor-pointer text-[#101828] hover:underline"
                                title="Copy username"
                              >
                                {legacyContentPlayer.creatorHandle != null
                                  ? `@${legacyContentPlayer.creatorHandle}`
                                  : (legacyContentPlayer.contentTitle.match(/\s+by\s+(@\S+)$/)?.[1] ?? '')}
                              </button>
                              {usernameCopyTooltipOpen && (
                                <span className="animate-in fade-in-0 text-xs font-medium text-emerald-600 duration-150">Copied!</span>
                              )}
                            </p>

                            {!isPlaceholder &&
                              !isEmbed &&
                              stripLen > 1 &&
                              (isVideo || isCarousel) && (
                              <div className="mb-4 flex items-center justify-center gap-3">
                                <button
                                  type="button"
                                  className="rounded-full p-2 text-gray-600 transition-colors hover:bg-gray-100"
                                  aria-label="Previous"
                                  onClick={() => {
                                    const items = legacyContentPlayer.structuredItems;
                                    if (items && items.length > 0) {
                                      const prevIdx =
                                        legacyContentPlayer.currentIndex > 0
                                          ? legacyContentPlayer.currentIndex - 1
                                          : items.length - 1;
                                      const item = items[prevIdx];
                                      const hero = resolveLegacyHeroFromCreatorContent(item);
                                      const multiKind =
                                        items.length > 1
                                          ? hero.contentKind === 'video' || hero.mediaType === 'video'
                                            ? 'video'
                                            : 'carousel'
                                          : hero.contentKind;
                                      setLegacyContentPlayer({
                                        ...legacyContentPlayer,
                                        currentIndex: prevIdx,
                                        contentTitle: `${item.title || t('campaigns.profiles.contentFallbackTitle')} by @${legacyContentPlayer.creatorHandle ?? ''}`,
                                        allContent: items.map((x) => x.image),
                                        ...hero,
                                        contentKind: multiKind ?? hero.contentKind,
                                        permalink: hero.permalink ?? item.permalink ?? legacyContentPlayer.permalink,
                                      });
                                      return;
                                    }
                                    const prevIdx =
                                      legacyContentPlayer.currentIndex > 0
                                        ? legacyContentPlayer.currentIndex - 1
                                        : legacyContentPlayer.allContent.length - 1;
                                    const nextUrl = legacyContentPlayer.allContent[prevIdx];
                                    setLegacyContentPlayer({
                                      ...legacyContentPlayer,
                                      currentIndex: prevIdx,
                                      contentImage: nextUrl,
                                      videoUrl: legacyContentPlayer.mediaType === 'video' ? nextUrl : legacyContentPlayer.videoUrl,
                                    });
                                  }}
                                >
                                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
                                  </svg>
                                </button>
                                <span className="text-xs text-gray-500">
                                  {legacyContentPlayer.currentIndex + 1} / {stripLen}
                                </span>
                                <button
                                  type="button"
                                  className="rounded-full p-2 text-gray-600 transition-colors hover:bg-gray-100"
                                  aria-label="Next"
                                  onClick={() => {
                                    const items = legacyContentPlayer.structuredItems;
                                    if (items && items.length > 0) {
                                      const nextIdx =
                                        legacyContentPlayer.currentIndex < items.length - 1
                                          ? legacyContentPlayer.currentIndex + 1
                                          : 0;
                                      const item = items[nextIdx];
                                      const hero = resolveLegacyHeroFromCreatorContent(item);
                                      const multiKind =
                                        items.length > 1
                                          ? hero.contentKind === 'video' || hero.mediaType === 'video'
                                            ? 'video'
                                            : 'carousel'
                                          : hero.contentKind;
                                      setLegacyContentPlayer({
                                        ...legacyContentPlayer,
                                        currentIndex: nextIdx,
                                        contentTitle: `${item.title || t('campaigns.profiles.contentFallbackTitle')} by @${legacyContentPlayer.creatorHandle ?? ''}`,
                                        allContent: items.map((x) => x.image),
                                        ...hero,
                                        contentKind: multiKind ?? hero.contentKind,
                                        permalink: hero.permalink ?? item.permalink ?? legacyContentPlayer.permalink,
                                      });
                                      return;
                                    }
                                    const nextIdx =
                                      legacyContentPlayer.currentIndex < legacyContentPlayer.allContent.length - 1
                                        ? legacyContentPlayer.currentIndex + 1
                                        : 0;
                                    const nextUrl = legacyContentPlayer.allContent[nextIdx];
                                    setLegacyContentPlayer({
                                      ...legacyContentPlayer,
                                      currentIndex: nextIdx,
                                      contentImage: nextUrl,
                                      videoUrl: legacyContentPlayer.mediaType === 'video' ? nextUrl : legacyContentPlayer.videoUrl,
                                    });
                                  }}
                                >
                                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M6 6v12l8.5-6zM17 6h2v12H17z" />
                                  </svg>
                                </button>
                              </div>
                            )}

                            {(isSingleImage || isVideo || isCarousel) && legacyContentPlayer.platform && (
                              <div className="flex items-center gap-3 text-gray-600">
                                <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                                  <PlatformIcon platform={legacyContentPlayer.platform} size="sm" />
                                  <span>{t(platformNameI18nKey[legacyContentPlayer.platform])}</span>
                                </span>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>,
                document.body,
              )}

            {/* Discovery profile sidebar — same embed as `/creators/:id`, ~60% width; stay on campaign URL */}
            <AnimatePresence>
              {profileSidebarOpen && profileSidebarInfluencerId && (
                <>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 bg-black/50 z-[100] backdrop-blur-md backdrop-saturate-150"
                    onClick={() => {
                      setProfileSidebarOpen(false);
                      setProfileSidebarInfluencerId(null);
                    }}
                  />
                  <motion.div
                    initial={{ x: '100%' }}
                    animate={{ x: 0 }}
                    exit={{ x: '100%' }}
                    transition={{ type: 'tween', duration: 0.3 }}
                    className="fixed right-0 top-0 bottom-0 z-[101] flex h-full min-h-0 w-full max-w-[100vw] flex-col overflow-hidden bg-[#f8f9fa] shadow-2xl sm:w-[60%]"
                  >
                    {discoveryProfileEmbedSrc ? (
                      <iframe
                        key={discoveryProfileEmbedSrc}
                        src={discoveryProfileEmbedSrc}
                        className="min-h-0 w-full flex-1 border-0"
                        title={t('discoveryEmbed.iframeTitle')}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <div
                        className="flex flex-1 items-center justify-center p-6"
                        role="alert"
                      >
                        <p className="max-w-md text-center text-sm text-muted-foreground">
                          {t('discoveryEmbed.missingToken')}
                        </p>
                      </div>
                    )}
                  </motion.div>
                </>
              )}
            </AnimatePresence>

            {/* ═══════════════════════════════════════════════ */}
            {/*  COLLABORATIONS / CONTENT REVIEW SUB-PAGE      */}
            {/* ════���══════════════════════════════════════════ */}
            {currentSubPage === 'collaborations' && (
              <ContentReviewSection
                collaborators={campaignIdForDetail && isCampaignIdValid ? collaborationsForReview : undefined}
                isLoading={campaignIdForDetail && isCampaignIdValid ? collaborationsLoading : false}
                error={
                  campaignIdForDetail && isCampaignIdValid && collaborationsError
                    ? t('campaigns.publicationsLoadError')
                    : null
                }
                onOpenPublishedPost={openPublishedPostInPlayer}
                onOpenBeatBrief={openBeatBriefFromReview}
                onOpenCreatorProfile={
                  navigateToPath
                    ? (influencerId: string) => {
                        navigateToPath(`/creators/${encodeURIComponent(influencerId)}`);
                      }
                    : undefined
                }
              />
            )}



            {/* ════════════════════════════════════════════��══ */}
            {/*  DRAFTS SUB-PAGE                               */}
            {/* ═══════════════════════════════════════════════ */}
            {currentSubPage === 'drafts' && (() => {
              if (campaignIdForDetail && draftsError) {
                return (
                  <div className="space-y-5" role="alert">
                    <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-center gap-2 text-amber-800 text-sm">
                      <AlertCircle className="w-4 h-4 shrink-0" aria-hidden />
                      <span>Unable to load content approval. It may not be available for this campaign yet.</span>
                    </div>
                  </div>
                );
              }
              if (campaignIdForDetail && draftsLoading && !draftsDataApi?.length) {
                return (
                  <div className="flex items-center justify-center py-16" role="status" aria-live="polite">
                    <Loader2 className="w-8 h-8 text-[#be43ff] animate-spin" aria-hidden />
                    <span className="sr-only">Loading content approval</span>
                  </div>
                );
              }

              const draftsData = draftsForList;
              const getLatestVersion = (draft: DraftItemUI) => draft.versions[draft.versions.length - 1];
              const getSelectedVersion = (draft: DraftItemUI) => {
                const selectedIdx = draftSelectedVersions[draft.id];
                if (selectedIdx !== undefined && selectedIdx < draft.versions.length) return draft.versions[selectedIdx];
                return getLatestVersion(draft);
              };
              const allLatestStatuses = draftsData.map(d => getLatestVersion(d).status);
              const pendingCount = allLatestStatuses.filter(s => s === 'pending').length;
              const approvedCount = allLatestStatuses.filter(s => s === 'approved').length;
              const declinedCount = allLatestStatuses.filter(s => s === 'declined').length;
              const filtered = draftsData.filter(d => getLatestVersion(d).status === draftsFilter);

              return (
                <PhotoProvider
                  key={contentApprovalPhotoProviderKey}
                  overlayRender={(o) => contentApprovalFullscreenOverlay(o, t)}
                  onVisibleChange={(visible) => {
                    if (visible) {
                      window.history.pushState({ beatlyContentApprovalPhoto: true } as const, '');
                      contentApprovalPhotoHistoryPushedRef.current = true;
                      return;
                    }
                    if (closeContentApprovalPhotoViaPopStateRef.current) {
                      closeContentApprovalPhotoViaPopStateRef.current = false;
                      contentApprovalPhotoHistoryPushedRef.current = false;
                      return;
                    }
                    if (contentApprovalPhotoHistoryPushedRef.current) {
                      contentApprovalPhotoHistoryPushedRef.current = false;
                      skipContentApprovalPhotoPopStateRef.current = true;
                      window.history.back();
                    }
                  }}
                >
                <div className="space-y-5">
                  {/* Drafts Title Card */}
                  <div className="rounded-[24px] bg-white px-4 py-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] md:px-8 md:py-6">
                    <h2 className="mb-4 text-[22px] text-[#101828]" style={{ fontWeight: 700 }}>Content Approval</h2>
                    {/* Filter tabs */}
                    <div className="flex flex-wrap items-center gap-2">
                      {([
                        { key: 'pending' as const, label: 'Pending approval', count: pendingCount },
                        { key: 'approved' as const, label: 'Approved', count: approvedCount },
                        { key: 'declined' as const, label: 'Declined', count: declinedCount },
                      ]).map(tab => (
                        <button
                          key={tab.key}
                          onClick={() => setDraftsFilter(tab.key)}
                          className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-[12px] transition-colors ${
                            draftsFilter === tab.key
                              ? 'bg-[#101828] text-white'
                              : 'text-[#667085] hover:bg-[#f8f9fa]'
                          }`}
                          style={{ fontWeight: 600 }}
                        >
                          {tab.label}
                          <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] ${
                            draftsFilter === tab.key ? 'bg-white/20 text-white' : 'bg-[#f8f9fa] text-[#667085]'
                          }`} style={{ fontWeight: 700 }}>{tab.count}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Draft Items */}
                  {filtered.map(draft => {
                    const currentVersion = getSelectedVersion(draft);
                    const latestVersion = getLatestVersion(draft);
                    const versionIdForApi = currentVersion.versionId?.trim() || draft.apiDraftVersionId?.trim() || '';
                    const isLatestSelected = currentVersion.version === latestVersion.version;
                    const isShowingRaw = draftShowRaw[draft.id] ?? false;
                    const versionMediasForCard = currentVersion.medias ?? [];
                    const finalMediaPool = draftFinalMediaPool(versionMediasForCard);
                    const slideKey = `${draft.id}-v${currentVersion.version}`;
                    const rawSlideIdx = draftFinalMediaSlide[slideKey];
                    const slideIdx =
                      finalMediaPool.length > 0
                        ? Math.min(Math.max(0, rawSlideIdx ?? 0), finalMediaPool.length - 1)
                        : 0;
                    const listPreview: DraftListPreview =
                      isShowingRaw && currentVersion.listRawPreview
                        ? {
                            mode: currentVersion.listRawPreview.mode,
                            url: currentVersion.listRawPreview.url,
                          }
                        : finalMediaPool.length > 0
                          ? previewForDraftMedia(finalMediaPool[slideIdx])
                          : currentVersion.listFinalPreview;
                    return (
                      <div
                        key={draft.id}
                        className="overflow-hidden rounded-[24px] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-all duration-150 hover:shadow-[0_4px_16px_rgba(0,0,0,0.06)]"
                      >
                        <div className="flex flex-col md:flex-row">
                          {/* Left — Thumbnail area (`group` = play control appears on hover like collaborations) */}
                          <div className="group relative w-full shrink-0 bg-gray-950 md:w-[380px]">
                            {listPreview.mode === 'none' || !listPreview.url ? (
                              <div className="w-full flex items-center justify-center text-gray-500 bg-gray-100" style={{ minHeight: 280, maxHeight: 420 }}>
                                <span className="text-[13px]" style={{ fontWeight: 500 }}>{t('contentReview.drafts.noPreview')}</span>
                              </div>
                            ) : (
                              <PhotoView src={listPreview.url}>
                                <div
                                  className="relative w-full cursor-pointer"
                                  style={{ minHeight: 280, maxHeight: 420 }}
                                  role="button"
                                  tabIndex={0}
                                  aria-label={t('contentReview.drafts.openFullscreenAria')}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      (e.currentTarget as HTMLDivElement).click();
                                    }
                                  }}
                                >
                                  {listPreview.mode === 'image' ? (
                                    <>
                                      <img
                                        src={listPreview.url}
                                        alt=""
                                        className="h-full w-full max-h-[420px] min-h-[280px] object-cover"
                                      />
                                      <div className="pointer-events-none absolute inset-0 z-[5] flex items-center justify-center bg-black/0 opacity-0 transition-opacity duration-200 group-hover:bg-black/20 group-hover:opacity-100">
                                        <span className="content-media-control content-media-control--play pointer-events-none" aria-hidden>
                                          <ZoomIn className="h-6 w-6 text-white" strokeWidth={2.25} />
                                        </span>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <video
                                        src={listPreview.url}
                                        muted
                                        playsInline
                                        preload="metadata"
                                        className="h-full w-full max-h-[420px] min-h-[280px] object-cover"
                                        aria-hidden
                                      />
                                      <div className="pointer-events-none absolute inset-0 z-[5] flex items-center justify-center bg-black/0 opacity-0 transition-opacity duration-200 group-hover:bg-black/20 group-hover:opacity-100">
                                        <span className="content-media-control content-media-control--play pointer-events-none" aria-hidden>
                                          <Play className="ml-1 h-6 w-6 text-white" fill="white" />
                                        </span>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </PhotoView>
                            )}

                            {/* Final-media carousel — same beat can have multiple assets (match app.beatly.com) */}
                            {finalMediaPool.length > 1 && (
                              <>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDraftShowRaw(prev => ({ ...prev, [draft.id]: false }));
                                    const n = finalMediaPool.length;
                                    setDraftFinalMediaSlide(prev => ({
                                      ...prev,
                                      [slideKey]: ((prev[slideKey] ?? slideIdx) - 1 + n) % n,
                                    }));
                                  }}
                                  className="content-media-control content-media-control--nav absolute left-2 top-1/2 z-20 -translate-y-1/2"
                                  aria-label={t('contentReview.drafts.previousMediaAria')}
                                >
                                  <ChevronLeft className="h-5 w-5 shrink-0" strokeWidth={2.25} aria-hidden />
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDraftShowRaw(prev => ({ ...prev, [draft.id]: false }));
                                    const n = finalMediaPool.length;
                                    setDraftFinalMediaSlide(prev => ({
                                      ...prev,
                                      [slideKey]: ((prev[slideKey] ?? slideIdx) + 1) % n,
                                    }));
                                  }}
                                  className="content-media-control content-media-control--nav absolute right-2 top-1/2 z-20 -translate-y-1/2"
                                  aria-label={t('contentReview.drafts.nextMediaAria')}
                                >
                                  <ChevronRight className="h-5 w-5 shrink-0" strokeWidth={2.25} aria-hidden />
                                </button>
                                {!isShowingRaw && (
                                  <div
                                    className="content-media-counter absolute bottom-3 left-1/2 z-20 -translate-x-1/2"
                                    aria-hidden
                                  >
                                    {t('contentReview.drafts.mediaSlideCounter', {
                                      current: slideIdx + 1,
                                      total: finalMediaPool.length,
                                    })}
                                  </div>
                                )}
                              </>
                            )}

                            {/* Version Picker — top left */}
                            {draft.versions.length > 1 && (
                              <div className="absolute top-3 left-3 z-10">
                                <button
                                  onClick={(e) => { e.stopPropagation(); setDraftVersionDropdownOpen(draftVersionDropdownOpen === draft.id ? null : draft.id); }}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-lg border border-gray-200 shadow-sm hover:bg-white transition-colors"
                                >
                                  <span className="text-[11px] text-[#344054]" style={{ fontWeight: 600 }}>
                                    Version {currentVersion.version}{isLatestSelected ? ' (latest)' : ''}
                                  </span>
                                  <ChevronDown className="w-3 h-3 text-[#667085]" />
                                </button>
                                <AnimatePresence>
                                  {draftVersionDropdownOpen === draft.id && (
                                    <motion.div
                                      initial={{ opacity: 0, y: -4 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      exit={{ opacity: 0, y: -4 }}
                                      transition={{ duration: 0.15 }}
                                      className="absolute top-full left-0 mt-1 bg-white rounded-lg border border-gray-200 shadow-lg overflow-hidden min-w-[160px] z-20"
                                    >
                                      {draft.versions.map((v, idx) => (
                                        <button
                                          key={v.version}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setDraftSelectedVersions(prev => ({ ...prev, [draft.id]: idx }));
                                            setDraftVersionDropdownOpen(null);
                                          }}
                                          className={`w-full text-left px-3.5 py-2 text-[11px] flex items-center justify-between hover:bg-[#f8f9fa] transition-colors ${
                                            currentVersion.version === v.version ? 'bg-[#be43ff]/5 text-[#be43ff]' : 'text-[#344054]'
                                          }`}
                                          style={{ fontWeight: currentVersion.version === v.version ? 600 : 500 }}
                                        >
                                          <span>Version {v.version}{idx === draft.versions.length - 1 ? ' (latest)' : ''}</span>
                                          {currentVersion.version === v.version && <Check className="w-3 h-3" />}
                                        </button>
                                      ))}
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                            )}

                            {/* Raw/Final toggle — top right (only if raw exists) */}
                            {currentVersion.rawThumbnail && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setDraftShowRaw(prev => ({ ...prev, [draft.id]: !prev[draft.id] })); }}
                                className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white/95 backdrop-blur-sm rounded-lg border border-gray-200 shadow-sm hover:bg-white transition-colors"
                                title={isShowingRaw ? 'Show final' : 'Show raw'}
                              >
                                {isShowingRaw ? <EyeOff className="w-3 h-3 text-[#667085]" /> : <Eye className="w-3 h-3 text-[#667085]" />}
                                <span className="text-[10px] text-[#344054]" style={{ fontWeight: 600 }}>{isShowingRaw ? 'Raw' : 'Final'}</span>
                              </button>
                            )}

                          </div>

                          {/* Right — Metadata */}
                          <div className="flex-1 min-w-0 flex flex-col">
                            {/* Status/reason banner at top — for approved or declined versions */}
                            {currentVersion.status !== 'pending' && (currentVersion.reason || currentVersion.feedback) && (
                              <div className={`px-6 py-4 border-b ${
                                currentVersion.status === 'declined'
                                  ? 'bg-red-50/60 border-red-100'
                                  : 'bg-emerald-50/60 border-emerald-100'
                              }`}>
                                <div className="flex items-start gap-2">
                                  <span className={`text-[11px] px-2 py-0.5 rounded-md shrink-0 ${
                                    currentVersion.status === 'declined'
                                      ? 'bg-red-100 text-red-700'
                                      : 'bg-emerald-100 text-emerald-700'
                                  }`} style={{ fontWeight: 700 }}>
                                    {currentVersion.status === 'declined' ? 'Rejected' : 'Accepted'}
                                  </span>
                                  <div className="min-w-0 flex-1">
                                    {currentVersion.reason && (
                                      <p className={`text-[12px] mb-1 ${
                                        currentVersion.status === 'declined' ? 'text-red-800' : 'text-emerald-800'
                                      }`} style={{ fontWeight: 600 }}>{currentVersion.reason}</p>
                                    )}
                                    {currentVersion.feedback && (
                                      <div className="mt-1.5">
                                        <span className="text-[10px] text-[#98a2b3] block mb-0.5" style={{ fontWeight: 600 }}>Feedback from campaign manager:</span>
                                        <p className={`text-[11px] leading-relaxed ${
                                          currentVersion.status === 'declined' ? 'text-red-700' : 'text-emerald-700'
                                        }`} style={{ fontWeight: 500 }}>{currentVersion.feedback}</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Main content area */}
                            <div className="flex-1 px-6 py-5">
                              {/* Creator row */}
                              <div className="flex items-center gap-2.5 mb-4">
                                {draft.influencerId && navigateToPath ? (
                                  <button
                                    type="button"
                                    className="flex shrink-0 cursor-pointer items-center gap-2.5 rounded-lg border-0 bg-transparent p-0 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#be43ff]/40 focus-visible:ring-offset-2"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const id = draft.influencerId;
                                      if (id) navigateToPath(`/creators/${encodeURIComponent(id)}`);
                                    }}
                                    aria-label={t('contentReview.openCreatorProfileAria', {
                                      name: draft.creator || draft.handle,
                                    })}
                                  >
                                    <img src={draft.avatar} alt="" className="h-8 w-8 rounded-full border border-gray-100 object-cover" />
                                    <span className="text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>{draft.handle}</span>
                                  </button>
                                ) : (
                                  <>
                                    <img src={draft.avatar} alt={draft.creator} className="h-8 w-8 rounded-full border border-gray-100 object-cover" />
                                    <span className="text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>{draft.handle}</span>
                                  </>
                                )}
                              </div>

                              {/* Content type pill — platform icon matches product type / name */}
                              <div className="flex items-center gap-1.5 mb-4">
                                <PlatformIcon platform={draft.contentPlatform} size="sm" />
                                <span className="text-[12px] text-[#be43ff]" style={{ fontWeight: 600 }}>{draft.contentType}</span>
                              </div>

                              {/* Dates */}
                              {draft.publicationDate && (
                                <div className="mb-1">
                                  <span className="text-[12px] text-[#667085]">Publication date: </span>
                                  <span className="text-[12px] text-[#344054]" style={{ fontWeight: 500 }}>{formatDateOnly(draft.publicationDate)}</span>
                                </div>
                              )}
                              <div className="mb-4">
                                <span className="text-[12px] text-[#667085]">Draft date: </span>
                                <span className="text-[12px] text-[#344054]" style={{ fontWeight: 500 }}>{formatDateOnly(currentVersion.draftDate)}</span>
                              </div>

                              {/* Caption */}
                              {currentVersion.caption ? (
                                <div className="mb-4">
                                  <span className="text-[12px] text-[#667085]">Caption:</span>
                                  <p className="text-[12px] text-[#344054] leading-relaxed mt-0.5" style={{ fontWeight: 500 }}>
                                    {currentVersion.caption}
                                  </p>
                                </div>
                              ) : (
                                <p className="text-[11px] text-[#98a2b3] italic mb-4">No caption provided</p>
                              )}
                            </div>

                            {/* Bottom action bar — pending: single entry to Review Content modal (Figma) */}
                            {currentVersion.status === 'pending' && isLatestSelected && (
                              <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-2.5">
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!versionIdForApi) {
                                      showError('Cannot open review: missing draft version id from server.');
                                      return;
                                    }
                                    setDraftReviewFeedback({ caption: '', files: {} });
                                    setDraftReviewToggles({ caption: true, files: {} });
                                    setDraftReviewGeneralTopics([]);
                                    setDraftReviewGeneralText('');
                                    setDraftReviewModal({
                                      draftId: draft.id,
                                      allDrafts: filtered,
                                      selectedVersionIndex: undefined,
                                      readOnly: false,
                                    });
                                    window.history.pushState({ beatlyDraftReview: true }, '');
                                    draftReviewHistoryPushedRef.current = true;
                                  }}
                                  disabled={!versionIdForApi}
                                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#be43ff] text-white text-[11px] hover:bg-[#a830e0] transition-colors shadow-[0_1px_3px_rgba(190,67,255,0.3)] disabled:cursor-not-allowed disabled:opacity-50"
                                  style={{ fontWeight: 700 }}
                                >
                                  Accept / Decline
                                </button>
                              </div>
                            )}
                            {currentVersion.status === 'declined' && isLatestSelected && (
                              <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-2.5">
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!versionIdForApi) {
                                      showError('Cannot approve: missing draft version id from server.');
                                      return;
                                    }
                                    setDraftAcceptModal({
                                      creator: draft.handle,
                                      contentType: draft.contentType,
                                      versionId: versionIdForApi,
                                    });
                                  }}
                                  disabled={!versionIdForApi}
                                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#101828] text-white text-[11px] hover:bg-[#2a3447] transition-colors disabled:cursor-not-allowed disabled:opacity-50 uppercase tracking-wide" style={{ fontWeight: 700 }}
                                >
                                  <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                                  Accept
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {filtered.length === 0 && (
                    <div className="bg-white rounded-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-12 text-center" aria-label="No content approval">
                      <div className="w-14 h-14 rounded-2xl bg-[#f8f9fa] flex items-center justify-center mx-auto mb-4" aria-hidden>
                        <FileText className="w-7 h-7 text-[#98a2b3]" />
                      </div>
                      <h3 className="text-[16px] text-[#101828] mb-2" style={{ fontWeight: 700 }}>No content</h3>
                      <p className="text-[13px] text-[#667085]">No {draftsFilter} content found for this campaign</p>
                    </div>
                  )}
                </div>
                </PhotoProvider>
              );
            })()}

            {currentSubPage === 'summary' && (
              <div className="space-y-5">
                {/* Summary Title Card */}
                <div className="rounded-[24px] bg-white px-4 py-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] md:px-8 md:py-6">
                  <h2 className="text-[22px] text-[#101828]" style={{ fontWeight: 700 }}>{t('campaigns.resultsPageTitle')}</h2>
                </div>

                {campaignDetailLoading && !campaignDetail && (
                  <div className="flex items-center justify-center py-16" role="status" aria-live="polite">
                    <Loader2 className="w-8 h-8 text-[#be43ff] animate-spin" aria-hidden />
                    <span className="sr-only">{t('campaigns.loadingResultsSr')}</span>
                  </div>
                )}

                {!campaignDetailLoading && campaignReportUrl && (
                  <div className="rounded-[24px] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden">
                    <div className="flex flex-col gap-4 border-b border-gray-100 p-4 md:flex-row md:items-center md:justify-between md:p-8">
                      <div>
                        <h3 className="text-[16px] text-[#101828] mb-1" style={{ fontWeight: 700 }}>{t('campaigns.campaignReportTitle')}</h3>
                        <p className="text-[13px] text-[#667085]">{t('campaigns.campaignReportSubtitle')}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          if (!campaignIdForDetail || !navigateToPath) return;
                          navigateToPath(
                            getPathForPage('campaign-report-embed', { campaignId: campaignIdForDetail })
                          );
                        }}
                        disabled={!campaignIdForDetail || !navigateToPath}
                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#be43ff] text-white text-[13px] hover:bg-[#a830e0] transition-colors disabled:cursor-not-allowed disabled:opacity-50 shrink-0"
                        style={{ fontWeight: 600 }}
                      >
                        <ExternalLink className="w-4 h-4" />
                        {t('campaigns.openReport')}
                      </button>
                    </div>
                    <div className="relative w-full bg-[#f8f9fa]">
                      <iframe
                        key={campaignReportUrl}
                        src={campaignReportUrl}
                        className="h-[min(720px,75vh)] min-h-[480px] w-full border-0"
                        title={t('campaigns.reportEmbedTitle')}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </div>
                )}

                {!campaignDetailLoading &&
                  (!campaignDetail || !campaignReportUrl) && (
                  <div className="rounded-[24px] bg-white p-8 text-center shadow-[0_1px_3px_rgba(0,0,0,0.04)] md:p-12" aria-label={t('campaigns.ariaCampaignResultsEmpty')}>
                    <div className="w-14 h-14 rounded-2xl bg-[#be43ff]/10 flex items-center justify-center mx-auto mb-4" aria-hidden>
                      <BarChart3 className="w-7 h-7 text-[#be43ff]" />
                    </div>
                    <h3 className="text-[16px] text-[#101828] mb-2" style={{ fontWeight: 700 }}>{t('campaigns.campaignResultsTitle')}</h3>
                    <p className="text-[13px] text-[#667085]">
                      {campaignDetail?.has_summary
                        ? t('campaigns.resultsSummaryInProgress')
                        : campaignDetail
                          ? t('campaigns.resultsEmpty')
                          : t('campaigns.resultsFallbackSubtitle')}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
